import { trackReadingHour, queueAchievementIncrement } from '../services/achievementService';
import { soundService } from '../services/soundService';
import { resolveWordTranslationHierarchy, ResolvedWordTranslation } from '../services/richDictionaryService';
import { useTranslation } from 'react-i18next';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, Languages, Loader2, X,
  Moon, Sun, ZoomIn, ZoomOut, Volume2, Sparkles, Settings,
  MessageSquare, Contrast, Trash2, Brain, Eye, Lock, Zap,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Hash, Search, ChevronUp, ChevronDown,
  Square, Target, Edit, Pencil, Palette, RotateCcw, Play, Pause, Gauge, Headphones, Plus, Minus, Globe, Copy, Check, AlertTriangle, Share2, Asterisk,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Type, Bold, Italic, SkipBack, SkipForward, Filter, Clock, Heart
} from 'lucide-react';
import QuoteCardModal, { ShareQuoteData } from '../components/QuoteCardModal';
import { storageService } from '../services/storageService';
import { AppBackButton, goBackOr, canGoBack } from '../components/AppBackButton';
import { fileStorageService } from '../services/fileStorageService';
import { translationService } from '../services/translationService';
import { textToSpeechService } from '../services/textToSpeechService';
import { Book, Annotation } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { supabase } from '../services/supabaseClient';
import { profileService } from '../services/profileService';
import { bookService } from '../services/bookService';
import { dbManager } from '../services/db_manager';
import { statsService } from '../services/statsService';
import { extractPostStyle } from '../utils/postUtils';
import BookDetailsModal from './BookDetailsModal';
import { GenerateExerciseModal, GenerateExerciseModalConfig } from '../components/GenerateExerciseModal';
import { flashcardManager } from '../services/flashcardManager';
import { notesDeckManager, normalizeLangCode } from '../services/notesDeckManager';
import { syncOfflineService } from '../services/syncOfflineService';
import { FLASHCARD_LANGUAGES, getLanguageName } from '../services/languageConstants';
import { activeStudyTracker } from '../services/activeStudyTrackerService';
import { STRICT_LANGUAGES } from '../constants';
import { getBookContent } from '../services/books';
import { useUnifiedDictionary } from '../hooks/useUnifiedDictionary';
import { useData } from '../contexts/DataContext';
import { useStreakCounter } from '../hooks/useStreakCounter';
import { tourService } from '../services/tourService';
import ShadowingProfileIcon from '../components/ShadowingProfileIcon';
import ShadowingSentenceOverlay from '../components/ShadowingSentenceOverlay';
import { useShadowingSentenceTracker } from '../hooks/useShadowingSentenceTracker';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import i18nInstance from '../i18n';

let currentBookDrawings: any[] = [];

const LIBRARY_HTML_URL = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-atualizadas.json";
const R2_HTML_BASE = "https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-online-htmls/";
const HEADER_HEIGHT = 90;
const FOOTER_HEIGHT = 190;
const SECTION_SIZE = 30000; // ~30KB per section for performance

/**
 * Botão do HUD do leitor (Design 2.0): redondo, ícone que "levanta" ao passar o mouse,
 * dica flutuante e estado ativo. As cores vêm das variáveis --hud-* (mudam com o tema do livro).
 */
const HudButton: React.FC<{
  label: string;
  onClick: () => void;
  children: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  id?: string;
  tone?: 'default' | 'danger' | 'ok' | 'warn';
  size?: 'sm' | 'md';
  tooltip?: 'top' | 'right' | 'none';
  className?: string;
}> = ({ label, onClick, children, active, disabled, id, tone = 'default', size = 'md', tooltip = 'top', className = '' }) => {
  const dim = size === 'sm' ? 'w-9 h-9' : 'w-11 h-11';
  const toneCls = active
    ? (tone === 'ok' ? 'bg-ok/90 text-white' : 'bg-[color:var(--hud-strong)] text-[color:var(--hud-on-strong)]')
    : tone === 'danger'
      ? 'text-[color:var(--hud-muted)] hover:text-bad hover:bg-bad/10'
      : tone === 'warn'
        ? 'text-warn hover:bg-warn/10'
        : 'text-[color:var(--hud-muted)] hover:text-[color:var(--hud-ink)] hover:bg-[color:var(--hud-soft)]';
  const tipPos = tooltip === 'right'
    ? 'left-full ml-3 top-1/2 -translate-y-1/2 -translate-x-1 group-hover:translate-x-0'
    : 'bottom-full mb-2.5 left-1/2 -translate-x-1/2 translate-y-1 group-hover:translate-y-0';
  return (
    <button
      type="button"
      id={id}
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={`group relative ${dim} shrink-0 rounded-full flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] active:scale-90 disabled:opacity-25 disabled:pointer-events-none ${toneCls} ${className}`}
    >
      <span className="flex items-center justify-center transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:-translate-y-px group-hover:scale-110">
        {children}
      </span>
      {tooltip !== 'none' && (
        <span className={`pointer-events-none absolute ${tipPos} whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium bg-[color:var(--hud-strong)] text-[color:var(--hud-on-strong)] opacity-0 group-hover:opacity-100 transition-all duration-300 hidden md:block z-50`}>
          {label}
        </span>
      )}
    </button>
  );
};

const BOOK_LANG_TO_LOCALE: Record<string, string> = {
  'pt': 'pt-BR', 'en': 'en-US', 'fr': 'fr-FR', 'es': 'es-ES', 'it': 'it-IT',
  'de': 'de-DE', 'ru': 'ru-RU', 'ja': 'ja-JP', 'zh': 'zh-CN', 'ar': 'ar-SA',
  'no': 'nb-NO', 'fi': 'fi-FI', 'da': 'da-DK', 'sv': 'sv-SE', 'pl': 'pl-PL',
  'nl': 'nl-NL', 'cs': 'cs-CZ', 'hu': 'hu-HU', 'tr': 'tr-TR', 'hi': 'hi-IN'
};

const normalizeText = (text: string) => {
  return text.toLowerCase().replace(/\s+/g, ' ').trim();
};

const parseNote = (content: string) => {
  if (!content) return { original: '', meta: '', translation: '', commentary: null };

  // Delimite priorities for splitting context/content
  // 1. |~| is the modern structured delimiter
  // 2. " -> " is the legacy content delimiter
  
  let mainBody = content;
  let contextPrefix = '';
  let contextSuffix = '';

  if (content.includes('|~|')) {
    const parts = content.split('|~|');
    mainBody = parts[0];
    contextPrefix = (parts[1] || '').trim();
    contextSuffix = (parts[2] || '').trim();
  }

  const parts = mainBody.split(' -> ');
  const original = parts[0] || '';
  const metadata = parts[1] || '';
  
  // Extract commentary if present
  const translationParts = metadata.split(' |~| ');
  const translation = translationParts[0]?.trim() || '';
  const commentary = translationParts[1]?.trim() || null;

  return {
    original: original.trim(),
    translation: translation,
    commentary: commentary,
    contextPrefix,
    contextSuffix
  };
};

const languageMap: Record<string, string> = {
  'en': 'en-US', 'pt': 'pt-BR', 'fr': 'fr-FR', 'es': 'es-ES', 'it': 'it-IT',
  'de': 'de-DE', 'ru': 'ru-RU', 'ja': 'ja-JP', 'zh': 'zh-CN', 'ar': 'ar-SA',
  'no': 'nb-NO', 'fi': 'fi-FI', 'da': 'da-DK', 'sv': 'sv-SE', 'pl': 'pl-PL',
  'nl': 'nl-NL', 'cs': 'cs-CZ', 'hu': 'hu-HU', 'tr': 'tr-TR', 'hi': 'hi-IN'
};

const TTS_OPTIONS = [
  { name: 'Português (BRA)', locale: 'pt-BR' },
  { name: 'Inglês (EUA)', locale: 'en-US' },
  { name: 'Inglês (GBR)', locale: 'en-GB' },
  { name: 'Francês (FRA)', locale: 'fr-FR' },
  { name: 'Espanhol (ESP)', locale: 'es-ES' },
  { name: 'Italiano (ITA)', locale: 'it-IT' },
  { name: 'Alemão (DEU)', locale: 'de-DE' },
  { name: 'Japonês (JPN)', locale: 'ja-JP' },
  { name: 'Chinês (CHN)', locale: 'zh-CN' },
  { name: 'Russo (RUS)', locale: 'ru-RU' },
  { name: 'Árabe (SAU)', locale: 'ar-SA' },
  { name: 'Hindi (IND)', locale: 'hi-IN' },
  { name: 'Coreano (KOR)', locale: 'ko-KR' },
  { name: 'Holandês (NLD)', locale: 'nl-NL' },
  { name: 'Polonês (POL)', locale: 'pl-PL' },
];

const READER_FONTS = [
  { name: 'Georgia', family: 'Georgia, serif' },
  { name: 'Palatino', family: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
  { name: 'Times New Roman', family: '"Times New Roman", Times, serif' },
  { name: 'Arial', family: 'Arial, Helvetica, sans-serif' },
  { name: 'Verdana', family: 'Verdana, Geneva, sans-serif' },
  { name: 'Helvetica', family: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  { name: 'Trebuchet MS', family: '"Trebuchet MS", "Lucida Sans Unicode", "Lucida Grande", "Lucida Sans", sans-serif' },
  { name: 'Courier New', family: '"Courier New", Courier, monospace' },
  { name: 'OpenDyslexic', family: 'OpenDyslexic, sans-serif' },
  { name: 'System Default', family: 'inherit' },
];

// Using FLASHCARD_LANGUAGES from services/languageConstants

const splitHtmlToSections = (html: string): string[] => {
  if (!html || !html.trim()) return [''];
  
  const TRIGGER = 2200; // Começa a buscar quebra natural de frase/parágrafo aos 2.2k
  const LIMIT = 3000;   // Limite máximo determinístico por página virtual (3k chars)

  const sections: string[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  let currentSectionHtml = '';
  let currentLength = 0;
  const openTags: string[] = [];

  const flush = () => {
    if (currentSectionHtml.trim() !== '') {
      sections.push(currentSectionHtml.trim());
    }
    currentSectionHtml = '';
    currentLength = 0;
  };

  const getWrapperOpen = () => openTags.map(tag => `<${tag}>`).join('');
  const getWrapperClose = () => [...openTags].reverse().map(tag => `</${tag}>`).join('');

  const processText = (text: string) => {
    let remaining = text;
    while (remaining.length > 0) {
      const spaceLeft = LIMIT - currentLength;

      if (currentLength < TRIGGER && remaining.length <= spaceLeft) {
        // Encaixa com segurança abaixo do gatilho
        currentSectionHtml += remaining;
        currentLength += remaining.length;
        remaining = '';
      } else {
        // Excede ou já está acima do TRIGGER: procurar ponto final de frase
        const searchSlice = remaining.substring(0, Math.min(remaining.length, spaceLeft));
        let breakIdx = -1;

        // Procura fim de frase (. ! ?) seguido de espaço ou final
        const match = searchSlice.match(/[.!?](\s+|$)/);
        if (match && match.index !== undefined) {
          breakIdx = match.index + 1;
        }

        if (breakIdx <= 0 && (currentLength + searchSlice.length >= LIMIT)) {
          // Se não encontrou pontuação e atingiu LIMIT, quebra no último espaço
          const spaceIdx = searchSlice.lastIndexOf(' ');
          if (spaceIdx > 0) {
            breakIdx = spaceIdx;
          } else {
            breakIdx = searchSlice.length;
          }
        }

        if (breakIdx > 0) {
          const part = remaining.substring(0, breakIdx);
          currentSectionHtml += part;
          currentSectionHtml += getWrapperClose();
          remaining = remaining.substring(breakIdx);
          flush();

          if (remaining.length > 0) {
            currentSectionHtml = getWrapperOpen();
            currentLength = currentSectionHtml.length;
          }
        } else {
          currentSectionHtml += remaining;
          currentLength += remaining.length;
          remaining = '';
        }
      }
    }
  };

  const processNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      processText(node.textContent || '');
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      const isBlock = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'blockquote', 'li', 'br'].includes(tag);

      if (isBlock && currentLength >= TRIGGER) {
        currentSectionHtml += getWrapperClose();
        flush();
        currentSectionHtml = getWrapperOpen();
        currentLength = currentSectionHtml.length;
      }

      const clone = el.cloneNode(false) as Element;
      const startTag = clone.outerHTML.replace(/<\/.*>$/, '');
      const closeTag = `</${tag}>`;

      currentSectionHtml += startTag;
      currentLength += startTag.length;
      openTags.push(tag);

      el.childNodes.forEach(child => processNode(child));

      currentSectionHtml += closeTag;
      currentLength += closeTag.length;
      openTags.pop();

      if (isBlock && currentLength >= TRIGGER) {
        currentSectionHtml += getWrapperClose();
        flush();
        currentSectionHtml = getWrapperOpen();
        currentLength = currentSectionHtml.length;
      }
    }
  };

  Array.from(body.childNodes).forEach(child => processNode(child));
  flush();

  return sections.length > 0 ? sections : [html];
};

interface WordSelection {
  id: string;
  text: string;
}

const UIRow: React.FC<{ label: string; active: boolean; onToggle: () => void }> = ({ label, active, onToggle }) => (
  <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl border border-black/[0.06] dark:border-white/[0.06]">
    <span className="font-bold text-sm dark:text-ink">{label}</span>
    <div onClick={onToggle} className={`w-12 h-7 rounded-full relative transition-all p-1 cursor-pointer ${active ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-700'}`}>
      <div className={`w-5 h-5 bg-surface rounded-full transition-transform ${active ? 'translate-x-5' : 'translate-x-0'}`} />
    </div>
  </div>
);

const LibriTuiReaderPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return () => {
      textToSpeechService.stopPageReading();
      textToSpeechService.stop();
    };
  }, []);
  const { 
    isDarkMode, targetLanguage, toggleAppOnlyTheme, themeMode, accentColor,
    isSmartTranslationEnabled 
  } = useTheme();
  const { user } = useAuth();
  const { books, refreshBooks, profile } = useData();
  const safeT = i18n.getFixedT(profile?.native_language || 'pt');
  useStreakCounter(true);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [bookData, setBookData] = useState<any>(null);
  const [zoom, setZoom] = useState(1.4);
  const [mimeType, setMimeType] = useState<string>('');
  const [showWizardError, setShowWizardError] = useState(false);

  // Reading Settings State (initialized with defaults or localStorage)
  const [readerFontSize, setReaderFontSize] = useState(() => Number(localStorage.getItem('readerFontSize')) || 19);
  const [readerTextAlign, setReaderTextAlign] = useState('center');
  const [readerMarginSize, setReaderMarginSize] = useState(() => localStorage.getItem('readerMarginSize') || 'medium');
  const [readerFontFamily, setReaderFontFamily] = useState(() => localStorage.getItem('readerFontFamily') || 'System Default');
  const [readerIsBold, setReaderIsBold] = useState(() => localStorage.getItem('readerIsBold') === 'true');
  const [readerIsItalic, setReaderIsItalic] = useState(() => localStorage.getItem('readerIsItalic') === 'true');
  const [readerVerticalSpacing, setReaderVerticalSpacing] = useState(() => localStorage.getItem('readerVerticalSpacing') || 'medium');
  const [isReadingSettingsModalOpen, setIsReadingSettingsModalOpen] = useState(false);

  // Persistence for reader settings
  useEffect(() => {
    localStorage.setItem('readerFontSize', String(readerFontSize));
    localStorage.setItem('readerTextAlign', readerTextAlign);
    localStorage.setItem('readerMarginSize', readerMarginSize);
    localStorage.setItem('readerFontFamily', readerFontFamily);
    localStorage.setItem('readerIsBold', String(readerIsBold));
    localStorage.setItem('readerIsItalic', String(readerIsItalic));
    localStorage.setItem('readerVerticalSpacing', readerVerticalSpacing);
  }, [readerFontSize, readerTextAlign, readerMarginSize, readerFontFamily, readerIsBold, readerIsItalic, readerVerticalSpacing]);

  // Active study tracker initialization & unmount flush for reader
  useEffect(() => {
    const rawLang = bookData?.language || bookData?.target_lang || profile?.learning_language;
    const clean = rawLang ? rawLang.split('-')[0].toLowerCase() : null;

    if (user?.id && clean && clean !== 'all') {
      activeStudyTracker.setUserContext(user.id, clean);
    }
    return () => {
      activeStudyTracker.flush();
    };
  }, [user?.id, bookData?.language, bookData?.target_lang, profile?.learning_language]);

  const [bookHtml, setBookHtml] = useState<string>('');
  const [rawSections, setRawSections] = useState<string[]>([]);
  const [renderedSections, setRenderedSections] = useState<{ idx: number, html: string }[]>([]);
  const [currentSectionIdx, setCurrentSectionIdx] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isReadyToSave, setIsReadyToSave] = useState(false);
  const [isSettingsMenuOpen, setIsSettingsMenuOpen] = useState(false);

  // Shadowing Mode State & Tracker
  const shadowingLocale = useMemo(() => {
    const rawLang = bookData?.language || bookData?.target_lang || profile?.learning_language || 'en';
    return BOOK_LANG_TO_LOCALE[rawLang.split('-')[0].toLowerCase()] || 'en-US';
  }, [bookData?.language, bookData?.target_lang, profile?.learning_language]);

  const {
    isShadowingActive,
    toggleShadowing,
    sentences: shadowingSentences,
    currentSentenceIndex: shadowingSentenceIdx,
    currentSentence: shadowingCurrentSentence,
    wordStatuses: shadowingWordStatuses,
    isListening: isShadowingListening,
    isTtsPlaying: isShadowingTtsPlaying,
    hasTtsFinished: hasShadowingTtsFinished,
    transcript: shadowingTranscript,
    isSentenceComplete: isShadowingSentenceComplete,
    ttsRate: shadowingTtsRate,
    cycleTtsRate: cycleShadowingTtsRate,
    setPageText: setShadowingPageText,
    playSentenceTts: playShadowingSentenceTts,
    playWordTts: playShadowingWordTts,
    startRecording: startShadowingRecording,
    stopRecording: stopShadowingRecording,
    jumpToSentence: jumpShadowingSentence
  } = useShadowingSentenceTracker({
    locale: shadowingLocale,
    userId: user?.id || 'guest',
    onSectionFinished: () => {
      if (currentPage < totalPages) {
        jumpToPage(currentPage + 1);
      }
    }
  });

  const handlePrevShadowingSentence = () => {
    if (shadowingSentenceIdx > 0) {
      jumpShadowingSentence(shadowingSentenceIdx - 1);
    } else if (currentPage > 1) {
      jumpToPage(currentPage - 1);
    }
  };

  const handleNextShadowingSentence = () => {
    if (shadowingSentenceIdx < shadowingSentences.length - 1) {
      jumpShadowingSentence(shadowingSentenceIdx + 1);
    } else if (currentPage < totalPages) {
      jumpToPage(currentPage + 1);
    }
  };

  const handleToggleShadowing = () => {
    if (!isShadowingActive) {
      const currentSec = renderedSections.find(s => s.idx === currentSectionIdx);
      if (currentSec?.html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(currentSec.html, 'text/html');
        const text = doc.body.textContent || '';
        setShadowingPageText(text, shadowingLocale);
      }
    }
    toggleShadowing();
  };

  useEffect(() => {
    if (isShadowingActive) {
      const currentSec = renderedSections.find(s => s.idx === currentSectionIdx);
      if (currentSec?.html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(currentSec.html, 'text/html');
        const text = doc.body.textContent || '';
        setShadowingPageText(text, shadowingLocale);
      }
    }
  }, [isShadowingActive, currentSectionIdx, renderedSections, shadowingLocale, setShadowingPageText]);

  useEffect(() => {
    if ((location.state?.shadowing === true || new URLSearchParams(window.location.search).get('shadowing') === 'true') && !isShadowingActive && renderedSections.length > 0) {
      const timer = setTimeout(() => {
        handleToggleShadowing();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.state, renderedSections]);

  // Creative Mode State
  const [isCreativeMode, setIsCreativeMode] = useState(false);
  const [brushColor, setBrushColor] = useState('#ffffff');
  const [brushSize, setBrushSize] = useState(4);
  const [drawingPaths, setDrawingPaths] = useState<any[]>([]);
  const [currentPath, setCurrentPath] = useState<any>(null);

  // Ref para o container do visualizador
  const viewerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [viewerWidth, setViewerWidth] = useState(window.innerWidth);
  const [dynamicHeaderHeight, setDynamicHeaderHeight] = useState(90);
  const headerRef = useRef<HTMLElement>(null);

  
  // Single Execution & Loop Prevention Refs
  const lastLoadedBookIdRef = useRef<string | null>(null);
  const hasFetchedAnnotationsRef = useRef<boolean>(false);
  const hasFetchedDrawingsRef = useRef<boolean>(false);
  const lastSavedPageRef = useRef<number | null>(null);

  const [isThemeModalOpen, setIsThemeModalOpen] = useState(false);

  // States for Theme/Texture
  const [activeTheme, setActiveTheme] = useState<'light' | 'dark' | 'custom' | null>(() => {
    const savedTheme = localStorage.getItem('readerTheme') as any;
    if (savedTheme === 'light' || savedTheme === 'dark' || savedTheme === 'custom') return savedTheme;
    // Fallback if texture is set
    const savedTexture = localStorage.getItem('readerTexture');
    if (savedTexture) return null;
    return isDarkMode ? 'dark' : 'light';
  });
  
  const [customBgColor, setCustomBgColor] = useState<string>(() => localStorage.getItem('readerCustomBg') || '#1C2430');
  const [customTextColor, setCustomTextColor] = useState<string>(() => localStorage.getItem('readerCustomText') || '#ffffff');
  const [activeTexture, setActiveTexture] = useState<string | null>(() => {
    return localStorage.getItem('readerTexture') || null;
  });

  // Derived styling for the HTML reader
  const isDarkBase = activeTheme === 'dark' || activeTexture === '3.jpg';
  // Use CSS mix-blend-mode equivalent to apply texture on top of html pages
  const htmlBlendMode = activeTexture ? (activeTexture === '3.jpg' ? 'screen' : 'multiply') : 'normal';

  // Fix: maintain backwards compatibility state synced immediately
  const [isContentDarkMode, setIsContentDarkMode] = useState(isDarkBase);
  useEffect(() => {
    setIsContentDarkMode(isDarkBase);
  }, [isDarkBase]);
  const [isUIVisible, setIsUIVisible] = useState(true);
  const [isPlayerOpen, setIsPlayerOpen] = useState(true);
  const [eyePosition, setEyePosition] = useState({ x: 24, y: 110 }); // Above the footer
  const [isDraggingEye, setIsDraggingEye] = useState(false);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const eyeRef = useRef<HTMLButtonElement>(null);

  const isDraggingEyeRef = useRef(false);
  const isPotentiallyDraggingRef = useRef(false);

  const handleEyeDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    // Only handle primary mouse button or touch
    if ('button' in e && e.button !== 0) return;
    
    // Prevent default touch behavior on iOS (like highlighting) ONLY if we know we are grabbing it
    // Actually, we can't preventDefault here easily in React without breaking click, but we can do it in native event
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    dragStartPos.current = { x: clientX, y: clientY };
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    dragOffset.current = {
      x: clientX - rect.left,
      y: rect.bottom - clientY
    };

    isPotentiallyDraggingRef.current = true;
    isDraggingEyeRef.current = false;
    setIsDraggingEye(false); // keep state in sync
  };

  useEffect(() => {
    const handleDrag = (e: MouseEvent | TouchEvent) => {
      if (!isPotentiallyDraggingRef.current || !eyeRef.current) return;
      
      const clientX = 'touches' in e ? (e as TouchEvent).touches[0].clientX : (e as MouseEvent).clientX;
      const clientY = 'touches' in e ? (e as TouchEvent).touches[0].clientY : (e as MouseEvent).clientY;

      if (!isDraggingEyeRef.current) {
        const dx = clientX - dragStartPos.current.x;
        const dy = clientY - dragStartPos.current.y;
        if (Math.sqrt(dx*dx + dy*dy) > 5) {
          isDraggingEyeRef.current = true;
          setIsDraggingEye(true); // update state so button styling can react if needed
        } else {
          return;
        }
      }

      // Prevent scrolling while dragging on iOS
      if (e.cancelable) {
        e.preventDefault();
      }

      const newX = Math.max(16, Math.min(window.innerWidth - 64, clientX - dragOffset.current.x));
      const newY = Math.max(16, Math.min(window.innerHeight - 64, window.innerHeight - clientY - dragOffset.current.y));

      eyeRef.current.style.left = `${newX}px`;
      eyeRef.current.style.bottom = `${newY}px`;
    };

    const handleDragEnd = (e: MouseEvent | TouchEvent) => {
      if (!isPotentiallyDraggingRef.current) return;
      isPotentiallyDraggingRef.current = false;

      if (!isDraggingEyeRef.current) return; // It was a tap
      
      // Delay resetting state to prevent onClick from firing immediately after drag
      setTimeout(() => {
          isDraggingEyeRef.current = false;
          setIsDraggingEye(false);
      }, 50);

      const rect = eyeRef.current?.getBoundingClientRect();
      if (rect) {
        setEyePosition({
          x: rect.left,
          y: window.innerHeight - rect.bottom
        });
      }
    };

    window.addEventListener('mousemove', handleDrag, { passive: false });
    window.addEventListener('touchmove', handleDrag, { passive: false });
    window.addEventListener('mouseup', handleDragEnd);
    window.addEventListener('touchend', handleDragEnd);
    
    return () => {
      window.removeEventListener('mousemove', handleDrag);
      window.removeEventListener('touchmove', handleDrag);
      window.removeEventListener('mouseup', handleDragEnd);
      window.removeEventListener('touchend', handleDragEnd);
    };
  }, []);

  // Reading Settings Persistence Effects
  useEffect(() => {
    localStorage.setItem('readerFontSize', readerFontSize.toString());
  }, [readerFontSize]);

  useEffect(() => {
    localStorage.setItem('readerTextAlign', readerTextAlign);
  }, [readerTextAlign]);

  useEffect(() => {
    localStorage.setItem('readerMarginSize', readerMarginSize);
  }, [readerMarginSize]);

  useEffect(() => {
    localStorage.setItem('readerFontFamily', readerFontFamily);
  }, [readerFontFamily]);

  useEffect(() => {
    localStorage.setItem('readerIsBold', readerIsBold.toString());
  }, [readerIsBold]);

  useEffect(() => {
    localStorage.setItem('readerIsItalic', readerIsItalic.toString());
  }, [readerIsItalic]);

  // Dynamic Header Measurement
  useEffect(() => {
    if (!headerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        if (entry.target instanceof HTMLElement) {
          setDynamicHeaderHeight(entry.target.offsetHeight);
        }
      }
    });
    observer.observe(headerRef.current);
    return () => observer.disconnect();
  }, []);

  // Estilo dinâmico para destaque
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .reader-root-container {
        position: fixed;
        inset: 0;
        width: 100vw;
        height: 100vh;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        background-color: ${activeTexture ? 'transparent' : (activeTheme === 'dark' ? '#1C2430' : '#F4F1EC')};
        background-image: ${activeTexture ? `url(/textures/${activeTexture})` : 'none'};
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        transition: background 0.3s ease;
      }
      .reader-main-layout {
        flex: 1;
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        width: 100%;
      }
      .reader-text-box {
        width: 100vw;
        height: 100vh;
        position: fixed;
        inset: 0;
        z-index: 5;
        display: flex;
        flex-direction: column;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        padding: 0;
        margin: 0;
        -webkit-text-size-adjust: none;
        text-size-adjust: none;
        transform: translateZ(0);
        backface-visibility: hidden;
        perspective: 1000;
      }
      .hud-element {
        backdrop-filter: blur(24px) saturate(1.2);
        -webkit-backdrop-filter: blur(24px) saturate(1.2);
        transition: opacity 0.5s cubic-bezier(0.22, 1, 0.36, 1), transform 0.5s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.4s ease, width 0.4s ease;
        border: 1px solid var(--hud-line, rgba(255, 255, 255, 0.08));
        background-color: var(--hud-bg, rgba(28, 36, 48, 0.82)) !important;
        color: var(--hud-ink, #E6E0D6);
        box-sizing: border-box;
      }
      .ui-invisible {
        opacity: 0 !important;
        pointer-events: none !important;
        transform: translateY(-20px);
      }
      .ui-invisible-bottom {
        opacity: 0 !important;
        pointer-events: none !important;
        transform: translate(-50%, 20px) !important;
      }
      .vertical-selection-menu {
        position: fixed;
        left: 1.5rem;
        top: 50%;
        transform: translateY(-50%);
        z-index: 160;
        border-radius: 2rem;
        padding: 0.75rem;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.75rem;
        transition: all 0.3s ease;
        max-width: 90vw;
        box-sizing: border-box;
      }
      .vertical-selection-menu:hover {
        opacity: 1;
        transform: translateY(-50%) scale(1.02);
      }
      /* Permanent Left Positioning */
      @media (max-width: 768px) {
        .vertical-selection-menu {
          left: 1.5rem;
          top: 50%;
          bottom: auto;
          transform: translateY(-50%);
          flex-direction: column;
          padding: 0.75rem;
          border-radius: 2rem;
          gap: 0.75rem;
        }
      }
      .bottom-player-card {
        position: fixed;
        bottom: 8rem;
        left: 50%;
        transform: translateX(-50%);
        z-index: 300;
        width: auto;
        min-width: 200px;
        height: 4.5rem;
        border-radius: 3rem;
        padding: 0 1.5rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 1rem;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      @media (max-width: 480px) {
        .bottom-player-card {
           bottom: 7.5rem;
           scale: 0.9;
        }
      }
      /* Lista de fontes: no toque segue sem barra; no computador, barra fina e discreta */
      .reader-font-row { scrollbar-width: none; cursor: grab; }
      .reader-font-row::-webkit-scrollbar { display: none; }
      @media (hover: hover) and (pointer: fine) {
        .reader-font-row { scrollbar-width: thin; scrollbar-color: var(--hud-line) transparent; }
        .reader-font-row::-webkit-scrollbar { display: block; height: 3px; }
        .reader-font-row::-webkit-scrollbar-track { background: transparent; }
        .reader-font-row::-webkit-scrollbar-thumb { background: var(--hud-line); border-radius: 9999px; }
        .reader-font-row:hover::-webkit-scrollbar-thumb { background: var(--hud-muted); }
      }
      .bottom-fixed-footer {
        position: fixed;
        bottom: calc(14px + env(safe-area-inset-bottom, 0px));
        left: 0;
        right: 0;
        margin: 0 auto;
        z-index: 300;
        width: min(520px, calc(100% - 24px));
        border-radius: 32px;
        padding: 0.75rem 0.75rem 0.5rem;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 0.35rem;
        overflow: visible;
      }
      .ui-toggle-btn {
        position: fixed;
        z-index: 180;
        width: 4.2rem;
        height: 4.2rem;
        border-radius: 50%;
        background-color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow 0.2s ease;
        cursor: grab;
      }
      .ui-toggle-btn:active {
        cursor: grabbing;
        transform: scale(0.95);
      }
      .dark .ui-toggle-btn {
        background-color: #252F3E;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }
      @media (max-width: 480px) {
        .bottom-action-bar {
           scale: 0.9;
           bottom: 1.5rem;
        }
      }
      .reader-main-container {
        contain: layout style;
        touch-action: pan-y;
        user-select: text;
        -webkit-user-select: text;
        position: relative;
        width: 100%;
        height: 100%;
        background: transparent;
      }
      /* Viewport Lock */
      html, body {
        height: 100% !important;
        overflow: hidden !important;
        overscroll-behavior: none !important;
      }
      /* Content padding applied to INNER elements, NOT the column container */
      #text-stability-container > * {
        padding-left: var(--reader-pad, 3rem);
        padding-right: var(--reader-pad, 3rem);
        max-width: 100% !important;
      }
      /* Prevent text from being cut in half between columns/pages */
      #text-stability-container p,
      #text-stability-container div,
      #text-stability-container h1,
      #text-stability-container h2,
      #text-stability-container h3,
      #text-stability-container h4,
      #text-stability-container h5,
      #text-stability-container h6,
      #text-stability-container li,
      #text-stability-container blockquote {
        break-inside: avoid-column;
        -webkit-column-break-inside: avoid-column;
        position: relative; /* For per-paragraph SVG anchoring */
        box-sizing: border-box;
      }
      /* Ensure global box-sizing to fix column padding conflicts */
      #text-stability-container * {
        box-sizing: border-box !important;
      }
      /* Per-paragraph drawing SVGs */
      .injected-drawing-svg {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        overflow: visible;
        z-index: 5;
      }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const lastScrollY = useRef(0);
  const scrollThreshold = 10;
  const [isLanguageModalOpen, setIsLanguageModalOpen] = useState(false);
  const [isExerciseConfirmOpen, setIsExerciseConfirmOpen] = useState(false);
  const [pendingLangForExercise, setPendingLangForExercise] = useState<string | null>(null);
  const [showNoteCreatedModal, setShowNoteCreatedModal] = useState(false);
  const [pendingFlashcard, setPendingFlashcard] = useState<{ original: string, translation: string } | null>(null);
  const [isBatchFlashcard, setIsBatchFlashcard] = useState(false);

  // --- WIZARD STATES ---
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | null>(null);
  const [wizardConfig, setWizardConfig] = useState({
    flashcardMode: 'existing' as 'existing' | 'new', flashcardDeckExisting: '', flashcardDeckNew: '', flashcardLang: 'en',
    pronMode: 'existing' as 'existing' | 'new', pronDeckExisting: '', pronDeckNew: '', pronLang: 'en',
    writeMode: 'existing' as 'existing' | 'new', writeDeckExisting: '', writeDeckNew: '', writeLang: 'en',
  });
  const [wizardAvailableDecks, setWizardAvailableDecks] = useState<{ flashcard: string[], pronunciation: string[], writing: string[] }>({ flashcard: [], pronunciation: [], writing: [] });
  const [isLangDropdownOpenWizard, setIsLangDropdownOpenWizard] = useState(false);

  const [selectedWords, setSelectedWords] = useState<WordSelection[]>([]);
  const unifiedDict = useUnifiedDictionary();
  const [translation, setTranslation] = useState<string | null>(null);
  // Conquista Poliglota em Foco: cada tradução feita no leitor (palavra ou frase) conta
  useEffect(() => {
    if (translation && user?.id) queueAchievementIncrement(user.id, 'poliglota_foco', 1);
  }, [translation]);
  const [isTranslating, setIsTranslating] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isCreatingFlashcard, setIsCreatingFlashcard] = useState(false);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isExplaining, setIsExplaining] = useState(false);
  const [lastDetectedLocale, setLastDetectedLocale] = useState<string | null>(null);

  // Estados para o Player de TTS (Design Imagem 2)
  const [isTtsPlayerModalOpen, setIsTtsPlayerModalOpen] = useState(false);
  const [ttsModalLocale, setTtsModalLocale] = useState('en-US');
  const [isTtsModalPlaying, setIsTtsModalPlaying] = useState(false);
  const [ttsModalRate, setTtsModalRate] = useState(0.95);
  const [isTtsLangPickerOpen, setIsTtsLangPickerOpen] = useState(false);

  // Estados para Controle de Leitura de Página (Unificado com PDF)
  const [isReaderPopupOpen, setIsReaderPopupOpen] = useState(false);
  const [pageReadingStatus, setPageReadingStatus] = useState(textToSpeechService.getStatus());
  const [ttsRate, setTtsRate] = useState(1.0);
  const isAutoPlayingRef = useRef(false);
  const isPageTransitioningRef = useRef(false);
  const wakeLockRef = useRef<any>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Wake Lock Management
  const requestWakeLock = async () => {
    if ('wakeLock' in navigator && document.visibilityState === 'visible') {
      try {
        wakeLockRef.current = await (navigator as any).wakeLock.request('screen');
      } catch (err) {
        console.warn("[Wake Lock] Failed:", err);
      }
    }
  };

  const releaseWakeLock = () => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release();
      wakeLockRef.current = null;
    }
  };

  // Estados para Seletor de Idioma TTS
  const [isReaderLangModalOpen, setIsReaderLangModalOpen] = useState(false);
  // bookLangDetected: true when language was auto-loaded from D1 (no manual selection needed)
  const [bookLangDetected, setBookLangDetected] = useState(false);
  const [readerTtsLocale, setReaderTtsLocale] = useState(() => {
    const saved = localStorage.getItem(`bookTtsLang_${id}`);
    return saved || 'pt-BR';
  });

  const [isHideMenuOpen, setIsHideMenuOpen] = useState(false);
  const [shareQuote, setShareQuote] = useState<ShareQuoteData | null>(null);
  const [cleanUIConfig, setCleanUIConfig] = useState(() => {
    const saved = localStorage.getItem('libritui_cleanUI_gut');
    return saved ? JSON.parse(saved) : { header: true, player: true, pages: true, notes: true };
  });

  const [noteUpdateToggle, setNoteUpdateToggle] = useState(0);
  const [activeNoteActionId, setActiveNoteActionId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<any | null>(null);
  const [editContent, setEditContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [highlightedWordIds, setHighlightedWordIds] = useState<Set<string>>(new Set());
  const [jumpedNoteId, setJumpedNoteId] = useState<string | null>(null);
  const [appNotification, setAppNotification] = useState<{
    show: boolean;
    title: string;
    message: string;
    type: 'success' | 'error' | 'info';
    hideIcon?: boolean;
  }>({ show: false, title: '', message: '', type: 'info' });
  const [wordsUnderAction, setWordsUnderAction] = useState<WordSelection[]>([]);
  const [wordDict, setWordDict] = useState<ResolvedWordTranslation | null>(null);
  const [wordDictTab, setWordDictTab] = useState<'definitions' | 'examples' | 'translations'>('definitions');
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState<Set<string>>(new Set());
  const [isBatchDeleteConfirmOpen, setIsBatchDeleteConfirmOpen] = useState(false);
  const [sortMode, setSortMode] = useState<'recent' | 'old' | 'favorite'>('recent');
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [visibleNotesCount, setVisibleNotesCount] = useState(15);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [syncQueue, setSyncQueue] = useState<any[]>([]);
  const translationReqIdRef = useRef(0);
  
  // Resilient load from IndexedDB Outbox
  useEffect(() => {
    const loadPending = async () => {
      try {
        const pending = await syncOfflineService.getAllPending();
        if (pending.length > 0) {
          setSyncQueue(pending);
        }
      } catch (e) {
        console.error("[Sync] Error loading persistent queue:", e);
      }
    };
    loadPending();
  }, []);
  const [isSyncingNew, setIsSyncingNew] = useState(false);
  const [showSyncNewConfirm, setShowSyncNewConfirm] = useState<{ count: number, langCode: string, items: any[], flashcardDecks: string[], pronunciationDecks: string[], writingDecks: string[] } | null>(null);
  const [isPickingLangForSync, setIsPickingLangForSync] = useState(false);

  const processNextBatch = useCallback(async (currentQueue: any[]) => {
    if (currentQueue.length === 0) {
      setIsSyncingNew(false);
      return;
    }
    
    setIsSyncingNew(true);
    const batchSize = 5; // User requested 5
    const batch = currentQueue.slice(0, batchSize);
    const remaining = currentQueue.slice(batchSize);
    
    let hitRateLimit = false;
    let processedCount = 0;

    // Sequential processing to allow precise stopping on 429
    for (const item of batch) {
      try {
        await flashcardManager.saveFlashcard(item.original, item.translation, item.locale, item.bookId, item.category);
        if (notesDeckManager) {
          await notesDeckManager.addToPronunciation(item.original, item.translation, item.locale, item.category, item.bookId);
          await notesDeckManager.addToWriting(item.original, item.translation, item.locale, item.category, item.bookId);
        }
        
        // Remove from persistent storage only after successful Cloudflare/D1 confirmation
        if (item.id !== undefined) {
          await syncOfflineService.removeItem(item.id);
        } else {
          await syncOfflineService.removeItem(item.original, item.bookId);
        }
        processedCount++;
      } catch (e: any) {
        console.error("[Sync] Error processing note:", item.original, e);
        // Check for 429 error (Supabase/D1 status or message)
        if (e.status === 429 || (e.message && e.message.includes("429"))) {
          hitRateLimit = true;
          break; // Stop this batch and wait
        }
        // For other errors, we count as "processed" to avoid infinite loops on bad data
        processedCount++;
      }
    }
    
    if (hitRateLimit) {
      // Re-add un-processed items of this batch back to the queue
      const updatedQueue = [...currentQueue.slice(processedCount)];
      setSyncQueue(updatedQueue);
      console.warn(`[Sync] Rate limit hit. Pausing for 10s... (${updatedQueue.length} items left)`);
      setTimeout(() => processNextBatch(updatedQueue), 10000);
      return;
    }

    setSyncQueue(remaining);
    if (remaining.length > 0) {
      console.log(`[Sync] Batch complete. Waiting 5s... (${remaining.length} items left)`);
      setTimeout(() => processNextBatch(remaining), 5000);
    } else {
      setIsSyncingNew(false);
      console.log("[Sync] All items processed successfully.");
    }
  }, []);

  useEffect(() => {
    // We only use localStorage as a fast-read cache now, the source of truth is IndexedDB
    localStorage.setItem('libritui_sync_queue', JSON.stringify(syncQueue));
    if (syncQueue.length > 0 && !isSyncingNew) {
      processNextBatch(syncQueue);
    }
  }, [syncQueue, isSyncingNew, processNextBatch]);

  const favoriteSyncQueueRef = useRef<Annotation[]>([]);
  const lastFavoriteSyncTimeRef = useRef<number>(0);

  useEffect(() => {
    const interval = setInterval(async () => {
      if (favoriteSyncQueueRef.current.length > 0 && Date.now() - lastFavoriteSyncTimeRef.current >= 1000 && user) {
        const annotation = favoriteSyncQueueRef.current[0]; // Peek
        lastFavoriteSyncTimeRef.current = Date.now();
        
        try {
          const { translationService } = await import('../services/translationService');
          const { original, translation: parsedTranslation } = parseNote(annotation.content);
          
          await translationService.saveUserNote(user.id, {
            id: annotation.id,
            book_id: id!,
            book_title: bookData?.title || 'Livro',
            original,
            translation: parsedTranslation,
            page_number: annotation.pageNumber,
            favorited: annotation.favorited || 'no'
          });
          
          favoriteSyncQueueRef.current.shift(); // Remove only after success
        } catch (e) {
          console.error("Error syncing favorite from queue:", e);
          // If error, we keep it in queue to retry or just shift if it's a persistent error
          // For now, let's shift to avoid blocking the queue infinitely
          favoriteSyncQueueRef.current.shift();
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, [user, id, bookData]);

  const toggleFavorite = async (annotation: Annotation) => {
    const newFavorited = annotation.favorited === 'yes' ? 'no' : 'yes';
    const updatedAnnotation = { ...annotation, favorited: newFavorited };
    
    // 1. Update LocalState (Immediate feedback)
    setAnnotations(prev => prev.map(a => a.id === annotation.id ? updatedAnnotation : a));
    
    // 2. Update LocalStorage (Persist locally)
    storageService.updateAnnotation(annotation.id, { favorited: newFavorited });
    
    // 3. Queue for Cloudflare Sync (Gradual)
    if (user) {
      // Check if already in queue, update it or add new
      const existingIdx = favoriteSyncQueueRef.current.findIndex(a => a.id === annotation.id);
      if (existingIdx !== -1) {
        favoriteSyncQueueRef.current[existingIdx] = updatedAnnotation;
      } else {
        favoriteSyncQueueRef.current.push(updatedAnnotation);
      }
    }
  };

  const handleNotesScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 100) {
      setVisibleNotesCount(prev => prev + 30);
    }
  };

  const [isJumpModalOpen, setIsJumpModalOpen] = useState(false);
  const [isCombinedSave, setIsCombinedSave] = useState(false);
  const [jumpPageInput, setJumpPageInput] = useState('');
  const [jumpError, setJumpError] = useState<string | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const debounceTimerRef = useRef<any>(null);

  useEffect(() => {
    localStorage.setItem('libritui_cleanUI_gut', JSON.stringify(cleanUIConfig));
  }, [cleanUIConfig]);

  // TTS Metadata and Wake Lock Sync
  useEffect(() => {
    if (pageReadingStatus.isReading && !pageReadingStatus.isPaused) {
      requestWakeLock();
      textToSpeechService.updateMetadata(
        bookData?.title || "LibriTui Reader",
        bookData?.author || bookData?.authors?.[0]?.name || "Autor Desconhecido",
        bookData?.coverUrl || bookData?.cover_url || "",
        currentPage < totalPages ? () => jumpToPage(currentPage + 1) : undefined,
        currentPage > 1 ? () => jumpToPage(currentPage - 1) : undefined
      );
    } else {
      releaseWakeLock();
    }

    return () => releaseWakeLock();
  }, [pageReadingStatus.isReading, pageReadingStatus.isPaused, bookData]);

  // Sincroniza estado visual com React State (Persistência)
  useEffect(() => {
    const timer = setInterval(() => {
      const currentStatus = textToSpeechService.getStatus();
      // Comparação rigorosa para evitar re-renders desnecessários que causam flicker
      if (
        currentStatus.isReading !== pageReadingStatus.isReading ||
        currentStatus.isPaused !== pageReadingStatus.isPaused
      ) {
        setPageReadingStatus({
          isReading: currentStatus.isReading,
          isPaused: currentStatus.isPaused,
          currentRate: currentStatus.currentRate
        });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [pageReadingStatus.isReading, pageReadingStatus.isPaused]);

  // Sections removed, consolidating to pure page navigation

  // Handler para leitura de página (Com Destaque e Checkpoints)
  const handlePagePlay = useCallback(async () => {
    // Sincronamente desbloqueia áudio para iOS
    textToSpeechService.unlockAudio();
    if (user?.id) queueAchievementIncrement(user.id, 'ouvido_atento', 1);

    const status = textToSpeechService.getStatus();

    if (!viewerRef.current) return;

    let textToRead = "";
    let wordsMap: { id: string, start: number, end: number }[] = [];

    // SELEÇÃO DE PALAVRAS: Apenas da página ATUAL
    const currentPagePrefix = `w-${currentPage - 1}-`;
    const allWords = Array.from(viewerRef.current.querySelectorAll('.palavra-interativa')) as HTMLElement[];
    const pageWords = allWords.filter(el => el.dataset.wordId && el.dataset.wordId.startsWith(currentPagePrefix));

    // SOLICITAÇÃO USUÁRIO: Começar da seleção se houver, caso contrário, do início ou retoma se pausado
    let startIndex = 0;
    
    if (selectedWords.length > 0) {
      const firstSelectedId = selectedWords[0].id;
      const foundIdx = pageWords.findIndex(el => el.dataset.wordId === firstSelectedId);
      if (foundIdx !== -1) {
        startIndex = foundIdx;
        // Se estava pausado, devemos cancelar o pause pois é uma nova leitura a partir da seleção
        if (status.isPaused || status.isReading) {
          textToSpeechService.stopPageReading();
        }
      }
      
      // SOLICITAÇÃO USUÁRIO: Limpar a seleção atual para ocultar o menu de baixo
      setSelectedWords([]);
      window.getSelection()?.removeAllRanges();

    } else {
      // Resume se estiver pausado (Checkpoint) e se NÃO houver seleção e NÃO mudamos de página
      if (status.isPaused) {
        textToSpeechService.resumePageReading();
        setPageReadingStatus(textToSpeechService.getStatus());
        return;
      }
    }

    const targetWords = pageWords.slice(startIndex);

    const { cleanTextForTts } = await import('../services/textToSpeechService');

    let currentCharCount = 0;

    for (const el of targetWords) {
      const rawText = el.textContent || "";
      const cleanWord = cleanTextForTts(rawText);
      const wordIdTts = el.dataset.wordId || "";

      if (!cleanWord) continue;

      wordsMap.push({
        id: wordIdTts,
        start: currentCharCount,
        end: currentCharCount + cleanWord.length
      });

      textToRead += cleanWord + " ";
      currentCharCount += cleanWord.length + 1;
    }

    if (!textToRead.trim()) return;

    // Use the user-selected language instead of book language
    const locale = readerTtsLocale;

    // Update metadata immediately
    textToSpeechService.updateMetadata(
      bookData?.title || "LibriTui Reader",
      bookData?.author || bookData?.authors?.[0]?.name || "Autor Desconhecido",
      bookData?.coverUrl || bookData?.cover_url || "",
      currentPage < totalPages ? () => jumpToPage(currentPage + 1) : undefined,
      currentPage > 1 ? () => jumpToPage(currentPage - 1) : undefined
    );

    isAutoPlayingRef.current = true;
    // Reseta flag de transição caso tenha sobrado
    isPageTransitioningRef.current = false;

    allWords.forEach(el => el.classList.remove('highlight-speaking'));

    textToSpeechService.startPageReading(
      textToRead,
      locale,
      ttsRate,
      () => {
        // CALLBACK FIM DE PÁGINA
        allWords.forEach(el => el.classList.remove('highlight-speaking'));

        if (isAutoPlayingRef.current) {
          if (currentPage < totalPages) {
            // Bloqueia re-leitura da página atual
            isPageTransitioningRef.current = true;
            // Atualiza status para 'parado' p/ liberar lógica, MAS a flag transition bloqueia o useEffect da própria página
            setPageReadingStatus({ ...textToSpeechService.getStatus(), isReading: false });

            // DELAY 1: Espera 1s antes de virar a página
            setTimeout(() => {
              jumpToPage(currentPage + 1);
              // A flag será limpa pelo useEffect de mudança de página ou aqui?
              // Melhor aqui após o jump.
              isPageTransitioningRef.current = false;
            }, 1000);
          } else {
            isAutoPlayingRef.current = false;
            setPageReadingStatus(textToSpeechService.getStatus());
          }
        } else {
          setPageReadingStatus(textToSpeechService.getStatus());
        }
      },
      (charIndex) => {
        // HIGHLIGHT SYNC: Encontra a palavra mais próxima cujo início seja menor ou igual ao charIndex atual
        // Isso previne falhas caso o TTS dispare o evento num espaço em branco ou tenha leve dessincronia.
        const match = [...wordsMap].reverse().find(w => w.start <= charIndex);

        const previousActive = viewerRef.current?.querySelector('.highlight-speaking');
        // REFEITO: Não removemos o destaque se for apenas uma pausa natural (match indefinido)
        // Só removemos se houver um NOVO match diferente do anterior
        if (match && previousActive && previousActive.getAttribute('data-word-id') !== match.id) {
          previousActive.classList.remove('highlight-speaking');
        }

        if (match) {
          const el = viewerRef.current?.querySelector(`[data-word-id="${match.id}"]`);
          if (el) {
            el.classList.add('highlight-speaking');
            el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
          }
        }
      },
      (err) => {
        // CALLBACK ERRO: Pausa e notifica o usuário
        setAppNotification({
          show: true,
          title: safeT('reader.ttsError', 'Erro na Leitura') + " ❌",
          message: safeT('reader.ttsErrorDesc', 'O motor de voz do navegador falhou ao ler esta página. A leitura foi pausada.'),
          type: 'error'
        });
        isAutoPlayingRef.current = false;
        setPageReadingStatus(textToSpeechService.getStatus());
      },
      0,
      bookData?.id || id
    );
    // Força status reading=true
    setPageReadingStatus({ ...textToSpeechService.getStatus(), isReading: true });
  }, [selectedWords, renderedSections, currentPage, bookData, ttsRate, totalPages, readerTtsLocale]);

  const handlePagePause = () => {
    isPageTransitioningRef.current = false;
    isAutoPlayingRef.current = false;
    textToSpeechService.pausePageReading();
    setPageReadingStatus(textToSpeechService.getStatus());
    // Sugestão do usuário: remover destaque no Pause manual
    if (viewerRef.current) {
      viewerRef.current.querySelectorAll('.highlight-speaking').forEach(el => el.classList.remove('highlight-speaking'));
    }
  };

  const handlePageStop = () => {
    isAutoPlayingRef.current = false;
    isPageTransitioningRef.current = false;
    textToSpeechService.stopPageReading();
    setPageReadingStatus(textToSpeechService.getStatus());
    // Remove highlights visuais
    if (viewerRef.current) {
      viewerRef.current.querySelectorAll('.highlight-speaking').forEach(el => el.classList.remove('highlight-speaking'));
    }
  };

  const handleRateChange = (newRate: number) => {
    const clamped = parseFloat(Math.max(0.5, Math.min(2.0, newRate)).toFixed(1));
    setTtsRate(clamped);
    textToSpeechService.updatePageReadingRate(clamped);
    setPageReadingStatus(textToSpeechService.getStatus());
  };

  const handleCycleSpeed = () => {
    const speeds = [0.5, 1.0, 1.5, 2.0];
    const currentIndex = speeds.indexOf(ttsRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    handleRateChange(speeds[nextIndex]);
  };

  // Auto-play next page effect
  useEffect(() => {
    // Só inicia se auto-play ATIVO, NÃO lendo atualmente, e NÃO em transição de saída
    if (isAutoPlayingRef.current && !pageReadingStatus.isReading && !isPageTransitioningRef.current) {
      // DELAY 2: Espera 1s após virar a página (render) para começar a ler
      const timer = setTimeout(() => {
        if (isAutoPlayingRef.current && !pageReadingStatus.isReading && !isPageTransitioningRef.current) {
          handlePagePlay();
        }
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [currentPage, handlePagePlay, pageReadingStatus.isReading]);

  useEffect(() => {
    // Sincroniza destaques salvos (tipo 'translation' ou 'note') com o leitor
    const pageTranslationWordIds = new Set<string>();
    annotations.forEach(a => {
      // Expandido para aceitar todos os tipos que contenham wordIds (notas, traduções, etc)
      if (a.pageNumber === currentPage && (a as any).wordIds) {
        (a as any).wordIds.forEach((id: string) => pageTranslationWordIds.add(id));
      }
    });
    setHighlightedWordIds(pageTranslationWordIds);
  }, [annotations, currentPage, renderedSections]);

  const reapplyHighlights = useCallback(() => {
    // Robust normalization: lowercase and remove ALL punctuation/special characters via Unicode properties
    const normalizeText = (text: string) => (text || '').toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, '').replace(/\s+/g, ' ').trim();

    if (!viewerRef.current || !annotations || annotations.length === 0) {
      console.log("[Reader] reapplyHighlights: skipping (no viewer or no annotations)");
      return false;
    }

    // Desativar o observer temporariamente para evitar loop infinito
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const allInteractiveWords = Array.from(viewerRef.current.querySelectorAll('.palavra-interativa')) as HTMLElement[];
    let appliedCount = 0;

    if (allInteractiveWords.length === 0) {
       // Search slightly delayed to catch initial render
    }

    // 1. Resetar
    allInteractiveWords.forEach((el) => {
      // Remover propriedades apenas de highlights antigos (não da seleção nativa libritui)
      el.classList.remove('destaque-nota', 'active-note-highlight');
      el.style.removeProperty('--highlight-color');
      el.style.boxShadow = '';
      el.style.borderBottom = '';
      el.style.animation = '';
    });

    if (cleanUIConfig.notes) {
      annotations.forEach(a => {
        // Triple Anchor: Page Constraint (Apenas notas da página atual)
        // Removida trava de type === 'translation' para aceitar notas gerais
        if (Number(a.pageNumber) !== Number(currentPage)) return;

        // Extrair Contexto de forma robusta (suporta ambos delimitadores)
        const { original: searchString, contextPrefix, contextSuffix } = parseNote(a.content);
        const searchNorm = normalizeText(searchString);
        if (!searchNorm) return;

        let foundElements: HTMLElement[] = [];

        // A: Tentar por IDs (wordIds) - Preferência 1
        if ((a as any).wordIds && (a as any).wordIds.length > 0) {
          (a as any).wordIds.forEach((id: string) => {
            const el = viewerRef.current?.querySelector(`[data-word-id="${id}"]`) as HTMLElement;
            // Verificar se o elemento realmente pertence ao texto (Triple check rapidinho: texto bate?)
            if (el && normalizeText(el.textContent || '') === searchNorm) {
              foundElements.push(el);
            } else if (el) {
              // ID existe, mas texto mudou? Pode ser drift de versão, fallback para busca
            }
          });
        }

        // B: Triple Anchor Search (Se IDs falharem ou não existirem)
        if (foundElements.length === 0) {
          const searchWords = searchNorm.split(' ').filter(s => s.trim().length > 0);

          if (searchWords.length === 1) {
            // Single Word: Simple match
            const candidates = allInteractiveWords.filter(el => normalizeText(el.textContent || '') === searchNorm);

            // Context Disambiguation for Single Word
            if (candidates.length === 1) {
              foundElements.push(candidates[0]);
            } else if (candidates.length > 1) {
              const bestMatch = candidates.find(candidate => {
                if (!contextPrefix && !contextSuffix) return true; // Fallback
                // Check Prefix
                let currentPrefix = '';
                let prev = candidate.previousSibling;
                let steps = 0;
                while (prev && currentPrefix.length < 20 && steps < 50) {
                  currentPrefix = (prev.textContent || '') + currentPrefix;
                  prev = prev.previousSibling;
                  steps++;
                }
                const normPrefix = normalizeText(currentPrefix);
                // Check Suffix
                let currentSuffix = '';
                let next = candidate.nextSibling;
                steps = 0;
                while (next && currentSuffix.length < 20 && steps < 50) {
                  currentSuffix += (next.textContent || '');
                  next = next.nextSibling;
                  steps++;
                }
                const normSuffix = normalizeText(currentSuffix);

                const prefixMatch = !contextPrefix || normPrefix.includes(contextPrefix) || contextPrefix.includes(normPrefix);
                const suffixMatch = !contextSuffix || normSuffix.includes(contextSuffix) || contextSuffix.includes(normSuffix);
                return prefixMatch && suffixMatch;
              });
              if (bestMatch) foundElements.push(bestMatch);
              else foundElements.push(candidates[0]);
            }

          } else if (searchWords.length > 1) {
            // Multi-Word Phrase: Sequence Matching
            // We need to find a sequence of elements [el1, el2, el3...] whose normalized texts match searchWords

            for (let i = 0; i < allInteractiveWords.length; i++) {
              const startNode = allInteractiveWords[i];

              // Optimization: First word must match
              if (normalizeText(startNode.textContent || '') !== searchWords[0]) continue;

              // Potential match found, check sequence
              let matchSequence = [startNode];
              let sequenceMatch = true;

              // Try to find subsequent words in the interactive nodes list
              // Note: This relies on allInteractiveWords being in DOM order
              for (let j = 1; j < searchWords.length; j++) {
                if (i + j >= allInteractiveWords.length) {
                  sequenceMatch = false;
                  break;
                }
                const nextNode = allInteractiveWords[i + j];
                if (normalizeText(nextNode.textContent || '') !== searchWords[j]) {
                  sequenceMatch = false;
                  break;
                }
                matchSequence.push(nextNode);
              }

              if (sequenceMatch) {
                // Found a sequence! Disambiguate context if multiple sequences likely (TODO: Robust context check for phrases)
                // For now, assuming phrases are unique enough or taking the first one that matches context if implemented
                foundElements.push(...matchSequence);
                // Skip the index forward to avoid overlapping processing?
                // i += searchWords.length - 1; 
              }
            }
          }
        }

        // Aplicar Estilos Robustos (!important via cssText ou setProperty)
        let firstScrolled = false;
        const isTargetJump = a.id === jumpedNoteId;

        foundElements.forEach(el => {
          el.classList.add('destaque-nota');
          
          let rawColor = a.color || '';
          // Normalize legacy amber/yellow colors to theme placeholders
          if (rawColor === 'amber-400' || rawColor === '#fbbf24' || rawColor === '#fbbf2440') {
            rawColor = '';
          }

          let colorToUse = rawColor && rawColor.trim() !== '' ? rawColor : 'rgba(var(--primary-rgb), 0.25)';
          let borderToUse = rawColor && rawColor.trim() !== '' ? rawColor : 'rgba(var(--primary-rgb), 0.4)';
          
          // Adiciona 25% de opacidade se for um código HEX puro
          if (colorToUse.startsWith('#') && colorToUse.length === 7) {
              colorToUse = colorToUse + '40'; 
          }

          if (isTargetJump) {
              el.classList.add('active-note-highlight');
              el.style.setProperty('position', 'relative', 'important');
              el.style.setProperty('z-index', '1000', 'important');
              el.style.setProperty('background-color', 'rgba(var(--primary-rgb), 0.6)', 'important');
              el.style.setProperty('box-shadow', '0 0 0 100vmax rgba(0,0,0,0.1), 0 0 40px rgb(var(--primary-rgb))', 'important');
              el.style.setProperty('transition', 'all 0.5s ease-in-out');
              el.style.setProperty('border-radius', '6px');
              el.style.animation = 'pulse-destaque 1.2s infinite ease-out';
              
              if (!firstScrolled) {
                  setTimeout(() => {
                      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 100);
                  firstScrolled = true;
              }
          } else {
              // Map legacy color strings to themes
              let finalColor = colorToUse;
              if (finalColor === 'primary') finalColor = 'rgba(var(--primary-rgb), 0.35)';
              
              el.style.setProperty('background-color', finalColor, 'important');
              el.style.setProperty('position', 'relative');
              el.style.setProperty('z-index', '5');
              el.style.setProperty('border-bottom', `2px solid ${borderToUse}`, 'important');
              el.classList.add('destaque-nota');
          }
          appliedCount++;
        });
      });
    }

    // Reativar o observer após as mudanças
    if (observerRef.current && viewerRef.current) {
      observerRef.current.observe(viewerRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      });
    }
    return appliedCount > 0;
  }, [annotations, currentPage, cleanUIConfig.notes, pageReadingStatus.isReading, noteUpdateToggle, jumpedNoteId, renderedSections]);

  // Effect to re-apply highlights whenever the book content (renderedSections) changes
  useEffect(() => {
    // Increased delay to ensure dangerouslySetInnerHTML is fully parsed and rendered
    const timer = setTimeout(() => {
      reapplyHighlights();
    }, 150); 
    return () => clearTimeout(timer);
  }, [renderedSections, reapplyHighlights]);

  useEffect(() => {
    if (jumpedNoteId) {
      // For jumped notes, we wait a bit longer to ensure the page skip and render is done
      const timer = setTimeout(() => {
        reapplyHighlights();
      }, 300);

      const clearTimer = setTimeout(() => {
        setJumpedNoteId(null);
      }, 6000); // 6 seconds visibility
      
      return () => {
        clearTimeout(timer);
        clearTimeout(clearTimer);
      };
    }
  }, [jumpedNoteId, reapplyHighlights]);

  const [targetAnchor, setTargetAnchor] = useState<string | null>(null);

  const jumpToAnchor = useCallback((anchorId: string) => {
    if (rawSections.length === 0) return;

    // 1. Find which section contains this anchor
    const sectionIdx = rawSections.findIndex(s => s.includes(`id="${anchorId}"`) || s.includes(`id='${anchorId}'`));

    if (sectionIdx !== -1) {
      setCurrentSectionIdx(sectionIdx);
      setTargetAnchor(anchorId);
    }
  }, [rawSections]);

  useEffect(() => {
    if (targetAnchor && renderedSections.some(s => s.idx === currentSectionIdx)) {
      try {
        // Use getElementById which safely handles any ID characters (dots, numbers, etc.)
        const container = viewerRef.current;
        if (!container) return;

        // Try getElementById first (safest, handles any characters)
        let el = document.getElementById(targetAnchor) as HTMLElement | null;

        // If not found directly, search within viewer container
        if (!el) {
          // Fallback: search by name attribute (some books use <a name="id">)
          const byName = container.querySelector(`[name="${targetAnchor}"]`) as HTMLElement | null;
          el = byName;
        }

        // Final fallback: try CSS.escape with querySelector
        if (!el) {
          try {
            el = container.querySelector(`#${CSS.escape(targetAnchor)}`) as HTMLElement | null;
          } catch { /* ignore */ }
        }

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        setTargetAnchor(null);
      } catch (err) {
        // Silently ignore invalid selectors to prevent white screen
        console.warn('[TOC] Failed to scroll to anchor:', targetAnchor, err);
        setTargetAnchor(null);
      }
    }
  }, [targetAnchor, renderedSections, currentSectionIdx]);

  useEffect(() => {
    if (!viewerRef.current) return;

    // Função debounced para re-aplicar destaques
    const debouncedReapply = () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        reapplyHighlights();
      }, 100);
    };

    // Deep Persistence: Monitor DOM changes directly (MutationObserver)
    const observer = new MutationObserver((mutations) => {
      // Filtro de Mudança: Ignorar mudanças que venham das tags de destaque
      const hasRealChange = mutations.some(mutation => {
        const target = mutation.target as HTMLElement;
        if (!target || !target.classList) return false; // Ignore Text nodes
        
        const isHighlightChange = (
          target.classList.contains('destaque-nota') ||
          target.classList.contains('active-note-highlight') ||
          target.classList.contains('libritui-highlight-selection') ||
          target.classList.contains('palavra-interativa')
        );
        return !isHighlightChange;
      });

      if (hasRealChange) {
        debouncedReapply();
      }
    });

    observerRef.current = observer;
    observer.observe(viewerRef.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    reapplyHighlights();

    // Global Force Redraw Hook
    (window as any).forceRedrawNotes = () => {
      console.log('Manual Force: Disparando reapplyHighlights...');
      reapplyHighlights();
    };

    // Trava de Seleção e Clique Direito Anti-Pirataria
    const handleContextMenu = (e: MouseEvent) => e.preventDefault();
    const handleSelectStart = (e: Event) => {
      const target = e.target;
      if (target instanceof Element && !target.closest('.palavra-interativa')) {
        e.preventDefault();
      }
    };
    const handleDragStart = (e: Event) => e.preventDefault();

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);

    // Removed aggressive retry cycle that caused blinking

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, [reapplyHighlights]);

  useEffect(() => {
    const handleGlobalScroll = (e: any) => {
      const target = e.target;
      if (target && target.classList && target.classList.contains('reader-page-container')) {
        const currentScrollY = target.scrollTop;
        const diff = currentScrollY - lastScrollY.current;

        if (Math.abs(diff) > scrollThreshold) {
          if (diff > 0 && showControls) setShowControls(false);
          else if (diff < 0 && !showControls) setShowControls(true);
          lastScrollY.current = currentScrollY;
        }
      }
    };

    window.addEventListener('scroll', handleGlobalScroll, true);
    return () => window.removeEventListener('scroll', handleGlobalScroll, true);
  }, [showControls]);


  useEffect(() => {
    const fetchAnnotations = async () => {
      // 1. Mandatory State Validation
      if (!user?.id || user.isGuest || !id || id === 'undefined' || id === 'null') return;
      
      // 2. Single-Execution Lock: Prevent loops and 429s
      if (hasFetchedAnnotationsRef.current && lastLoadedBookIdRef.current === id) return;

      try {
        console.log(`[Reader] Fetching annotations for book: ${id}`);
        hasFetchedAnnotationsRef.current = true;
        lastLoadedBookIdRef.current = id;

        // Fetch only notes for this book from translations_cache
        const results = await dbManager.from('translations_cache', user.id).select({ 
          book_id: String(id),
          type: 'note' 
        });

        if (results && Array.isArray(results)) {
          const mapped = results.map((r: any) => {
            const originalText = (r.text != null ? r.text : null) || (r.original_text != null ? r.original_text : null) || '';
            const translatedText = (r.translation != null ? r.translation : null) || (r.translated_text != null ? r.translated_text : null) || '';
            const explanationText = (r.explanation != null ? r.explanation : null) || '';
            
            return {
              id: String(r.id),
              pageNumber: r.page_number || 1,
              content: `${originalText} -> ${translatedText} |~| ${explanationText} |~| `,
              favorited: r.favorited || 'no',
              createdAt: r.created_at,
              wordIds: []
            };
          });
          setAnnotations(mapped);
        } else {
          setAnnotations([]);
        }
      } catch (err) {
        console.warn("[Reader] Erro ao carregar notas do caderno:", err);
      }
    };
    fetchAnnotations();
  }, [id, noteUpdateToggle, user?.id]);

  // Processa o HTML inteiro: Atribui IDs para os parágrafos (Ancoragem Semântica) e envolve palavras
  // Divide o HTML em seções de ~10.000 caracteres respeitando tags HTML

  const processSectionHtml = useCallback((html: string, sectionIdx: number) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // 1. Assign paragraph IDs for Semantic Anchoring
    const blockElements = doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li, div');
    blockElements.forEach((el, idx) => {
      if (!el.id) {
        el.id = `anch-${sectionIdx}-${idx}`;
      }
      (el as HTMLElement).style.breakInside = 'avoid';
      (el as HTMLElement).style.pageBreakInside = 'avoid';
    });

    // 2. Wrap words (Optimized for section)
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT, null);
    let node;
    const nodesToReplace: Node[] = [];
    let wordCounter = 0;

    while (node = walker.nextNode()) {
      if (node.parentNode?.nodeName !== 'SCRIPT' && node.parentNode?.nodeName !== 'STYLE') {
        nodesToReplace.push(node);
      }
    }

    nodesToReplace.forEach(textNode => {
      if (!textNode.nodeValue) return;
      const words = textNode.nodeValue.split(/(\s+)/);
      const fragment = document.createDocumentFragment();

      words.forEach(word => {
        if (word.trim().length > 0) {
          const span = document.createElement('span');
          span.className = 'palavra-interativa';
          span.dataset.wordId = `w-${sectionIdx}-${wordCounter++}`;
          span.textContent = word;
          fragment.appendChild(span);
        } else {
          fragment.appendChild(document.createTextNode(word));
        }
      });

      textNode.parentNode?.replaceChild(fragment, textNode);
    });

    // SEGURANÇA: HTML de livros pode vir de terceiros (EPUB/clubes); remove scripts e handlers
    return sanitizeHtml(doc.body.innerHTML);
  }, []);


  // Função para corrigir HTML, Layout e Imagens (SOLICITADO PELO USUÁRIO)
  const fixHtmlContent = useCallback((html: string) => {
    // 0. SANITIZE: Replace literal newline codes and escapes (SOLICITADO)
    // Trata tanto \n literal quanto strings escapadas que aparecem no texto bruto
    let sanitized = html
      .replace(/\\\\n/g, '\n') // Handle double escaped
      .replace(/\\n/g, '\n')   // Handle single escaped literal
      .replace(/\r\n/g, '\n'); // Normalize line endings

    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitized, 'text/html');

    // 1. Corrigir Imagens com caminho R2
    const images = doc.querySelectorAll('img');
    images.forEach((img) => {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
        // Remove slash inicial se existir
        const cleanSrc = src.startsWith('/') ? src.substring(1) : src;
        img.src = `${R2_HTML_BASE}${cleanSrc}`;
        img.onerror = function () {
          (this as HTMLImageElement).style.display = 'none'; // Esconde se falhar
        };
      }
      // Garante responsividade
      img.style.maxWidth = '100%';
      img.style.height = 'auto';
      img.style.display = 'block';
      img.style.margin = '20px auto';
      img.style.borderRadius = '8px';
      img.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
    });

    // 2. Corrigir Layout e Margens
    const body = doc.body;
    body.style.width = '100%';
    body.style.maxWidth = '100%';
    body.style.overflowWrap = 'break-word';
    body.style.wordBreak = 'break-word';
    body.style.lineHeight = '1.8';
    body.style.textAlign = 'justify';
    body.style.whiteSpace = 'pre-wrap'; // Ensure newlines are honored if not in tags

    // Remove margens excessivas do próprio HTML se houver
    const allElements = doc.querySelectorAll('*');
    allElements.forEach((el: any) => {
      el.style.maxWidth = '100%';
      // Remove alturas fixas que podem causar scroll infinito
      if (el.style.height && el.style.height !== 'auto') {
        el.style.height = 'auto';
      }
    });

    return doc.body.innerHTML;
  }, []);

  useEffect(() => {
    const handleTour = () => {
      if (tourService.isActive() && tourService.getPhase() === 'READER' && bookData) {
        tourService.createDriver([
          {
            element: '#tour-reader-text',
            popover: {
              title: 'Selecione as Palavras',
              description: 'Toque ou arraste sobre qualquer palavra do texto para selecioná-la. Isso revelará o menu mágico no rodapé!',
              showButtons: [] // User must interact
            }
          }
        ]);
      }
    };

    const timeoutId = setTimeout(handleTour, 800);
    window.addEventListener('libritui-tour-started', handleTour);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('libritui-tour-started', handleTour);
    };
  }, [bookData]);

  useEffect(() => {
    if (tourService.isActive() && tourService.getPhase() === 'READER' && selectedWords.length > 0) {
      tourService.createDriver([
        {
          element: '#tour-reader-translate',
          popover: {
            title: 'Tradução Contextual',
            description: 'O menu apareceu! Clique aqui para traduzir a palavra com base no contexto inteiro da frase!',
            showButtons: [] // User must click translate
          }
        }
      ]);
    }
    
    // Phase 2: Translation modal opened
    if (tourService.isActive() && tourService.getPhase() === 'NOTES_MODAL' && translation) {
      tourService.createDriver([
        {
          element: '#tour-save-note',
          popover: {
            title: `${t('reader.createFlashcard')}s`,
            description: 'A mágica acontece aqui! Salve essa frase e sua tradução para praticar depois!',
            showButtons: [] // User must click "Salvar Nota"
          }
        }
      ]);
    }

    // Phase 3: Note saved, modal closed, guide user back
    if (tourService.isActive() && tourService.getPhase() === 'NOTES_MODAL' && !translation) {
      tourService.setPhase('GOTO_FLASHCARDS');
      tourService.createDriver([
        {
          element: '#tour-reader-back',
          popover: {
            title: 'Próxima Parada: Flashcards',
            description: 'Volte para a tela inicial para acessarmos a aba Flashcards e treinar essa nova palavra.',
            showButtons: []
          }
        }
      ]);
    }
  }, [selectedWords.length, translation]);

  const loadBook = useCallback(async () => {
    // 1. Mandatory State Validation
    if (!id || !user?.id || user.isGuest) {
      console.warn("[Reader] ID or User not ready for loadBook or is Guest");
      return;
    }

    // 2. Prevent infinite loop and 429s: Single Execution Guard
    if (lastLoadedBookIdRef.current === id && loading === false && bookData) {
      return;
    }

    console.log("[Reader] Loading book with ID:", id);
    lastLoadedBookIdRef.current = id;
    hasFetchedAnnotationsRef.current = false; // Reset locks for new book
    hasFetchedDrawingsRef.current = false;

    try {
      let rawHtml = '';
      if (location.state?.externalUrl) {
        const extUrl = location.state.externalUrl;
        setBookData({ title: location.state.title || "Leitura Web", id });
        setMimeType('text/html');
        const r = await fetch(extUrl);
        rawHtml = await r.text();
        const fixedHtml = fixHtmlContent(rawHtml);
        const sections = splitHtmlToSections(fixedHtml);
        setRawSections(sections);
        // Instant Initial Render
        setRenderedSections([{ idx: 0, html: processSectionHtml(sections[0], 0) }]);
        setLoading(false);
        return;
      }
      let b: any = null;
      
      // Fallback local storage check before D1/R2 or navigation state
      const localBooks = storageService.getBooks();
      const localBook = localBooks.find((lb) => lb.id === id || lb.custom_id === id);
      if (localBook) {
        b = localBook;
      }

      if (!b && user && !user.isGuest) {
        const isNumeric = /^\d+$/.test(id);
        let data = null;
        let error = null;
        try {
          const response = await dbManager.from('books', user.id).select({ OR_ID_CUSTOM_ID: id });
          data = response?.[0]; // dbManager.execute now returns results directly
        } catch (e: any) {
          console.error("[Reader] dbManager error:", e);
          error = e;
        }

        if (!error && data) {
          b = {
            id: data.custom_id || String(data.id),
            // Salvamos o ID original para usar caso o ID costumizado não bata com um arquivo no R2
            originalBookId: data.book_id || data.id, 
            title: data.title,
            author: data.author,
            fileUrl: data.file_url,
            coverUrl: data.cover_url,
            lastPage: data.last_page,
            language: data.language || 'en',
            isFavorite: data.is_favorite || false,
            format: data.format
          };
        }
      }
      if (!b) {
        if (location.state?.importedBook) {
          console.log("[Reader] Using importedBook from navigation state");
          b = location.state.importedBook;
        } else {
          console.warn("[Reader] Book not found in LocalStorage or D1 for ID:", id);
          setLoadError(true);
          setLoading(false);
          return;
        }
      }
      setBookData(b);
      const mime = localStorage.getItem(`mime_${b.id}`) || 'text/html';
      setMimeType(mime);

      // === AUTO LANGUAGE RESOLUTION FROM R2 CATALOG / D1 ===
      let d1Lang = b.language;
      if (!d1Lang || d1Lang === 'en' || d1Lang === 'all') {
        try {
          const catalogRes = await fetch("https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json", { mode: 'cors' });
          if (catalogRes.ok) {
            const catalogData = await catalogRes.json();
            const r2Item = catalogData.find((item: any) => String(item.id) === String(b.id) || String(item.id) === String(id));
            if (r2Item?.language) {
              d1Lang = r2Item.language.toLowerCase().split(/[;,\s]/)[0];
              b.language = d1Lang;
            }
          }
        } catch (err) {
          console.warn("[Reader] Catalog language fetch error:", err);
        }
      }

      if (d1Lang) {
        const ttsLocale = languageMap[d1Lang] || BOOK_LANG_TO_LOCALE[d1Lang] || `${d1Lang}-${d1Lang.toUpperCase()}`;
        setReaderTtsLocale(ttsLocale);
        localStorage.setItem(`bookTtsLang_${id}`, ttsLocale);
        setBookLangDetected(true);
        console.log(`[Lang] Auto-set TTS locale from D1/R2: ${d1Lang} → ${ttsLocale}`);
      } else {
        setBookLangDetected(false);
        setTimeout(() => setIsReaderLangModalOpen(true), 1200);
        console.warn(`[Lang] No language in D1 for book ${b.id}, opening language picker.`);
      }


      const safeUserId = (user && !user.isGuest) ? user.id : undefined;
      
      // 1) Descobrir de onde paramos ANTES de salvar qualquer coisa de volta DB (evita reset para página 1)
      const best = await storageService.getBestProgress(safeUserId, b.id);
      
      let initialPage = 1;
      if (best) {
          initialPage = best.page;
      } else {
          const savedProgress = localStorage.getItem(`progresso_${b.id}`);
          if (savedProgress) {
             try { initialPage = JSON.parse(savedProgress).page || 1; } catch { }
          } else {
             initialPage = b.lastPage || 1;
          }
      }

      // 2) Agora sim, atualizar o lastRead para "Recentes" sem destruir a página correta
      storageService.saveBookProgress(safeUserId, b.id, initialPage, 0);

      if (b.fileUrl.startsWith('http')) {
        const timestamp = new Date().getTime();
        try {
          // CHECK OFFLINE STORAGE FIRST
          const { getBookOffline } = await import('../services/offlineStorageService');
          const offlinePayload = await getBookOffline(b.id) || await getBookOffline(b.originalBookId || '');
          if (offlinePayload && offlinePayload.html) {
             rawHtml = offlinePayload.html;
             console.log('[Reader] Loaded offline payload from IndexedDB for book:', b.id);
          } else {
            if (b.fileUrl.includes('.r2.dev') || b.fileUrl.includes('.html')) {
               try {
                  const urlRes = await fetch(b.fileUrl);
                  if (urlRes.ok) {
                     rawHtml = await urlRes.text();
                  }
               } catch(urlErr) { }
            }
          }
          
          if (!rawHtml) {
             const fileId = b.id.endsWith('.html') ? b.id : `${b.id}.html`;
             rawHtml = await getBookContent(fileId);
          }
        } catch (fetchErr) {
          // Fallback para buscar usando o originalBookId (P/ contornar clubes de livro/custom_ids)
          let fallbackSuccess = false;
          
          if (b.originalBookId && String(b.originalBookId) !== String(b.id)) {
            try {
              const fallbackFileId = b.originalBookId.endsWith('.html') ? b.originalBookId : `${b.originalBookId}.html`;
              rawHtml = await getBookContent(fallbackFileId);
              fallbackSuccess = true;
            } catch (fallbackErr) {
              console.warn("Fallback de busca R2 (pelo book_id original) falhou", fallbackErr);
            }
          }

          if (!fallbackSuccess) {
            // Fallback para catálogo se falhar (mantendo a lógica original)
            const catRes = await fetch(`${LIBRARY_HTML_URL}?v=${timestamp}`);
            if (catRes.ok) {
              const catalog = await catRes.json();
              const freshBook = catalog.find((cb: any) => 
                String(cb.id || cb.gutenberg_id) === String(b.id) ||
                String(cb.id || cb.gutenberg_id) === String(b.originalBookId)
              );
              if (freshBook && freshBook.file) {
                const fileIdFromCatalog = freshBook.file.split('/').pop() || `${b.id}.html`;
                rawHtml = await getBookContent(fileIdFromCatalog);
              }
            } else {
              // Isso garante que não vamos dar json() numa página HTML de erro (Cloudflare 404)
              throw new Error('Arquivo original não encontrado no servidor');
            }
            if (!rawHtml) throw fetchErr; // Preserva o erro original se o catálogo também não resolveu
          }
        }
      } else if (b.fileUrl.startsWith('indexeddb://')) {
        const blob = await fileStorageService.getFile(b.id);
        if (blob) {
          const isEpub = mime === 'application/epub+zip' || b.format === 'epub' || b.fileUrl.endsWith('.epub') || b.id.includes('epub');
          if (isEpub) {
            const { parseEpubToHtml } = await import('../services/epubParser');
            rawHtml = await parseEpubToHtml(blob);
          } else {
            rawHtml = await blob.text();
          }
        }
      }

      if (rawHtml) {
        const fixedHtml = fixHtmlContent(rawHtml);
        setBookHtml(fixedHtml);
        const sections = splitHtmlToSections(fixedHtml);
        setRawSections(sections);
        setTotalPages(sections.length);

        // initialPage já foi calculado acima na montagem (evitando bug de overwrite na linha 1528)

        const initialIdx = Math.max(0, Math.min(sections.length - 1, initialPage - 1));
        setCurrentSectionIdx(initialIdx);
        setCurrentPage(initialIdx + 1);

        // Ensure scroll reset on load
        if (viewerRef.current) viewerRef.current.scrollTop = 0;

        const initialSection = {
          idx: initialIdx,
          html: processSectionHtml(sections[initialIdx], initialIdx)
        };
        setRenderedSections([initialSection]);

        setIsReadyToSave(true);

        // === FETCH DRAWING ANNOTATIONS ===
        const safeUserId = (user && !user.isGuest) ? user.id : undefined;
        const bookIdForDrawings = b.id; // This is custom_id || String(id) — same key used in save
        
        // RECURSION GUARD: Prevent infinite loops if component re-renders
        if (safeUserId && bookIdForDrawings && !hasFetchedDrawingsRef.current) {
          const stringifiedBookId = String(bookIdForDrawings);
          
          if (stringifiedBookId === 'undefined' || stringifiedBookId === 'null') {
             console.warn('[Creative] Skipping fetch: invalid bookId');
             return;
          }

          hasFetchedDrawingsRef.current = true;
          console.log('[Creative] Fetching drawings for book_id:', stringifiedBookId, 'user:', safeUserId);
          Promise.all([
            storageService.getUserAnnotations(safeUserId, stringifiedBookId),
            import('../services/translationService').then(m => m.translationService.getBookTranslations(safeUserId, stringifiedBookId))
          ]).then(([drawings, translationsResponse]) => {
            let combined: any[] = [];
            
            if (drawings && drawings.length > 0) {
              combined = [...drawings];
            }

            if (translationsResponse && Array.isArray(translationsResponse)) {
              const mappedTranslations = translationsResponse.map((t: any) => ({
                 id: String(t.id),
                 bookId: String(t.book_id),
                 pageNumber: t.page_number,
                 content: t.text, // Translation text mapping
                 type: 'translation',
                 color: '#fbbf24',
                 createdAt: t.created_at ? new Date(t.created_at).getTime() : Date.now(),
              }));
              
              // Avoid duplicates if a note is stored in both tables
              const existingIds = new Set(combined.map(d => String(d.id)));
              const uniqueTranslations = mappedTranslations.filter(t => !existingIds.has(t.id));
              combined = [...combined, ...uniqueTranslations];
            }

            console.log('[Creative] Dados combinados da nuvem (D1):', combined);
            
            if (combined && combined.length > 0) {
              console.log('[Creative] Rendering', combined.length, 'saved strokes and notes');
              currentBookDrawings = [...combined];
              setDrawingPaths(currentBookDrawings);
            } else {
              console.log('[Creative] No drawings or notes found in D1, checking localStorage...');
              // Fallback: check localStorage for guest-saved drawings
              try {
                const localDrawings = localStorage.getItem(`drawings_${bookIdForDrawings}`);
                if (localDrawings) {
                  const parsed = JSON.parse(localDrawings);
                  if (Array.isArray(parsed) && parsed.length > 0) {
                    console.log('[Creative] Loaded', parsed.length, 'strokes from localStorage');
                    currentBookDrawings = [...parsed];
                    setDrawingPaths(currentBookDrawings);
                  }
                }
              } catch { }
            }
          }).catch(err => {
            console.error('[Creative] Error fetching annotations from D1:', err);
          });
        } else {
          // Guest user: load from localStorage only
          try {
            const localDrawings = localStorage.getItem(`drawings_${bookIdForDrawings}`);
            if (localDrawings) {
              const parsed = JSON.parse(localDrawings);
              if (Array.isArray(parsed) && parsed.length > 0) {
                console.log('[Creative] Guest: Loaded', parsed.length, 'strokes from localStorage');
                currentBookDrawings = [...parsed];
                setDrawingPaths(currentBookDrawings);
              }
            }
          } catch { }
        }
      }
      setLoading(false);
    } catch (err: any) {
      console.error("Load Error:", err);
      const isAuthError = err.message?.includes("Sessão expirada") || err.message?.includes("Status 403");
      
      if (isAuthError) {
        setAppNotification({
          show: true,
          title: "Sessão expirada",
          message: "Sua sessão expirou. Por favor, faça login novamente para acessar seus livros.",
          type: 'error'
        });
      } else {
        setLoadError(true);
      }
      setLoading(false);
    }
  }, [id, navigate, processSectionHtml, location.state, fixHtmlContent, user]);

  // Efeito principal de carregamento
  useEffect(() => {
    if (user === undefined) return;

    loadBook();

    return () => {
      textToSpeechService.stop();
      textToSpeechService.stopPageReading();
    };
  }, [id, navigate, processSectionHtml, location.state, fixHtmlContent, user]);

  // VIRTUALIZATION HOOK: 3-Page Sliding Window
  useEffect(() => {
    if (rawSections.length === 0) return;

    const windowSize = 1; // 1 before, 1 after
    const startIdx = Math.max(0, currentSectionIdx - windowSize);
    const endIdx = Math.min(rawSections.length - 1, currentSectionIdx + windowSize);

    const nextSections: { idx: number; html: string }[] = [];
    for (let i = startIdx; i <= endIdx; i++) {
      const existing = renderedSections.find(s => s.idx === i);
      if (existing) {
        nextSections.push(existing);
      } else {
        nextSections.push({
          idx: i,
          html: processSectionHtml(rawSections[i], i)
        });
      }
    }

    // Keep it ordered
    nextSections.sort((a, b) => a.idx - b.idx);
    setRenderedSections(nextSections);
  }, [currentSectionIdx, rawSections, processSectionHtml]);

  // SAVE PROGRESS (Hybrid Strategy: Immediate Local + Debounced Cloud)
  useEffect(() => {
    if (!bookData?.id || !currentPage || !isReadyToSave) return;
    if (lastSavedPageRef.current === currentPage) return;

    // GUARD CLAUSE: Ensure user variables are fully loaded and valid
    if (!user || !user.id || user.id === 'undefined' || user.id === 'null') {
      return;
    }

    const safeUserId = user.isGuest ? undefined : user.id;

    lastSavedPageRef.current = currentPage;
    storageService.saveBookProgress(safeUserId, bookData.id, currentPage, totalPages || 0);

    // Increment books_read if reached the end
    if (currentPage === totalPages && totalPages > 0 && !user.isGuest) {
      const bookReadKey = `libritui_book_read_${bookData.id}`;
      if (!localStorage.getItem(bookReadKey)) {
        console.log(`[Stats] Finished reading book ${bookData.id}. Recording...`);
        statsService.increment('books_read', user.id, 1, { langCode: bookData?.language || 'all' });
        localStorage.setItem(bookReadKey, 'true');
      }
    }

  }, [currentPage, bookData?.id, user?.id, user?.isGuest, totalPages, isReadyToSave]);

  // STATISTICS: 30-second reading timer
  useEffect(() => {
    // GUARD CLAUSE: Ensure user variables are fully loaded and valid
    if (!user || !user.id || user.id === 'undefined' || user.id === 'null' || user.isGuest || !isReadyToSave) return;

    console.log(`[Stats] Timer started for page ${currentPage}`);
    const timer = setTimeout(() => {
      console.log(`[Stats] 10s milestone reached for page ${currentPage}. Recording...`);
      statsService.increment('pages_read', user.id, 1, { langCode: bookData?.language || 'all' });
    }, 10000); // 10 seconds

    return () => {
      console.log(`[Stats] Timer cleared for page ${currentPage}`);
      clearTimeout(timer);
    };
  }, [currentPage, user?.id, isReadyToSave]);

  // Sync pending progress on component unmount
  useEffect(() => {
    return () => {
      storageService.syncPendingProgress();
    };
  }, []);

  // Sincroniza estado visual com React State (Persistência) - Otimizado para evitar "Tremor/Flash"
  useEffect(() => {
    if (!viewerRef.current) return;
    
    const safeAccentColor = accentColor || '#E8823F';
    const hexToRgba = (hex: string, alpha: number) => {
      let cleanHex = hex.replace('#', '');
      if (cleanHex.length === 3) cleanHex = cleanHex.split('').map(c => c + c).join('');
      if (cleanHex.length === 6) {
        const r = parseInt(cleanHex.substring(0, 2), 16);
        const g = parseInt(cleanHex.substring(2, 4), 16);
        const b = parseInt(cleanHex.substring(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
      return hex;
    };
    const transparentBg = hexToRgba(safeAccentColor, 0.45);
    const selectedIds = new Set(selectedWords.map(w => w.id));

    // Usamos o viewerRef para limitar a busca e ser mais rápido que document.querySelectorAll
    const allInteractive = viewerRef.current.querySelectorAll('.palavra-interativa');
    
    allInteractive.forEach((el: any) => {
      const wordId = el.dataset.wordId;
      const isWordSelected = wordId && selectedIds.has(wordId);
      
      if (isWordSelected) {
        el.classList.add('libritui-highlight-selection');
        el.dataset.selected = 'true';
      } else {
        el.classList.remove('libritui-highlight-selection');
        el.dataset.selected = 'false';
      }
    });
  }, [selectedWords, currentPage, accentColor, pageReadingStatus.isReading, pageReadingStatus.isPaused]);


  const handleAnchorNavigation = (targetId: string) => {
    jumpToAnchor(targetId);
  };


  const handleInteractiveClick = (e: React.MouseEvent) => {
    if (!(e.target instanceof HTMLElement)) return;
    const target = e.target;

    // Interceptação de Âncora Interna (Prioridade de Clique)
    const anchor = target.closest('a');
    if (anchor && anchor.getAttribute('href')?.startsWith('#')) {
      const href = anchor.getAttribute('href');
      if (href && href.length > 1) {
        e.preventDefault();
        e.stopPropagation();
        handleAnchorNavigation(href.substring(1));
        return;
      }
    }

    if (target.classList.contains('palavra-interativa')) {
      e.preventDefault();
      e.stopPropagation();

      // Se o usuário clicar manualmente para selecionar, paramos qualquer TTS ativo
      if (isAutoPlayingRef.current || pageReadingStatus.isReading) {
        textToSpeechService.stopPageReading();
        setPageReadingStatus(textToSpeechService.getStatus());
        isAutoPlayingRef.current = false;
      }

      const wordId = target.dataset.wordId || '';
      const wordText = target.textContent || '';
      
      setSelectedWords(prev => {
        let nextSelected: WordSelection[] = [];

        if (prev.length === 1 && prev[0].id === wordId) {
          // Clique na mesma palavra = Deselecionar tudo
          nextSelected = [];
        } else if (prev.length >= 1) {
          // Clique em outra palavra: Range Selection (considera o primeiro item como start)
          const startId = prev[0].id;
          const allWordsDOM = Array.from(viewerRef.current?.querySelectorAll('.palavra-interativa') || []) as HTMLElement[];
          
          const startIndex = allWordsDOM.findIndex(el => el.dataset.wordId === startId);
          const endIndex = allWordsDOM.findIndex(el => el.dataset.wordId === wordId);

          if (startIndex !== -1 && endIndex !== -1) {
            const minIdx = Math.min(startIndex, endIndex);
            const maxIdx = Math.max(startIndex, endIndex);
            
            // O Limite de palavras passa a ser 100
            const maxAllowed = 100;
            const diff = (maxIdx - minIdx) + 1; // +1 inclui a propria palavra
            const limitMax = Math.min(maxIdx, minIdx + (maxAllowed - 1));

            for (let i = minIdx; i <= limitMax; i++) {
              const el = allWordsDOM[i];
              if (el) {
                nextSelected.push({ id: el.dataset.wordId || '', text: el.textContent || '' });
              }
            }

            if (diff > maxAllowed) {
              // Notifica fora do fluxo síncrono do render
              setTimeout(() => {
                setAppNotification({
                  show: true,
                  title: 'Limite atingido',
                  message: `A seleção foi limitada a ${maxAllowed} palavras. Você tentou selecionar ${diff}.`,
                  type: 'info'
                });
              }, 100); // Increased delay slightly to ensure state stability
            }

          } else {
             // Fallback de segurança caso os IDs não sejam achados 
             nextSelected = [{ id: wordId, text: wordText }];
          }
        } else {
          // O 1º Clique
          nextSelected = [{ id: wordId, text: wordText }];
        }

        return nextSelected;
      });
      
      setTranslation(null);
      setExplanation(null);
    } else {
      // Clique fora das de palavras: não limpamos no clique de fundo conforme pedido pelo user
      // para manter a seleção visivel até clique no 'X' ou des-seleção manual.
    }
  };

  const clearSelection = () => {
    textToSpeechService.stop();
    // Clear browser selection
    window.getSelection()?.removeAllRanges();

    const elements = document.querySelectorAll('.palavra-interativa.libritui-highlight-selection');
    elements.forEach((el: any) => {
      el.classList.remove('libritui-highlight-selection');
    });
    setSelectedWords([]);
    setTranslation(null);
    setExplanation(null);
  };

  // Conquistas Madrugador / Coruja Noturna: abrir o leitor cedo ou tarde conta uma vez por dia
  useEffect(() => {
    if (user?.id) trackReadingHour(user.id).catch(() => {});
  }, [user?.id]);

  // Som suave nos avisos de sucesso (ex.: "Nota salva")
  useEffect(() => {
    if (appNotification?.show && appNotification.type === 'success') soundService.play('notify');
  }, [appNotification?.show, appNotification?.type]);

  const closeActionModals = () => {
    translationReqIdRef.current++;
    setTranslation(null);
    setExplanation(null);
    setIsTranslating(false);
    setIsExplaining(false);
    setWordDict(null);
  };

  // ======== CREATIVE MODE HANDLERS ========

  const handleDrawingStart = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isCreativeMode || !canvasRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture(e.pointerId);

    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    const pixelX = e.clientX - canvasRect.left;
    const pixelY = e.clientY - canvasRect.top;

    // Find the closest block element for paragraph anchoring
    canvas.style.pointerEvents = 'none';
    const hitEl = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    canvas.style.pointerEvents = 'auto';

    let anchorParaId = '';
    let pctX = 50;
    let pctY = 50;

    let para = (hitEl instanceof Element) 
      ? (hitEl.closest('p, h1, h2, h3, h4, h5, h6, li, blockquote, div[id^="anch-"], #text-stability-container') as HTMLElement) 
      : null;
    if (!para) para = document.getElementById('text-stability-container') as HTMLElement;

    if (para) {
      if (!para.id) para.id = `dyn-para-${Math.random().toString(36).substr(2, 8)}`;
      anchorParaId = para.id;
      const rect = para.getBoundingClientRect();
      pctX = ((e.clientX - rect.left) / rect.width) * 100;
      pctY = ((e.clientY - rect.top) / rect.height) * 100;
    }

    console.log('[Creative] Start:', { pixelX, pixelY, anchorParaId, pctX, pctY });

    setCurrentPath({
      points: [{ x: pixelX, y: pixelY }],
      pctPoints: [{ px: pctX, py: pctY }],
      anchorParaId,
      color: brushColor,
      size: brushSize,
      pageIndex: currentPage,
    });
  }, [isCreativeMode, brushColor, brushSize, currentPage]);

  const handleDrawingMove = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!currentPath || !canvasRef.current) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const canvasRect = canvas.getBoundingClientRect();
    const pixelX = e.clientX - canvasRect.left;
    const pixelY = e.clientY - canvasRect.top;

    let px = 50;
    let py = 50;
    if (currentPath.anchorParaId) {
      const para = document.getElementById(currentPath.anchorParaId);
      if (para) {
        const rect = para.getBoundingClientRect();
        px = ((e.clientX - rect.left) / rect.width) * 100;
        py = ((e.clientY - rect.top) / rect.height) * 100;
      }
    }

    setCurrentPath((prev: any) => {
      if (!prev) return null;
      return {
        ...prev,
        points: [...prev.points, { x: pixelX, y: pixelY }],
        pctPoints: [...(prev.pctPoints || []), { px, py }],
      };
    });
  }, [currentPath]);

  const handleDrawingEnd = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!currentPath) return;
    e.preventDefault();

    console.log('[Creative] Drawing End. Points:', currentPath.points.length);

    // CRITICAL: Persist the finished stroke into the currentBookDrawings array
    if (currentPath.points.length > 1) {
      currentBookDrawings.push(currentPath);
      setDrawingPaths([...currentBookDrawings]);

      // Auto-save to localStorage/cloud
      const safeUserId = (user && !user.isGuest) ? user.id : undefined;
      const bookIdForSave = bookData?.id;
      if (bookIdForSave) {
        try {
          localStorage.setItem(`drawings_${bookIdForSave}`, JSON.stringify(currentBookDrawings));
          if (safeUserId) {
            storageService.saveUserAnnotations(safeUserId, bookIdForSave, currentBookDrawings).catch(console.error);
          }
        } catch (e) {
          console.error('[Creative] Error saving to storage:', e);
        }
      }
    }
    // Reset current path AFTER persisting
    setCurrentPath(null);
  }, [currentPath]);

  const undoLastDrawing = useCallback(() => {
    setDrawingPaths(prev => {
      if (prev.length === 0) return prev;
      return prev.slice(0, -1);
    });
    setCurrentPath(null);
    console.log('[Creative] Last drawing undone.');
  }, []);

  const handleSaveCreativeMode = useCallback(async () => {
    const safeUserId = (user && !user.isGuest) ? user.id : undefined;
    const bookIdForSave = bookData?.id;
    console.log('[Creative] Explicit Saving', currentBookDrawings.length, 'strokes. book_id:', bookIdForSave, 'user:', safeUserId || 'guest');

    if (safeUserId && bookIdForSave) {
      await storageService.saveUserAnnotations(safeUserId, bookIdForSave, currentBookDrawings);
      setAppNotification({
        show: true,
        title: 'Desenhos Salvos!',
        message: `${drawingPaths.length} traços foram sincronizados na nuvem.`,
        type: 'success'
      });
      // Save locally for guests
      try {
        localStorage.setItem(`drawings_${bookIdForSave}`, JSON.stringify(currentBookDrawings));
      } catch { }
      setAppNotification({
        show: true,
        title: 'Salvo localmente',
        message: 'Faça login para sincronizar seus desenhos na nuvem.',
        type: 'info'
      });
    }
    setIsCreativeMode(false);
  }, [user, bookData, drawingPaths]);

  // ======== END CREATIVE MODE HANDLERS ========

  const calculateTotalPages = useCallback(() => {
    if (rawSections.length > 0) {
      setTotalPages(rawSections.length);
    }
    if (viewerRef.current) {
      setViewerWidth(viewerRef.current.clientWidth || window.innerWidth);
    }
  }, [rawSections.length]);

  const jumpToPage = useCallback((num: number) => {
    if (rawSections.length === 0) return;
    const targetIdx = Math.max(0, Math.min(num - 1, rawSections.length - 1));

    if (!isPageTransitioningRef.current) {
      isAutoPlayingRef.current = false;
      textToSpeechService.stopPageReading();
      setPageReadingStatus({ isReading: false, isPaused: false, currentRate: ttsRate });
    }

    setCurrentSectionIdx(targetIdx);
    setCurrentPage(targetIdx + 1);

    // RESET SCROLL: Ensure the new slice starts at the top
    if (viewerRef.current) {
      viewerRef.current.scrollTop = 0;
    }

    setCurrentPath(null);
    clearSelection();
  }, [rawSections.length, setPageReadingStatus]);

  // Recalculate total pages when content or style changes
  useEffect(() => {
    calculateTotalPages();
  }, [rawSections.length, calculateTotalPages]);

  // HANDLE RESIZE: Re-slice if crossing mobile threshold
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const wasMobile = viewerWidth < 768;

      if (isMobile !== wasMobile && bookHtml) {
        const sections = splitHtmlToSections(bookHtml);
        setRawSections(sections);
        setTotalPages(sections.length);

        // Try to stay on approximately the same relative position
        const relativeProgress = currentSectionIdx / Math.max(1, rawSections.length - 1);
        const nextIdx = Math.floor(relativeProgress * (sections.length - 1));
        setCurrentSectionIdx(Math.max(0, nextIdx));
      }

      setViewerWidth(window.innerWidth);
      calculateTotalPages();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [bookHtml, viewerWidth, currentSectionIdx, rawSections.length, calculateTotalPages]);

  // ResizeObserver: re-anchor drawings when container changes size (rotation, window resize)
  const [drawRedrawKey, setDrawRedrawKey] = useState(0);
  useEffect(() => {
    if (!viewerRef.current) return;
    const observer = new ResizeObserver(() => {
      calculateTotalPages();
      // Force SVG re-render so anchored drawings recalculate positions
      setDrawRedrawKey(prev => prev + 1);
    });
    observer.observe(viewerRef.current);
    return () => observer.disconnect();
  }, [calculateTotalPages]);

  // ============ CANVAS DRAWING (requestAnimationFrame) ============
  const drawAll = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !viewerRef.current) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    // Configure High DPI Canvas
    const dpr = window.devicePixelRatio || 1;
    if (canvas.width !== Math.floor(rect.width * dpr) || canvas.height !== Math.floor(rect.height * dpr)) {
      canvas.width = Math.floor(rect.width * dpr);
      canvas.height = Math.floor(rect.height * dpr);
    }

    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, rect.width, rect.height);

    // Draw saved strokes for current book (sections removed)
    const sectionDrawings = currentBookDrawings;

    // Draw saved strokes proportional to their anchor paragraph
    sectionDrawings.forEach(path => {
      if (!path.anchorParaId || !path.pctPoints || path.pctPoints.length < 2) return;

      const para = document.getElementById(path.anchorParaId);
      if (!para) return; // Skip if paragraph is not currently visible in DOM

      const paraRect = para.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();

      // Relative offsets between paragraph and canvas
      const offsetX = paraRect.left - canvasRect.left;
      const offsetY = paraRect.top - canvasRect.top;

      ctx.beginPath();
      path.pctPoints.forEach((p: any, i: number) => {
        const x = offsetX + (p.px / 100) * paraRect.width;
        const y = offsetY + (p.py / 100) * paraRect.height;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });

      ctx.strokeStyle = path.color || '#ef4444';
      ctx.lineWidth = path.size || 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    });

    // Draw current active stroke being drawn
    if (currentPath && currentPath.points && currentPath.points.length > 0) {
      ctx.beginPath();
      currentPath.points.forEach((p: any, i: number) => {
        if (i === 0) ctx.moveTo(p.x, p.y);
        else ctx.lineTo(p.x, p.y);
      });
      ctx.strokeStyle = currentPath.color || '#ef4444';
      ctx.lineWidth = currentPath.size || 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    ctx.restore();
  }, [currentPath, drawRedrawKey, drawingPaths]);

  // Redraw when states change
  useEffect(() => {
    let animationFrameId: number;
    const renderLoop = () => {
      drawAll();
      animationFrameId = requestAnimationFrame(renderLoop);
    };
    renderLoop();
    return () => cancelAnimationFrame(animationFrameId);
  }, [drawAll]);


  const handlePlayNoteAudio = async (text: string, locale?: string) => {
    // Sincronamente desbloqueia áudio para iOS
    textToSpeechService.unlockAudio();
    if (user?.id) queueAchievementIncrement(user.id, 'ouvido_atento', 1);

    if (!text) return;

    // Interrupt any ongoing audio (Native or Proxy)
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis.cancel();

    // Se já estiver falando, para
    if (isSpeaking) {
      setIsSpeaking(false);
      return;
    }

    try {
      setIsSpeaking(true);
      const bookLang = bookData?.language || 'en';
      const finalLocale = locale || BOOK_LANG_TO_LOCALE[bookLang.toLowerCase()] || 'en-US';
      await textToSpeechService.speak(text, finalLocale, 1.0, bookData?.id || id);
      setIsSpeaking(false);
    } catch (err) {
      console.error("Note TTS Error:", err);
      setIsSpeaking(false);
    }
  };

  const handleAction = async (action: 'traduzir' | 'ouvir' | 'explicar') => {
    const fullText = selectedWords.map(w => w.text).join(' ');
    if (!fullText) return;

    if (action === 'ouvir') {
      handlePlayNoteAudio(fullText);
      // Keep selection on play as requested
    } else if (action === 'traduzir' || action === 'explicar') {
      if (action === 'traduzir' && selectedWords.length === 1) {
        // Palavra única: mesma janela da frase, com o dicionário completo embaixo da tradução
        const rawWord = selectedWords[0].text.trim();
        const singleWord = rawWord.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()?"'«»“”]/g, '') || rawWord;
        const sourceLang = (bookData?.language || readerTtsLocale || 'en').split('-')[0].toLowerCase();
        const targetLang = (i18n.language || targetLanguage || 'pt').split('-')[0].toLowerCase();
        setWordsUnderAction([...selectedWords]);
        setExplanation(null);
        setTranslation(null);
        setWordDict(null);
        setWordDictTab('definitions');
        setIsTranslating(true);
        const wordReqId = ++translationReqIdRef.current;
        try {
          const res = await resolveWordTranslationHierarchy(singleWord, targetLang, sourceLang);
          if (translationReqIdRef.current !== wordReqId) return;
          setWordDict(res);
          setTranslation(res.primaryTranslation || res.translations?.[0] || singleWord);
        } catch (err) {
          if (translationReqIdRef.current !== wordReqId) return;
          console.warn('[Reader] Dicionário indisponível:', err);
          setTranslation(singleWord);
        } finally {
          if (translationReqIdRef.current === wordReqId) setIsTranslating(false);
        }
        return;
      }

      const textToUnderAction = fullText;
      setWordsUnderAction([...selectedWords]);
      setIsTranslating(true);
      setTranslation(null);
      setExplanation(null);
      const currentReqId = ++translationReqIdRef.current;

      try {
        const isPro = profile?.plan_type === 'pro' || profile?.subscription_tier === 'pro';
        const sourceBookLanguage = (bookData?.language || readerTtsLocale || lastDetectedLocale || 'auto').split('-')[0].toLowerCase();
        const result = await translationService.unifiedTranslate(
          textToUnderAction, 
          targetLanguage, 
          user?.id, 
          sourceBookLanguage !== 'auto' ? sourceBookLanguage : undefined, 
          bookData?.id || id,
          isPro,
          isSmartTranslationEnabled
        );
        if (translationReqIdRef.current !== currentReqId) {
          console.log('[TRADUTOR] Tradução cancelada pelo usuário.');
          return;
        }
        console.log('[TRADUTOR] Resultado:', result);
        setLastDetectedLocale(result.isoCode || null);
        const sanitize = (str: string) => str.replace(/[*#_~\x60>]/g, '').replace(/\n{2,}/g, '\n').trim();

        if (result.explanation) {
          setExplanation(sanitize(result.explanation));
        }

        setTranslation(sanitize(result.translation));
      } catch (e) {
        if (translationReqIdRef.current === currentReqId) {
          setTranslation(t('translation.offlineError', 'Tradução indisponível, tente novamente mais tarde.'));
        }
      } finally {
        if (translationReqIdRef.current === currentReqId) {
          setIsTranslating(false);
        }
      }
    }
  };

  const handleCopy = () => {
    if (selectedWords.length === 0) return;
    const textToCopy = selectedWords.map(w => w.text).join(' ');
    navigator.clipboard.writeText(textToCopy).then(() => {
      setAppNotification({
        show: true,
        title: safeT('reader.textCopied', 'Texto copiado!'),
        message: safeT('reader.textCopiedDesc', 'O texto selecionado foi copiado para sua área de transferência.'),
        type: 'success'
      });
      // CRITICAL: Clear selection after copy as requested
      clearSelection();
    }).catch(err => {
      console.error('Falha ao copiar texto:', err);
    });
  };

  const handleModalPlay = async () => {
    // Sincronamente desbloqueia áudio para iOS
    textToSpeechService.unlockAudio();
    if (user?.id) queueAchievementIncrement(user.id, 'ouvido_atento', 1);

    const textToSpeak = wordsUnderAction.map(w => w.text).join(' ');
    if (!textToSpeak) return;

    // Interrupt any ongoing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    window.speechSynthesis.cancel();

    if (isTtsModalPlaying) {
      setIsTtsModalPlaying(false);
      return;
    }

    try {
      setIsTtsModalPlaying(true);
      
      const bookLang = bookData?.language || 'en';
      await textToSpeechService.speak(textToSpeak, ttsModalLocale, 1.0, bookData?.id || id);
      
      setIsTtsModalPlaying(false);
    } catch (err) {
      console.error("TTS Modal Error:", err);
      setIsTtsModalPlaying(false);
    }
  };

  const handleStopSpeak = () => {
    textToSpeechService.stop();
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsSpeaking(false);
    setIsTtsModalPlaying(false);
    // Não limpamos wordsUnderAction aqui para permitir re-play no modal
  };

  const saveNote = async (skipNotification = false) => {
    if (!bookData || !translation) return;
    const fullText = wordsUnderAction.map(w => w.text).join(' ');

    // --- Triple Anchor: Context Capture ---
    let contextPrefix = '';
    let contextSuffix = '';

    try {
      if (wordsUnderAction.length > 0) {
        const firstId = wordsUnderAction[0].id;
        const lastId = wordsUnderAction[wordsUnderAction.length - 1].id;
        const firstEl = document.querySelector(`[data-word-id="${firstId}"]`);
        const lastEl = document.querySelector(`[data-word-id="${lastId}"]`);

        if (firstEl && lastEl) {
          // Capture 20 chars before
          let prev = firstEl.previousSibling;
          while (prev && contextPrefix.length < 20) {
            contextPrefix = (prev.textContent || '') + contextPrefix;
            prev = prev.previousSibling;
          }
          // Also look at previous element siblings if text nodes are exhausted
          if (contextPrefix.length < 20 && firstEl.previousElementSibling) {
            let prevEl = firstEl.previousElementSibling;
            while (prevEl && contextPrefix.length < 20) {
              contextPrefix = (prevEl.textContent || '') + contextPrefix;
              prevEl = prevEl.previousElementSibling;
            }
          }

          // Capture 20 chars after
          let next = lastEl.nextSibling;
          while (next && contextSuffix.length < 20) {
            contextSuffix += (next.textContent || '');
            next = next.nextSibling;
          }
          if (contextSuffix.length < 20 && lastEl.nextElementSibling) {
            let nextEl = lastEl.nextElementSibling;
            while (nextEl && contextSuffix.length < 20) {
              contextSuffix += (nextEl.textContent || '');
              nextEl = nextEl.nextElementSibling;
            }
          }
        }
      }
    } catch (e) {
      console.warn("Error capturing context:", e);
    }

    // Clean context
    contextPrefix = contextPrefix.slice(-20).replace(/\s+/g, ' ');
    contextSuffix = contextSuffix.slice(0, 20).replace(/\s+/g, ' ');
    // --------------------------------------

    // Fix: Use deterministic ID for Translations Cache using Base64
    const safeBtoa = (str: string) => btoa(unescape(encodeURIComponent(str)));
    const noteId = safeBtoa(`${bookData.id}_${currentPage}_${fullText.trim()}`).slice(0, 36);

    // Store context in content string with delimiter or metadata object
    // Format: "Original Text -> Translation |~|Prefix|~|Suffix"
    const contentWithContext = `${fullText} -> ${translation} |~||~|`;

    const note = {
      id: noteId,
      bookId: bookData.id,
      pageNumber: currentPage,
      content: contentWithContext, // Saving context in content
      type: 'translation',
      color: '', // Fallback to theme accent color
      createdAt: Date.now(),
      detectedLocale: lastDetectedLocale || undefined,
      wordIds: wordsUnderAction.map(w => w.id),
    };

    // Always save locally and globally via Safe Haven logic
    const safeUserId = (user && !user.isGuest) ? user.id : undefined;
    storageService.saveAnnotationGlobal(safeUserId, note as any);

    if (safeUserId) {
      try {
        // Result is wrapped in { data: result }, errors are thrown
        await translationService.saveUserNote(user.id, {
          id: noteId,
          book_id: bookData?.id || id || '',
          book_title: bookData?.title || 'Livro Desconhecido',
          original: fullText,
          translation: translation,
          page_number: currentPage,
        });
      } catch (e) {
        console.error("Erro ao sincronizar nota no Cloud/D1:", e);
        // Don't block the UI, the note is already saved locally
        setAppNotification({
          show: true,
          title: 'Sincronização pendente',
          message: 'Sua nota foi salva localmente e será sincronizada assim que possível.',
          type: 'info'
        });
      }
    }

    setNoteUpdateToggle(prev => prev + 1);
    // Instant UI feedback: Add the new note to the local state immediately
    setAnnotations(prev => [note as any, ...prev]);

    if (!skipNotification) {
      setShowNoteCreatedModal(true);
    }

    setTranslation(null);
    setExplanation(null);
    setIsExplaining(false);
    setWordsUnderAction([]);
    
    // Clear selection after saving note as requested
    clearSelection();
  };

  const saveNoteAndCreateExercises = async () => {
    if (!translation) return;
    const fullText = wordsUnderAction.map(w => w.text).join(' ');
    const savedTranslation = translation;

    // 1. Salva a nota normalmente (sem notificação individual)
    setIsCombinedSave(true);
    await saveNote(true);

    // 2. Abre o Wizard
    openExerciseWizard(false, { original: fullText, translation: savedTranslation });
    setActiveNoteActionId(null);
  };

  const getBookLanguageCode = async (): Promise<string> => {
    if (bookData?.language) return bookData.language.toLowerCase();
    try {
      const res = await fetch("https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/library-capas-description-level-tags.json", { mode: 'cors' });
      const json = await res.json();
      const bookInfo = json.find((b: any) => String(b.id) === String(id));
      if (bookInfo?.language) return bookInfo.language.split(/[;,\s]/)[0].toLowerCase();
    } catch (e) {
      console.error("Failed to get language from R2", e);
    }
    return "en";
  };

  const openFlashcardCreationModal = async (a: any) => {
    const mainContent = a.content.split('|~|')[0];
    const { original, translation } = parseNote(mainContent);
    openExerciseWizard(false, { original, translation });
    setActiveNoteActionId(null);
  };

  const handleSaveToAllPracticeModes = (langCode: string) => {
    // legacy fallback if needed, but not used typically directly now
    setPendingLangForExercise(langCode);
    setIsExerciseConfirmOpen(true);
  };

  const openExerciseWizard = async (isBatch: boolean, singleCard?: { original: string, translation: string }) => {
    setIsBatchFlashcard(isBatch);
    if (singleCard) setPendingFlashcard(singleCard);
    
    let langCode = await getBookLanguageCode();
    langCode = langCode ? langCode.substring(0, 2).toLowerCase() : 'en';
    
    setWizardStep(1);
    
    try {
      const userId = user?.id || 'guest';
      const existingDecksRaw = userId !== 'guest' ? await dbManager.from('decks', userId).select() : [];
      const existingDecks = Array.isArray(existingDecksRaw) ? existingDecksRaw : [];
      
      const flashcardDecks = Array.from(new Set(existingDecks.filter((d: any) => d.type === 'flashcard').map((d: any) => d.name))) as string[];
      const pronDecks = Array.from(new Set(existingDecks.filter((d: any) => d.type === 'pronunciation').map((d: any) => d.name))) as string[];
      const writeDecks = Array.from(new Set(existingDecks.filter((d: any) => d.type === 'writing').map((d: any) => d.name))) as string[];
      
      setWizardAvailableDecks({
        flashcard: flashcardDecks,
        pronunciation: pronDecks,
        writing: writeDecks
      });
      
      setWizardConfig({
        flashcardMode: flashcardDecks.length > 0 ? 'existing' : 'new',
        flashcardDeck: flashcardDecks[0] || '',
        flashcardLang: langCode,
        pronMode: pronDecks.length > 0 ? 'existing' : 'new',
        pronDeck: pronDecks[0] || '',
        pronLang: langCode,
        writeMode: writeDecks.length > 0 ? 'existing' : 'new',
        writeDeck: writeDecks[0] || '',
        writeLang: langCode,
      });
    } catch (e) {
      console.error("Failed to fetch decks for wizard", e);
    }

    setWizardStep(1);
    setIsExerciseConfirmOpen(false);
    setIsLanguageModalOpen(false);
  };

  
  const getUniqueDeckNameWizard = (baseName: string, existingNames: string[]) => {
    let name = baseName;
    let counter = 1;
    while (existingNames.includes(name)) {
        name = `${baseName} (${counter})`;
        counter++;
    }
    return name;
  };

  const confirmExerciseWizard = async () => {
    // FECHA O WIZARD IMEDIATAMENTE (UI INSTANTÂNEA)
    setWizardStep(null);
    setPendingFlashcard(null);
    setIsBatchFlashcard(false);
    setWizardConfig(prev => ({...prev, flashcardDeckExisting: '', flashcardDeckNew: '', pronDeckExisting: '', pronDeckNew: '', writeDeckExisting: '', writeDeckNew: ''}));

    if (isCombinedSave) {
      setAppNotification({
        show: true,
        title: t('reader.savedInPracticeTitle', 'Salvo em Prática!'),
        message: t('reader.savedInPracticeMessage', 'Exercício criado nas abas: Flashcards, Pronúncia e Escrita.'),
        type: 'success',
        hideIcon: true
      });
      setIsCombinedSave(false);
    } else {
      setAppNotification({
        show: true,
        title: t('reader.savedInPracticeTitle', 'Salvo em Prática!'),
        message: t('reader.savedInPracticeMessage', 'Exercício criado nas abas: Flashcards, Pronúncia e Escrita.'),
        type: 'success',
        hideIcon: true
      });
    }

    if (isBatchFlashcard) {
      handleBatchWizardSave();
    } else if (pendingFlashcard) {
      try {
        const userId = user?.id || 'guest';

        // Flashcards
        const finalFlashLang = wizardConfig.flashcardLang.substring(0, 2).toLowerCase();
        let flashcardDeckId = null;
        if (wizardConfig.flashcardMode === 'new') {
          flashcardDeckId = crypto.randomUUID();
          await dbManager.from('decks', userId).insert({ id: flashcardDeckId, user_id: userId, name: (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting), lang_code: finalFlashLang, type: 'flashcard' });
        } else {
          // get id from existing
          const decks = await dbManager.from('decks', userId).select();
          const d = decks?.find((x: any) => x.name === (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting) && x.type === 'flashcard');
          if (d) flashcardDeckId = d.id;
        }
        await flashcardManager.saveFlashcard(pendingFlashcard.original, pendingFlashcard.translation, finalFlashLang, id, (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting) || 'Geral', flashcardDeckId || undefined);

        // Pronunciation
        const finalPronLang = wizardConfig.pronLang.substring(0, 2).toLowerCase();
        let pronDeckId = null;
        if (wizardConfig.pronMode === 'new') {
          pronDeckId = crypto.randomUUID();
          await dbManager.from('decks', userId).insert({ id: pronDeckId, user_id: userId, name: (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting), lang_code: finalPronLang, type: 'pronunciation' });
        } else {
          const decks = await dbManager.from('decks', userId).select();
          const d = decks?.find((x: any) => x.name === (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting) && x.type === 'pronunciation');
          if (d) pronDeckId = d.id;
        }
        await notesDeckManager.addToPronunciation(pendingFlashcard.original, pendingFlashcard.translation, finalPronLang, (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting) || 'Geral', bookData?.id || id, pronDeckId || undefined);

        // Writing
        const finalWriteLang = wizardConfig.writeLang.substring(0, 2).toLowerCase();
        let writeDeckId = null;
        if (wizardConfig.writeMode === 'new') {
          writeDeckId = crypto.randomUUID();
          await dbManager.from('decks', userId).insert({ id: writeDeckId, user_id: userId, name: (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting), lang_code: finalWriteLang, type: 'writing' });
        } else {
          const decks = await dbManager.from('decks', userId).select();
          const d = decks?.find((x: any) => x.name === (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting) && x.type === 'writing');
          if (d) writeDeckId = d.id;
        }
        await notesDeckManager.addToWriting(pendingFlashcard.original, pendingFlashcard.translation, finalWriteLang, (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting) || 'Geral', bookData?.id || id, writeDeckId || undefined);

      } catch (err) {
        console.error("[Reader] Failed to save exercises:", err);
      }
    }
  };

  const handleReimport = async () => {
    if (!id) return; // Allow even if bookData is null
    setLoading(true);
    setLoadError(false);

    try {
      const timestamp = new Date().getTime();
      // 1. Re-fetch from library-html.json to get fresh URL if needed
      const res = await fetch(`${LIBRARY_HTML_URL}?v=${timestamp}`);
      
      const contentType = res.headers.get("content-type");
      if (!res.ok || (contentType && contentType.includes("text/html"))) {
         throw new Error('Arquivo original não encontrado no servidor');
      }
      
      const library = await res.json();
      
      // Expande a busca para incluir o originalBookId (crucial para club_... books)
      const onlineBook = library.find((b: any) => {
        const bId = String(b.id);
        const bCustomId = String(b.custom_id);
        const currentId = String(id);
        const originalId = bookData?.originalBookId ? String(bookData.originalBookId) : null;

        // Strict matching hierarchy:
        // 1. Match by originalBookId if available (for community/pro books)
        if (originalId && (bId === originalId || bCustomId === originalId)) return true;
        // 2. Match by current ID param (for library books)
        if (bId === currentId || bCustomId === currentId) return true;
        
        return false;
      });

      if (onlineBook) {
        // 2. Refresh file content
        const fileRes = await fetch(onlineBook.url);
        
        if (!fileRes.ok) {
           throw new Error('Arquivo original não encontrado no servidor');
        }
        
        const html = await fileRes.text();
        const fixedHtml = fixHtmlContent(html);

        // 3. Update Local Storage & bookData
        const newBookData = {
          id: String(id), // Ensure it's a string
          title: onlineBook.title || bookData?.title || 'Livro Restaurado',
          author: onlineBook.author || bookData?.author || 'Desconhecido',
          coverUrl: onlineBook.cover || bookData?.coverUrl || `generated://${id}`,
          fileUrl: onlineBook.url,
          format: 'html',
          dateAdded: Date.now(),
          language: onlineBook.language || bookData?.language || 'en',
          lastPage: 1,
          totalPages: 0,
          category: 'LibriTui Library (HTML)'
        };
        
        storageService.saveBook(newBookData);
        setBookData(newBookData);

        // 4. Update state and reload
        const sections = splitHtmlToSections(fixedHtml);
        setRawSections(sections);
        setRenderedSections([{ idx: 0, html: processSectionHtml(sections[0], 0) }]);

        // 5. Restore Progress and Notes
        const safeUserId = (user && !user.isGuest) ? user.id : undefined;
        const progress = await storageService.getBestProgress(safeUserId, String(id));
        if (progress) setCurrentPage(progress.page);

        if (safeUserId) {
          // Sync with D1 to ensure the user now has this record
          try {
             await dbManager.from('books', safeUserId).upsert({
               custom_id: String(id),
               title: newBookData.title,
               author: newBookData.author,
               file_url: newBookData.fileUrl,
               cover_url: newBookData.coverUrl,
               last_page: progress?.page || 1,
               file_type: 'html',
               language: newBookData.language,
               category: 'LibriTui Library (HTML)'
             });
          } catch(e) { 
            console.error("Erro ao sincronizar re-importação com D1:", e);
          }

          const remoteNotes = await storageService.syncAnnotations(safeUserId, String(id));
          if (remoteNotes.length > 0) setAnnotations(remoteNotes);
        }

        setAppNotification({
          show: true,
          title: 'Restaurado!',
          message: 'Livro e notas recuperados com sucesso.',
          type: 'success'
        });
      } else {
        throw new Error('Arquivo original não encontrado no servidor');
      }
    } catch (err: any) {
      console.error("Re-import failed:", err);
      const isAuthError = err.message?.includes("Sessão expirada") || err.message?.includes("Status 403");
      
      if (isAuthError) {
        setAppNotification({
          show: true,
          title: "Erro de Sincronização",
          message: "A sessão expirou durante a re-importação. Por favor, faça login novamente.",
          type: 'error'
        });
      } else if (err.message === 'Arquivo original não encontrado no servidor') {
        // Exibe o erro de 404 clarificado para o usuário
        setLoadError(true);
        setAppNotification({
          show: true,
          title: "Falha na Importação",
          message: err.message,
          type: 'error'
        });
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleUI = (key: keyof typeof cleanUIConfig) => {
    setCleanUIConfig((prev: any) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleBatchDelete = () => {
    if (selectedNoteIds.size === 0) return;
    setIsBatchDeleteConfirmOpen(true);
  };

  const confirmBatchDelete = async () => {
    const total = selectedNoteIds.size;
    const idsToDelete = Array.from(selectedNoteIds);

    // 1. Remove Local Storage
    for (const noteId of idsToDelete) {
      storageService.deleteAnnotation(String(noteId));
    }

    // 2. Remove Cloud Data (if logged in)
    if (user && !user.isGuest && user.id) {
      try {
        // Batch delete in D1 via loop (or we could add a batch method to dbManager)
        // Batch delete in D1 via loop (recommended for reliability)
        for (const noteId of idsToDelete) {
          await translationService.deleteUserNote(user.id, String(noteId));
        }
      } catch (e) {
        console.error("Error deleting notes:", e);
      }
    }

    // 3. Update UI instantly
    const idsSet = new Set(idsToDelete);
    setAnnotations(prev => prev.filter(a => !idsSet.has(a.id)));
    setNoteUpdateToggle(v => v + 1);

    setSelectedNoteIds(new Set());
    setIsSelectionMode(false);
    setIsBatchDeleteConfirmOpen(false);
    setShowDeleteSuccess(true);
  };

  const handleStartSyncNew = async (langCode: string) => {
    setIsPickingLangForSync(false);
    setLoading(true);
    
    try {
      const existingFlashcards = await flashcardManager.getFlashcards();
      const existingPronunciation = notesDeckManager.getPronunciationNotes();
      const existingWriting = notesDeckManager.getWritingNotes();
      
      const locale = normalizeLangCode(langCode);
      const category = getLanguageName(langCode);
      
      const newItems = annotations.filter(a => {
        const { original } = parseNote(a.content);
        const normOriginal = original.trim().toLowerCase();
        
        const hasFlashcard = existingFlashcards.some(f => f.original.trim().toLowerCase() === normOriginal && f.lang_code === langCode);
        const hasPronunciation = existingPronunciation.some(p => p.original.trim().toLowerCase() === normOriginal && p.langCode === locale);
        const hasWriting = existingWriting.some(w => w.original.trim().toLowerCase() === normOriginal && w.langCode === locale);
        
        return !hasFlashcard && !hasPronunciation && !hasWriting;
      }).map(a => {
        const { original, translation } = parseNote(a.content);
        return {
          original,
          translation,
          locale,
          category,
          bookId: bookData?.id || id || ''
        };
      });
      
      if (newItems.length === 0) {
        setAppNotification({
          show: true,
          title: "Tudo Sincronizado!",
          message: "Todas as suas notas já possuem exercícios neste idioma.",
          type: 'info'
        });
      } else {
        const flashcardDecks = Array.from(new Set(existingFlashcards.filter(f => f.lang_code === langCode).map(f => f.category || getLanguageName(langCode))));
        const pronunciationDecks = Array.from(new Set(existingPronunciation.filter(p => p.langCode === locale).map(p => p.category)));
        const writingDecks = Array.from(new Set(existingWriting.filter(w => w.langCode === locale).map(w => w.category)));
        
        setShowSyncNewConfirm({ 
          count: newItems.length, 
          langCode, 
          items: newItems,
          flashcardDecks,
          pronunciationDecks,
          writingDecks
        });
      }
    } catch (e) {
      console.error("Error during sync check:", e);
    } finally {
      setLoading(false);
    }
  };

  const confirmSyncNew = async (config: GenerateExerciseModalConfig) => {
    if (!showSyncNewConfirm) return;
    
    const itemsWithDecks = showSyncNewConfirm.items.map(item => ({
      ...item,
      flashcardCategory: config.flashcards.deckName,
      pronunciationCategory: config.pronunciation.deckName,
      writingCategory: config.writing.deckName
    }));

    // Save to persistent Outbox BEFORE updating memory state to prevent loss on reload
    await syncOfflineService.addItems(itemsWithDecks);

    const newQueue = [...syncQueue, ...itemsWithDecks];
    setSyncQueue(newQueue);
    setShowSyncNewConfirm(null);
    
    setAppNotification({
      show: true,
      title: "Sincronização Iniciada! ⚡",
      message: "Criando seus exercícios... eles aparecerão gradualmente na sua aba de Estudos.",
      type: 'success'
    });
  };

  const openBatchFlashcardModal = async () => {
    if (selectedNoteIds.size === 0) return;
    openExerciseWizard(true);
  };

  const handleBatchWizardSave = async () => {
    setIsCreatingFlashcard(true);
    
    try {
      const userId = user?.id || 'guest';
      const decks = await dbManager.from('decks', userId).select() || [];

      const finalFlashLang = wizardConfig.flashcardLang.substring(0, 2).toLowerCase();
      const finalPronLang = wizardConfig.pronLang.substring(0, 2).toLowerCase();
      const finalWriteLang = wizardConfig.writeLang.substring(0, 2).toLowerCase();

      // Create Decks if needed
      let flashcardDeckId = null;
      if (wizardConfig.flashcardMode === 'new') {
        flashcardDeckId = crypto.randomUUID();
        await dbManager.from('decks', userId).insert({ id: flashcardDeckId, user_id: userId, name: (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting), lang_code: finalFlashLang, type: 'flashcard' });
      } else {
        const d = decks.find((x: any) => x.name === (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting) && x.type === 'flashcard');
        if (d) flashcardDeckId = d.id;
      }
      
      let pronDeckId = null;
      if (wizardConfig.pronMode === 'new') {
        pronDeckId = crypto.randomUUID();
        await dbManager.from('decks', userId).insert({ id: pronDeckId, user_id: userId, name: (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting), lang_code: finalPronLang, type: 'pronunciation' });
      } else {
        const d = decks.find((x: any) => x.name === (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting) && x.type === 'pronunciation');
        if (d) pronDeckId = d.id;
      }

      let writeDeckId = null;
      if (wizardConfig.writeMode === 'new') {
        writeDeckId = crypto.randomUUID();
        await dbManager.from('decks', userId).insert({ id: writeDeckId, user_id: userId, name: (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting), lang_code: finalWriteLang, type: 'writing' });
      } else {
        const d = decks.find((x: any) => x.name === (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting) && x.type === 'writing');
        if (d) writeDeckId = d.id;
      }

      const flashcardItems: any[] = [];
      const pronItems: any[] = [];
      const writeItems: any[] = [];

      for (const noteId of selectedNoteIds) {
        const note = annotations.find(a => a.id === noteId);
        if (note) {
          const { original, translation } = parseNote(note.content.split('|~|')[0]);

          flashcardItems.push({
            original, translation, locale: finalFlashLang, category: (wizardConfig.flashcardMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.flashcardDeckNew.trim(), wizardAvailableDecks.flashcard) : wizardConfig.flashcardDeckExisting), bookId: bookData?.id || id || '', deck_id: flashcardDeckId
          });
          pronItems.push({
            original, translation, locale: finalPronLang, category: (wizardConfig.pronMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.pronDeckNew.trim(), wizardAvailableDecks.pronunciation) : wizardConfig.pronDeckExisting), bookId: bookData?.id || id || '', deck_id: pronDeckId
          });
          writeItems.push({
            original, translation, locale: finalWriteLang, category: (wizardConfig.writeMode === 'new' ? getUniqueDeckNameWizard(wizardConfig.writeDeckNew.trim(), wizardAvailableDecks.writing) : wizardConfig.writeDeckExisting), bookId: bookData?.id || id || '', deck_id: writeDeckId
          });
        }
      }

      // Add to queues / save
      for (const item of flashcardItems) {
         await flashcardManager.saveFlashcard(item.original, item.translation, item.locale, item.bookId, item.category, item.deck_id);
      }
      for (const item of pronItems) {
         await notesDeckManager.addToPronunciation(item.original, item.translation, item.locale, item.category, item.bookId, item.deck_id);
      }
      for (const item of writeItems) {
         await notesDeckManager.addToWriting(item.original, item.translation, item.locale, item.category, item.bookId, item.deck_id);
      }

    } catch (e) {
      console.error(e);
    }

    setIsCreatingFlashcard(false);
    setSelectedNoteIds(new Set());
    setIsSelectionMode(false);
  };


  const handleJumpSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const pageNum = parseInt(jumpPageInput, 10);
    if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
      setJumpError(`Página inválida (1 - ${totalPages})`);
      return;
    }
    jumpToPage(pageNum);
    setIsJumpModalOpen(false);
    setJumpPageInput('');
    setJumpError(null);
  };

  if (loading) return (
    <div className="h-screen flex flex-col items-center justify-center bg-[var(--bg-main)] relative overflow-hidden select-none">
      {/* Voltar padrão do app */}
      <div className="absolute top-6 left-6 z-50">
        <AppBackButton fallbackTo="/libritui-library" />
      </div>

      {/* Carregamento: orbe girando e barra fina */}
      <div className="flex flex-col items-center max-w-sm px-6 text-center z-10">
        <div className="relative w-20 h-20 mb-6">
          <span className="absolute inset-0 rounded-full border border-ink/10" />
          <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
          <span className="absolute inset-3 rounded-full bg-tint-lavender/60" />
        </div>
        <p className="text-muted font-normal uppercase font-mono text-[11px] tracking-[0.14em] mb-4">
          {t('reader.syncingInteractivity', 'SINCRONIZANDO INTERATIVIDADE...')}
        </p>
        <div className="w-56 max-w-[70vw] h-1 bg-ink/[0.07] rounded-full overflow-hidden">
          <div className="h-full w-1/3 bg-ink rounded-full animate-[pulse_1.4s_ease-in-out_infinite]" />
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`reader-root-container select-none ${isDarkMode ? 'dark text-white' : 'text-slate-900'}`}
      style={{
        backgroundColor: activeTheme === 'custom' ? customBgColor : (activeTexture ? 'transparent' : (isDarkBase ? '#1C2430' : '#F4F1EC')),
        backgroundImage: activeTexture ? `url(/textures/${activeTexture})` : 'none',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        transition: 'background 0.35s ease',
        ...(isDarkBase || activeTheme === 'custom'
          ? { '--hud-bg': 'rgba(28, 36, 48, 0.82)', '--hud-ink': '#E6E0D6', '--hud-muted': 'rgba(230, 224, 214, 0.55)', '--hud-line': 'rgba(255, 255, 255, 0.08)', '--hud-soft': 'rgba(255, 255, 255, 0.07)', '--hud-strong': '#E6E0D6', '--hud-on-strong': '#1C2430', '--hud-solid': '#252F3E' }
          : { '--hud-bg': 'rgba(247, 244, 239, 0.86)', '--hud-ink': '#1C2430', '--hud-muted': 'rgba(28, 36, 48, 0.5)', '--hud-line': 'rgba(0, 0, 0, 0.07)', '--hud-soft': 'rgba(0, 0, 0, 0.045)', '--hud-strong': '#1C2430', '--hud-on-strong': '#F7F4EF', '--hud-solid': '#F7F4EF' }) as React.CSSProperties
      }}
    >

      {/* TOPO (Design 2.0): voltar, título do livro e ajustes */}
      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-[150] px-3 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isUIVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}
        style={{ top: 'max(12px, env(safe-area-inset-top))' }}
      >
        <div className="hud-element mx-auto w-full max-w-[640px] h-14 rounded-full pl-1.5 pr-1.5 flex items-center gap-2 shadow-[0_24px_60px_-32px_rgba(0,0,0,0.5)]">
          <HudButton
            id="tour-reader-back"
            label={t('common.back', 'Voltar')}
            tooltip="none"
            onClick={() => {
              storageService.syncPendingProgress().then(() => {
                if (refreshBooks) refreshBooks(true);
              });
              if (tourService.isActive() && tourService.getPhase() === 'GOTO_FLASHCARDS') {
                navigate('/study-hub');
              } else {
                goBackOr(navigate, '/library-hub');
              }
            }}
          >
            <ArrowLeft size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-translate-x-0.5" />
          </HudButton>

          <div className="flex-1 min-w-0 text-center">
            <h1 className="font-display text-[17px] md:text-lg font-medium tracking-[-0.01em] leading-tight truncate text-[color:var(--hud-ink)]">
              {bookData?.title}<span className="text-primary">.</span>
            </h1>
            <p className="text-[11px] leading-tight truncate text-[color:var(--hud-muted)]">
              {bookData?.author || bookData?.authors?.[0]?.name || t('reader.unknownAuthor', 'Autor desconhecido')}
            </p>
          </div>

          <HudButton
            id="tour-settings-hub"
            label={t('reader.settingsMenu', 'Ajustes de leitura')}
            tooltip="none"
            onClick={() => setIsSettingsMenuOpen(!isSettingsMenuOpen)}
            active={isSettingsMenuOpen}
          >
            <Settings size={18} strokeWidth={1.75} className={`transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${isSettingsMenuOpen ? 'rotate-90' : 'group-hover:rotate-45'}`} />
          </HudButton>
        </div>
      </header>

      <main
        className="reader-main-layout flex-1 relative overflow-hidden"
        style={{
          position: 'relative',
          height: '100vh',
          backgroundColor: activeTheme === 'custom' ? customBgColor : (isDarkBase ? '#151B24' : '#F7F4EF')
        }}
        onClick={(e) => { if (!showControls) setShowControls(true); handleInteractiveClick(e); }}
      >
        {/* TEXT CONTAINER - Paginated view, only shows current section */}
        <div
          className="w-full flex overflow-hidden"
          style={{
            height: '100vh',
            marginTop: '0px',
            position: 'relative',
            zIndex: 10
          }}
        >
          <div
            ref={viewerRef}
            className={`h-full w-full overflow-y-auto custom-scrollbar viewer-container ${activeTheme === 'light' ? 'claro-texto-preto' : ''}`}
            id="tour-reader-text"
            style={{
              fontSize: `${readerFontSize}px`,
              fontFamily: READER_FONTS.find(f => f.name === readerFontFamily)?.family || 'inherit',
              fontWeight: readerIsBold ? 'black' : 'normal',
              fontStyle: readerIsItalic ? 'italic' : 'normal',
              textAlign: readerTextAlign as any,
              lineHeight: '1.7',
               paddingTop: `calc(${dynamicHeaderHeight}px + ${readerVerticalSpacing === 'short' ? '20px' : readerVerticalSpacing === 'long' ? '120px' : '60px'})`,
               paddingLeft: readerMarginSize === 'narrow' ? '6px' : readerMarginSize === 'medium' ? '32px' : '64px',
               paddingRight: readerMarginSize === 'narrow' ? '6px' : readerMarginSize === 'medium' ? '32px' : '64px',
               paddingBottom: `calc(${FOOTER_HEIGHT}px + ${readerVerticalSpacing === 'short' ? '40px' : readerVerticalSpacing === 'long' ? '140px' : '80px'})`,
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap', // Honor newlines as actual line breaks
              color: activeTheme === 'custom' ? customTextColor : (isDarkBase ? '#E6E0D6' : '#1C2430'),
              mixBlendMode: htmlBlendMode as any,
              pointerEvents: isCreativeMode ? 'none' : 'auto',
              userSelect: isCreativeMode ? 'none' : 'auto',
              WebkitUserSelect: isCreativeMode ? 'none' : 'auto',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              filter: isShadowingActive ? 'blur(3px)' : 'none',
              transition: 'filter 0.3s ease'
            }}
          >
            {/* Find only the current section to display */}
            {renderedSections.filter(s => s.idx === currentSectionIdx).map((section) => (
              <React.Fragment key={`sec-${section.idx}`}>
                <div
                  className="section-packet w-full"
                  dangerouslySetInnerHTML={{ __html: section.html }}
                />
                {/* Visual Spacer at end of each page content */}
                <div style={{ height: '15px', width: '100%' }} aria-hidden="true" />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* SHADOWING OVERLAY */}
        {isShadowingActive && (
          <ShadowingSentenceOverlay
            currentSentence={shadowingCurrentSentence}
            sentencesCount={shadowingSentences.length}
            currentSentenceIndex={shadowingSentenceIdx}
            wordStatuses={shadowingWordStatuses}
            isListening={isShadowingListening}
            isTtsPlaying={isShadowingTtsPlaying}
            hasTtsFinished={hasShadowingTtsFinished}
            transcript={shadowingTranscript}
            isSentenceComplete={isShadowingSentenceComplete}
            ttsRate={shadowingTtsRate}
            onCycleTtsRate={cycleShadowingTtsRate}
            onPlayTts={playShadowingSentenceTts}
            onPlayWordTts={playShadowingWordTts}
            onStartRecording={startShadowingRecording}
            onStopRecording={stopShadowingRecording}
            onPrevSentence={handlePrevShadowingSentence}
            onNextSentence={handleNextShadowingSentence}
            onCloseShadowing={toggleShadowing}
          />
        )}

        {/* CANVAS DRAWING OVERLAY - Renders current stroke and saved strokes */}
        <canvas
          key={`canvas-overlay-${drawRedrawKey}`}
          ref={canvasRef}
          className="absolute top-0 left-0 w-full h-full"
          style={{
            zIndex: isCreativeMode ? 160 : 10,
            pointerEvents: isCreativeMode ? 'auto' : 'none',
            cursor: isCreativeMode ? 'crosshair' : 'default',
            touchAction: isCreativeMode ? 'none' : 'auto',
          }}
          onPointerDown={handleDrawingStart}
          onPointerMove={handleDrawingMove}
          onPointerUp={handleDrawingEnd}
          onPointerLeave={handleDrawingEnd}
        />


        {/* BOTTOM SELECTION MENU (Swaps with footer) */}
        <div
          className={`fixed bottom-5 md:bottom-6 inset-x-0 mx-auto w-max max-w-[calc(100vw-24px)] z-[160] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${selectedWords.length > 0 && !translation && !isTranslating && !explanation && !isExplaining
            ? 'translate-y-0 opacity-100 pointer-events-auto'
            : 'translate-y-[160%] opacity-0 pointer-events-none'
            }`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="hud-element rounded-full p-2 flex items-center gap-1 md:gap-1.5 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in-95 duration-500">
            <span
              className="h-11 min-w-[44px] px-3 rounded-full bg-[color:var(--hud-soft)] font-mono text-[12px] tabular-nums flex items-center justify-center text-[color:var(--hud-ink)]"
              title={t('reader.selectedWords', 'Palavras selecionadas')}
            >
              <span key={selectedWords.length} className="inline-block animate-in zoom-in-50 fade-in duration-300">{selectedWords.length}</span>
            </span>

            <span className="w-px h-6 bg-[color:var(--hud-line)] mx-1" />

            <HudButton id="tour-reader-translate" label={t('reader.translate', 'Traduzir')} onClick={() => handleAction('traduzir')}>
              <Languages size={19} strokeWidth={1.75} className="transition-transform duration-500 group-hover:-rotate-12" />
            </HudButton>

            <HudButton id="tour-reader-play" label={t('reader.listen', 'Ouvir')} onClick={() => handleAction('ouvir')} active={isSpeaking}>
              <Volume2 size={19} strokeWidth={1.75} className={isSpeaking ? 'animate-pulse' : 'transition-transform duration-500 group-hover:translate-x-0.5 group-hover:scale-110'} />
            </HudButton>

            <HudButton label={t('reader.copyText', 'Copiar')} onClick={handleCopy}>
              <Copy size={18} strokeWidth={1.75} className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </HudButton>

            <HudButton
              label={t('reader.shareQuote', 'Compartilhar')}
              onClick={() => {
                const fullText = selectedWords.map(w => w.text).join(' ');
                navigate('/sharing', {
                    state: {
                        shareQuote: {
                            text: fullText,
                            bookId: bookData?.id,
                            bookTitle: bookData?.title || '',
                            authorName: bookData?.authors?.[0]?.name || bookData?.author || '',
                            coverUrl: bookData?.coverUrl || bookData?.cover_url || null,
                        }
                    }
                });
                clearSelection();
              }}
            >
              <Share2 size={18} strokeWidth={1.75} className="transition-transform duration-500 group-hover:rotate-[20deg] group-hover:scale-110" />
            </HudButton>

            <span className="w-px h-6 bg-[color:var(--hud-line)] mx-1" />

            <HudButton label={t('reader.clearSelection', 'Limpar seleção')} onClick={clearSelection} tone="danger">
              <X size={18} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
            </HudButton>
          </div>
        </div>
      </main>

      {/* RODAPÉ (Design 2.0): player em destaque e, abaixo, a paginação com 70% de opacidade */}
      <div
        className={`bottom-fixed-footer hud-element shadow-[0_24px_60px_-28px_rgba(0,0,0,0.5)] ${((selectedWords.length > 0 && !translation && !isTranslating && !explanation && !isExplaining) || isThemeModalOpen || isReadingSettingsModalOpen)
        ? 'translate-y-[160%] opacity-0 pointer-events-none'
        : `translate-y-0 pointer-events-auto ${isUIVisible ? 'opacity-100' : 'opacity-50 hover:opacity-100'}`
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Progresso do livro: linha fina no topo da barra */}
        <div className="absolute left-8 right-8 top-0 h-[2px] rounded-full bg-[color:var(--hud-line)] overflow-hidden pointer-events-none">
          <div
            className="h-full rounded-full bg-[color:var(--hud-ink)] opacity-70 transition-[width] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
            style={{ width: `${totalPages ? (currentPage / totalPages) * 100 : 0}%` }}
          />
        </div>

        {/* Player */}
        <div className="flex items-center justify-center gap-2 md:gap-3">
          <HudButton
            label={t('reader.readingLang', 'Idioma da leitura')}
            onClick={() => {
              textToSpeechService.stopPageReading();
              handlePageStop();
              setIsReaderLangModalOpen(true);
            }}
            tone={bookLangDetected ? 'default' : 'warn'}
          >
            <span className="relative flex">
              <Globe size={18} strokeWidth={1.75} className="transition-transform duration-700 group-hover:rotate-[30deg]" />
              {!bookLangDetected && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-warn animate-pulse" />}
            </span>
          </HudButton>

          <HudButton label={`${t('reader.speed', 'Velocidade')} ${ttsRate.toFixed(1)}x`} onClick={handleCycleSpeed}>
            <span key={ttsRate} className="font-mono text-[11px] tabular-nums tracking-tight animate-in fade-in zoom-in-90 duration-300">{ttsRate.toFixed(1)}×</span>
          </HudButton>

          {/* Play / pausa: botão principal, com anel pulsando enquanto lê */}
          <button
            type="button"
            onClick={pageReadingStatus.isReading && !pageReadingStatus.isPaused ? handlePagePause : handlePagePlay}
            aria-label={pageReadingStatus.isReading && !pageReadingStatus.isPaused ? t('reader.pause', 'Pausar') : t('reader.play', 'Ouvir página')}
            className="group relative mx-1 w-14 h-14 rounded-full bg-[color:var(--hud-strong)] text-[color:var(--hud-on-strong)] flex items-center justify-center transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:scale-105 active:scale-90"
          >
            {pageReadingStatus.isReading && !pageReadingStatus.isPaused && (
              <span className="absolute inset-0 rounded-full ring-2 ring-[color:var(--hud-strong)] animate-ping opacity-30" />
            )}
            {pageReadingStatus.isReading && !pageReadingStatus.isPaused ? (
              <Pause size={21} fill="currentColor" strokeWidth={0} />
            ) : (
              <Play size={21} fill="currentColor" strokeWidth={0} className="ml-0.5 transition-transform duration-300 group-hover:scale-110" />
            )}
          </button>

          <HudButton
            label={t('reader.stopReading', 'Parar leitura')}
            onClick={() => { textToSpeechService.stopPageReading(); handlePageStop(); }}
            disabled={!pageReadingStatus.isReading}
          >
            <Square size={14} fill="currentColor" strokeWidth={0} />
          </HudButton>

          <HudButton
            id="tour-shadowing-toggle"
            label={t('reader.shadowingMode', 'Modo Shadowing')}
            onClick={handleToggleShadowing}
            active={isShadowingActive}
            tone="ok"
          >
            <ShadowingProfileIcon size={19} />
          </HudButton>
        </div>

        {/* Paginação (70% de opacidade) */}
        <div className="flex items-center justify-center gap-0.5 md:gap-1 opacity-70 hover:opacity-100 transition-opacity duration-300">
          <HudButton label={t('reader.firstPage', 'Primeira página')} onClick={() => jumpToPage(1)} disabled={currentPage <= 1} size="sm">
            <ChevronsLeft size={16} strokeWidth={1.75} />
          </HudButton>
          <HudButton label={t('reader.previousPage', 'Página anterior')} onClick={() => jumpToPage(currentPage - 1)} disabled={currentPage <= 1} size="sm">
            <ChevronLeft size={18} strokeWidth={1.75} />
          </HudButton>

          <button
            type="button"
            onClick={() => setIsJumpModalOpen(true)}
            title={t('reader.goToPage', 'Ir para página')}
            className="group h-9 min-w-[112px] px-3 rounded-full flex items-center justify-center gap-2 hover:bg-[color:var(--hud-soft)] transition-colors duration-300"
          >
            <span className="font-mono text-[12px] tabular-nums tracking-[0.04em] text-[color:var(--hud-ink)] whitespace-nowrap">
              <span key={currentPage} className="inline-block animate-in fade-in slide-in-from-bottom-2 duration-300">{currentPage}</span>
              <span className="text-[color:var(--hud-muted)]"> / {totalPages}</span>
            </span>
            <Asterisk size={13} strokeWidth={2} className="text-[color:var(--hud-muted)] transition-transform duration-500 group-hover:rotate-90 group-hover:text-[color:var(--hud-ink)]" />
          </button>

          <HudButton label={t('reader.nextPage', 'Próxima página')} onClick={() => jumpToPage(currentPage + 1)} disabled={currentPage >= totalPages} size="sm">
            <ChevronRight size={18} strokeWidth={1.75} />
          </HudButton>
          <HudButton label={t('reader.lastPage', 'Última página')} onClick={() => jumpToPage(totalPages)} disabled={currentPage >= totalPages} size="sm">
            <ChevronsRight size={16} strokeWidth={1.75} />
          </HudButton>
        </div>
      </div>

      {/* MENU DE AJUSTES: por cima de toda a janela, com blur no que está por baixo */}
      {isSettingsMenuOpen && (
        <div
          className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-black/20 backdrop-blur-[20px] animate-in fade-in duration-300"
          onClick={() => setIsSettingsMenuOpen(false)}
        >
          <div className="w-full max-w-sm animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5 px-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--hud-ink)] opacity-60">
                {t('reader.settingsMenu', 'Ajustes de leitura')}
              </p>
              <HudButton label={t('common.close', 'Fechar')} onClick={() => setIsSettingsMenuOpen(false)} size="sm" tooltip="none">
                <X size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
              </HudButton>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: 'theme', label: t('reader.themeLabel', 'Tema'), tint: 'bg-tint-lavender', icon: <Contrast size={20} strokeWidth={1.75} className="transition-transform duration-700 group-hover:rotate-180" />, onClick: () => { setIsThemeModalOpen(true); setIsSettingsMenuOpen(false); } },
                { key: 'font', label: t('reader.fontLabel', 'Fonte'), tint: 'bg-tint-peach', icon: <Type size={20} strokeWidth={1.75} className="transition-transform duration-500 group-hover:scale-125" />, onClick: () => { setIsReadingSettingsModalOpen(true); setIsSettingsMenuOpen(false); } },
                { key: 'notes', label: t('reader.notesLabel', 'Notas'), tint: 'bg-tint-sage', icon: <MessageSquare size={20} strokeWidth={1.75} className="transition-transform duration-500 group-hover:-rotate-12 group-hover:-translate-y-0.5" />, onClick: () => { setIsSidebarOpen(true); setIsSettingsMenuOpen(false); } },
              ].map((item, i) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={item.onClick}
                  style={{ animationDelay: `${80 + i * 60}ms` }}
                  className="group hud-element aspect-square rounded-[28px] flex flex-col items-center justify-center gap-3 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] hover:-translate-y-1 active:scale-95 animate-in fade-in slide-in-from-bottom-4"
                >
                  <span className={`w-12 h-12 rounded-full ${item.tint} text-ink flex items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110`}>
                    {item.icon}
                  </span>
                  <span className="text-[13px] font-medium text-[color:var(--hud-ink)]">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* TEMA (Design 2.0): folha minimalista na base, sem escurecer o livro para ver a mudança ao vivo */}
      {
        isThemeModalOpen && (
          <div className="fixed inset-0 z-[6000] flex items-end justify-center p-3 animate-in fade-in duration-300" onClick={() => setIsThemeModalOpen(false)}>
            <div className="w-full max-w-md bg-[color:var(--hud-solid)] text-[color:var(--hud-ink)] border border-[color:var(--hud-line)] rounded-[28px] p-5 shadow-[0_32px_80px_-32px_rgba(0,0,0,0.55)] animate-in fade-in slide-in-from-bottom-8 duration-500" style={{ marginBottom: 'env(safe-area-inset-bottom)' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-xl font-light tracking-[-0.04em] text-[color:var(--hud-ink)]">
                  {t('reader.themeLabel', 'Tema')}<span className="text-primary">.</span>
                </h3>
                <HudButton label={t('common.close', 'Fechar')} onClick={() => setIsThemeModalOpen(false)} size="sm" tooltip="none">
                  <X size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                </HudButton>
              </div>

              {(() => {
                const applyPreset = (preset: { theme: string; bg?: string; text?: string }) => {
                  setActiveTexture(null);
                  localStorage.removeItem('readerTexture');
                  if (preset.theme === 'custom') {
                    setCustomBgColor(preset.bg!);
                    setCustomTextColor(preset.text!);
                    localStorage.setItem('readerCustomBg', preset.bg!);
                    localStorage.setItem('readerCustomText', preset.text!);
                  }
                  setActiveTheme(preset.theme as any);
                  localStorage.setItem('readerTheme', preset.theme);
                };
                const presets = [
                  { key: 'light', label: t('reader.lightTheme', 'Claro'), theme: 'light', swatch: '#F7F4EF', ink: '#1C2430' },
                  { key: 'sepia', label: t('reader.sepiaTheme', 'Sépia'), theme: 'custom', bg: '#f1e7d0', text: '#3b2f1e', swatch: '#f1e7d0', ink: '#3b2f1e' },
                  { key: 'dark', label: t('reader.darkTheme', 'Escuro'), theme: 'dark', swatch: '#151B24', ink: '#E6E0D6' },
                  { key: 'night', label: t('reader.nightTheme', 'Noite'), theme: 'custom', bg: '#000000', text: '#b9b4aa', swatch: '#000000', ink: '#b9b4aa' },
                ];
                const isActive = (p: typeof presets[number]) => !activeTexture && (p.theme === 'custom'
                  ? activeTheme === 'custom' && customBgColor.toLowerCase() === p.bg && customTextColor.toLowerCase() === p.text
                  : activeTheme === p.theme);
                return (
                  <div className="grid grid-cols-4 gap-2">
                    {presets.map((p, i) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => applyPreset(p)}
                        style={{ animationDelay: `${i * 50}ms` }}
                        className="group flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500"
                      >
                        <span
                          className={`w-14 h-14 rounded-full flex items-center justify-center font-serif text-lg transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105 ${isActive(p) ? 'ring-2 ring-offset-2 ring-[color:var(--hud-ink)] ring-offset-transparent scale-105' : 'ring-1 ring-[color:var(--hud-line)]'}`}
                          style={{ backgroundColor: p.swatch, color: p.ink }}
                        >
                          Aa
                        </span>
                        <span className={`text-[11px] ${isActive(p) ? 'text-[color:var(--hud-ink)] font-medium' : 'text-[color:var(--hud-muted)]'}`}>{p.label}</span>
                      </button>
                    ))}
                  </div>
                );
              })()}

              <div className="mt-5 pt-4 border-t border-[color:var(--hud-line)]">
                <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--hud-muted)] mb-3">{t('reader.customColor', 'Cor personalizada')}</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'bg', label: t('reader.backgroundLabel', 'Fundo'), value: customBgColor, store: 'readerCustomBg', set: setCustomBgColor },
                    { key: 'text', label: t('reader.fontColor', 'Texto'), value: customTextColor, store: 'readerCustomText', set: setCustomTextColor },
                  ].map(c => (
                    <label key={c.key} className="group relative h-12 rounded-full bg-[color:var(--hud-soft)] hover:bg-[color:var(--hud-line)] transition-colors duration-300 flex items-center gap-3 pl-1.5 pr-4 cursor-pointer overflow-hidden">
                      <span className="w-9 h-9 rounded-full ring-1 ring-[color:var(--hud-line)] transition-transform duration-300 group-hover:scale-110" style={{ backgroundColor: c.value }} />
                      <span className="text-[13px] text-[color:var(--hud-ink)]">{c.label}</span>
                      <input
                        type="color"
                        value={c.value}
                        onChange={(e) => {
                          const newColor = e.target.value;
                          c.set(newColor);
                          setActiveTheme('custom');
                          setActiveTexture(null);
                          localStorage.setItem('readerTheme', 'custom');
                          localStorage.setItem(c.store, newColor);
                          localStorage.removeItem('readerTexture');
                        }}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        title={c.label}
                      />
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      }
      {
        (translation || isTranslating || explanation || isExplaining) && (
          <div className="fixed inset-0 z-[3000] flex items-end sm:items-center justify-center p-3 sm:p-6 bg-slate-950/50 backdrop-blur-md px-safe py-safe pointer-events-auto animate-in fade-in duration-300" onClick={closeActionModals}>
            <div className="bg-surface text-ink w-full max-w-lg max-h-[88dvh] overflow-y-auto custom-scrollbar rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] shadow-[0_32px_80px_-32px_rgba(0,0,0,0.5)] animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 pt-5 md:px-8 md:pt-7">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-tint-lavender text-ink flex items-center justify-center"><Languages size={12} strokeWidth={1.75} /></span>
                  {t('reader.translationLabel', 'Tradução')}
                </span>
                <button onClick={closeActionModals} aria-label={t('common.close', 'Fechar')} className="group w-9 h-9 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone transition-colors duration-300 shrink-0 cursor-pointer">
                  <X size={17} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                </button>
              </div>

              <div className="px-6 md:px-8 pt-5 pb-6">
                <div className="flex items-start gap-3 border-l-2 border-primary/80 pl-4">
                  <p className="flex-1 min-w-0 font-serif italic text-[15px] md:text-base leading-relaxed text-muted line-clamp-4">
                    {wordsUnderAction.map(w => w.text).join(' ')}
                  </p>
                  <button
                    type="button"
                    onClick={() => handlePlayNoteAudio(wordsUnderAction.map(w => w.text).join(' '))}
                    aria-label={t('reader.listen', 'Ouvir')}
                    className="shrink-0 -mt-0.5 w-9 h-9 rounded-full bg-primary/15 text-primary hover:bg-primary hover:text-slate-900 flex items-center justify-center transition-colors duration-300 active:scale-95 cursor-pointer"
                  >
                    <Volume2 size={15} strokeWidth={1.75} />
                  </button>
                </div>

                <div className="mt-6 min-h-[72px]">
                  {isTranslating || isExplaining ? (
                    <div className="space-y-2.5 animate-in fade-in duration-300" aria-live="polite">
                      <div className="h-4 rounded-full bg-tint-stone animate-pulse w-11/12" />
                      <div className="h-4 rounded-full bg-tint-stone animate-pulse w-8/12" />
                      <p className="pt-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted flex items-center gap-2">
                        <Loader2 className="animate-spin text-primary" size={12} />
                        {t('settings.translation.translating', 'Traduzindo...')}
                      </p>
                    </div>
                  ) : (
                    <div className="group/tr relative animate-in fade-in slide-in-from-bottom-2 duration-500">
                      <p className="text-xl md:text-2xl font-normal tracking-[-0.02em] leading-snug whitespace-pre-wrap text-ink pr-10">
                        {translation}
                      </p>
                      <button
                        type="button"
                        onClick={() => { if (translation) navigator.clipboard?.writeText(String(translation)).catch(() => {}); }}
                        aria-label={t('reader.copyText', 'Copiar')}
                        className="absolute top-0 right-0 w-8 h-8 rounded-full flex items-center justify-center text-muted hover:text-ink hover:bg-tint-stone opacity-60 group-hover/tr:opacity-100 transition-all duration-300"
                      >
                        <Copy size={14} strokeWidth={1.75} />
                      </button>
                    </div>
                  )}
                </div>
                {wordDict && !isTranslating && (() => {
                  const rich: any = wordDict.richItem;
                  const defs = ((rich?.definitions || []) as any[])
                    .map(d => typeof d === 'string' ? { pos: '', text: d } : { pos: String(d?.pos || ''), text: String(d?.definition || d?.text || '') })
                    .map(d => ({ ...d, text: d.text.replace(/\[\[.*?\]\]/g, '').trim() }))
                    .filter(d => d.text);
                  const exs = ((rich?.examples || []) as any[]).filter(Boolean).map(String);
                  const trs = Array.from(new Set([
                    ...(wordDict.translations || []),
                    ...((rich?.dictionary || []) as any[]).flatMap(g => (g?.translations || []).map((tr: any) => String(tr?.word || '')))
                  ])).filter(Boolean);
                  const tabs = [
                    { key: 'definitions' as const, label: t('dictionary.definitions', 'Definições') },
                    { key: 'examples' as const, label: t('dictionary.examples', 'Exemplos') },
                    { key: 'translations' as const, label: t('dictionary.translations', 'Traduções') },
                  ];
                  return (
                    <div className="mt-6 animate-in fade-in duration-500">
                      <div className="flex gap-1 p-1 rounded-full bg-tint-stone" role="tablist">
                        {tabs.map(tab => (
                          <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={wordDictTab === tab.key}
                            onClick={() => setWordDictTab(tab.key)}
                            className={`flex-1 h-8 rounded-full text-[12px] font-medium transition-colors duration-300 cursor-pointer ${wordDictTab === tab.key ? 'bg-surface text-ink shadow-sm' : 'text-muted hover:text-ink'}`}
                          >
                            {tab.label}
                          </button>
                        ))}
                      </div>
                      <div className="mt-4 max-h-[32dvh] overflow-y-auto custom-scrollbar pr-1">
                        {wordDictTab === 'definitions' && (defs.length ? (
                          <ol className="space-y-3">
                            {defs.map((d, i) => (
                              <li key={i} className="flex gap-3 text-[14px] leading-relaxed text-ink">
                                <span className="shrink-0 w-5 text-right text-primary font-medium tabular-nums">{i + 1}</span>
                                <span className="min-w-0">
                                  {d.pos && <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.12em] text-muted">{d.pos}</span>}
                                  {d.text}
                                </span>
                              </li>
                            ))}
                          </ol>
                        ) : <p className="text-[13px] text-muted">{t('dictionary.noDefinitions', 'Sem definições para esta palavra.')}</p>)}
                        {wordDictTab === 'examples' && (exs.length ? (
                          <ul className="space-y-3">
                            {exs.map((ex, i) => (
                              <li key={i} className="font-serif italic text-[14px] leading-relaxed text-ink border-l-2 border-tintd-sky pl-3">{ex}</li>
                            ))}
                          </ul>
                        ) : <p className="text-[13px] text-muted">{t('dictionary.noExamples', 'Sem exemplos para esta palavra.')}</p>)}
                        {wordDictTab === 'translations' && (trs.length ? (
                          <div className="flex flex-wrap gap-2">
                            {trs.map((tr, i) => (
                              <span key={i} className="px-3 py-1.5 rounded-full bg-tint-stone text-[13px] text-ink">{tr}</span>
                            ))}
                          </div>
                        ) : <p className="text-[13px] text-muted">{t('dictionary.noTranslations', 'Sem outras traduções.')}</p>)}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <div className="px-6 md:px-8 pb-6 md:pb-8 flex flex-col gap-2">
                <button
                  id="tour-save-note"
                  disabled={isTranslating || isExplaining || !translation}
                  onClick={() => saveNote()}
                  className="group w-full h-12 rounded-full bg-primary text-slate-900 text-[13px] font-medium flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 active:scale-[0.98] disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
                >
                  <Check size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:scale-110" />
                  {t('reader.saveNote', 'Salvar nota')}
                </button>
                <button
                  disabled={isTranslating || isExplaining || !translation}
                  onClick={() => saveNoteAndCreateExercises()}
                  className="group w-full h-12 rounded-full border border-black/[0.08] dark:border-white/[0.08] text-ink text-[13px] font-medium flex items-center justify-center gap-2 transition-all duration-300 hover:bg-tint-lavender active:scale-[0.98] disabled:opacity-30 disabled:pointer-events-none cursor-pointer"
                >
                  <Brain size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:-rotate-12" />
                  {t('reader.saveNoteAndExercises', 'Salvar nota e criar exercício')}
                </button>
                <button
                  onClick={closeActionModals}
                  className="w-full h-10 rounded-full text-muted hover:text-ink text-[12px] font-medium transition-colors duration-300 cursor-pointer"
                >
                  {t('common.close', 'Fechar')}
                </button>
              </div>
            </div>
          </div>
        )
      }



      {/* TTS PLAYER MODAL (Design Sincronizado com PDF) */}
      {
        isTtsPlayerModalOpen && (
          <div className={`fixed inset-0 z-[400] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300 ${isUIVisible ? '' : 'ui-invisible'}`} onClick={() => { setIsTtsPlayerModalOpen(false); handleStopSpeak(); }}>
            <div className="bg-surface w-full max-w-sm rounded-[28px] p-8 pt-12 flex flex-col items-center gap-8 relative animate-in zoom-in-95 border border-black/[0.06] dark:border-white/[0.06]" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => { setIsTtsPlayerModalOpen(false); handleStopSpeak(); }}
                className="absolute top-4 right-4 p-2 bg-primary text-slate-900 rounded-full hover:scale-110 active:scale-95 transition-all z-20"
              >
                <X size={18} />
              </button>

              <div className="relative w-full flex justify-center">
                <button
                  onClick={() => setIsTtsLangPickerOpen(!isTtsLangPickerOpen)}
                  className="w-auto px-4 py-2 rounded-xl flex items-center justify-center gap-2 text-ink font-medium text-[13px] hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors group"
                >
                  {TTS_OPTIONS.find(o => o.locale === ttsModalLocale)?.name || ttsModalLocale}
                  <ChevronRight size={18} className={`text-slate-400 group-hover:translate-x-1 transition-all ${isTtsLangPickerOpen ? 'rotate-90' : ''}`} />
                </button>

                {isTtsLangPickerOpen && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-56 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] p-2 z-[210] max-h-60 overflow-y-auto custom-scrollbar animate-in slide-in-from-top-2">
                    {TTS_OPTIONS.map(opt => (
                      <button
                        key={opt.locale}
                        onClick={() => { setTtsModalLocale(opt.locale); setIsTtsLangPickerOpen(false); handleStopSpeak(); }}
                        className={`w-full text-left px-4 py-3 rounded-xl text-xs font-bold transition-all ${ttsModalLocale === opt.locale ? 'bg-ink text-surface' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                      >
                        {opt.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-full h-px bg-slate-100 dark:bg-slate-800" />

              <div className="w-full max-h-48 overflow-y-auto custom-scrollbar px-2 text-center">
                <p className="text-slate-600 dark:text-slate-300 font-medium leading-relaxed text-[17px] tracking-tight">
                  {selectedWords.map(w => w.text).join(' ')}
                </p>
              </div>

              <div className="w-full h-px bg-slate-100 dark:bg-slate-800" />

              <div className="flex items-center gap-8">
                <button
                  onClick={handleStopSpeak}
                  className="w-12 h-12 rounded-full border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-primary hover:bg-primary/5 transition-all active:scale-90"
                  title="Reiniciar"
                >
                  <RotateCcw size={22} />
                </button>

                <button
                  onClick={handleModalPlay}
                  className="w-20 h-20 rounded-full bg-primary flex items-center justify-center text-slate-900 active:scale-95 transition-all group"
                >
                  {isTtsModalPlaying ? (
                    <Pause size={32} fill="currentColor" />
                  ) : (
                    <Play size={32} fill="currentColor" className="ml-1.5" />
                  )}
                </button>

                <div className="relative group/speed">
                  <button
                    className="w-12 h-12 rounded-full border border-black/[0.06] dark:border-white/[0.06] flex items-center justify-center text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                    title="Velocidade"
                  >
                    <Gauge size={22} />
                  </button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 bg-surface border border-black/[0.06] dark:border-white/[0.06] p-4 rounded-3xl opacity-0 invisible group-hover/speed:opacity-100 group-hover/speed:visible transition-all">
                    <div className="flex flex-col items-center gap-2">
                      <span className="text-[9px] font-normal text-primary uppercase tracking-[0.14em] font-mono">{ttsModalRate.toFixed(1)}x</span>
                      <input
                        type="range" min="0.5" max="1.5" step="0.1" value={ttsModalRate}
                        onChange={(e) => setTtsModalRate(parseFloat(e.target.value))}
                        className="w-24 -rotate-90 appearance-none bg-slate-200 dark:bg-slate-800 h-1.5 rounded-full accent-primary cursor-pointer my-10"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {showNoteCreatedModal && (
        <div className="fixed inset-0 z-[7000] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
          <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-sm text-center animate-in zoom-in-95 duration-200">
            <div className="w-14 h-14 bg-tint-sage rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="text-ink" size={22} strokeWidth={1.75} />
            </div>
            <h3 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2 leading-tight">
              {t('reader.success')}
            </h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-8 leading-relaxed">
              A nota foi criada com sucesso!
            </p>
            <button
              onClick={() => setShowNoteCreatedModal(false)}
              className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full active:scale-95 transition-all"
            >
              Confirmar
            </button>
          </div>
        </div>
      )}

      {
        isHideMenuOpen && (
          <div className={`fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in duration-200 ${isUIVisible ? '' : 'ui-invisible'}`} onClick={() => setIsHideMenuOpen(false)}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[70vw] max-w-lg animate-in zoom-in-95" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('reader.hideElements')}</h3>
                <button onClick={() => setIsHideMenuOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors"><X size={20} /></button>
              </div>
              <div className="space-y-4">
                <UIRow label={t('reader.topBar')} active={cleanUIConfig.header} onToggle={() => toggleUI('header')} />
                <UIRow label={t('reader.playerControls')} active={cleanUIConfig.player} onToggle={() => toggleUI('player')} />
                <UIRow label={t('reader.pages')} active={cleanUIConfig.pages} onToggle={() => toggleUI('pages')} />
                <UIRow label={t('reader.savedNotes')} active={cleanUIConfig.notes} onToggle={() => toggleUI('notes')} />
              </div>
              <button onClick={() => setIsHideMenuOpen(false)} className="text-[13px] font-medium w-full mt-6 h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full">{t('reader.done')}</button>
            </div>
          </div>
        )
      }

      {
        wizardStep && (
          <div className="fixed inset-0 z-[3000] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-md animate-in fade-in duration-300" onClick={() => { setWizardStep(null); setPendingFlashcard(null); setIsBatchFlashcard(false); setWizardConfig(prev => ({...prev, flashcardDeckExisting: '', flashcardDeckNew: '', pronDeckExisting: '', pronDeckNew: '', writeDeckExisting: '', writeDeckNew: ''})); }}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[90vw] max-w-sm text-center animate-in zoom-in-95 duration-200 flex flex-col max-h-[85vh] relative" onClick={(e) => e.stopPropagation()}>
              <button 
                onClick={() => { setWizardStep(null); setPendingFlashcard(null); setIsBatchFlashcard(false); setShowWizardError(false); setWizardConfig(prev => ({...prev, flashcardDeckExisting: '', flashcardDeckNew: '', pronDeckExisting: '', pronDeckNew: '', writeDeckExisting: '', writeDeckNew: ''})); }} 
                className="absolute top-6 left-6 text-slate-400 opacity-50 hover:opacity-100 transition-opacity z-10"
              >
                <X size={24} />
              </button>
              <div className="w-14 h-14 bg-tint-sage rounded-full flex items-center justify-center mx-auto mb-6 shrink-0 relative mt-2">
                <Brain className="text-primary" size={32} />
              </div>
              <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-1 leading-tight">
                Passo {wizardStep} de 3
              </h3>
              <p className="text-xl font-medium text-ink tracking-[-0.02em] mb-6 leading-relaxed">
                Configurar {wizardStep === 1 ? 'Flashcards' : wizardStep === 2 ? 'Pronúncia' : 'Escrita'}
              </p>

              <div className="flex-1 mb-6 text-left space-y-4">
                
                {/* TABS */}
                <div className="flex p-1 bg-slate-100 dark:bg-slate-800 rounded-2xl mb-4 relative z-0">
                  <div
                    className="absolute inset-y-1 w-[calc(50%-4px)] bg-surface dark:bg-slate-700 rounded-xl transition-all duration-300 ease-out z-[-1]"
                    style={{ left: wizardConfig[wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode'] === 'existing' ? '4px' : 'calc(50%)' }}
                  />
                  <button
                    onClick={() => setWizardConfig({...wizardConfig, [wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode']: 'existing'})}
                    className={`flex-1 py-3 text-[11px] font-normal uppercase font-mono tracking-[0.14em] transition-colors ${wizardConfig[wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode'] === 'existing' ? 'text-primary' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                  >
                    Existente
                  </button>
                  <button
                    onClick={() => setWizardConfig({...wizardConfig, [wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode']: 'new'})}
                    className={`flex-1 py-3 text-[11px] font-normal uppercase font-mono tracking-[0.14em] transition-colors ${wizardConfig[wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode'] === 'new' ? 'text-primary' : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                  >
                    Novo
                  </button>
                </div>

                {wizardConfig[wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode'] === 'existing' ? (
                  <div>
                    <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 mb-2 block">
                      Baralho Existente
                    </label>
                    <div className="relative">
                      <select
                        value={wizardConfig[wizardStep === 1 ? 'flashcardDeckExisting' : wizardStep === 2 ? 'pronDeckExisting' : 'writeDeckExisting']}
                        onChange={(e) => setWizardConfig({...wizardConfig, [wizardStep === 1 ? 'flashcardDeckExisting' : wizardStep === 2 ? 'pronDeckExisting' : 'writeDeckExisting']: e.target.value})}
                        className="w-full h-14 bg-slate-50 dark:bg-slate-800/50 rounded-2xl px-4 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none border border-transparent focus:border-primary/80 transition-all appearance-none cursor-pointer"
                      >
                        {wizardAvailableDecks[wizardStep === 1 ? 'flashcard' : wizardStep === 2 ? 'pronunciation' : 'writing'].length === 0 && <option value="">Nenhum baralho encontrado</option>}
                        {wizardAvailableDecks[wizardStep === 1 ? 'flashcard' : wizardStep === 2 ? 'pronunciation' : 'writing'].map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                      <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 mb-2 block">
                        Nome do Novo Baralho
                      </label>
                      <input
                        type="text"
                        maxLength={15}
                        placeholder={i18nInstance.t('ui.txt.deckPlaceholder')}
                        value={wizardConfig[wizardStep === 1 ? 'flashcardDeckNew' : wizardStep === 2 ? 'pronDeckNew' : 'writeDeckNew']}
                        onChange={(e) => {
                           setWizardConfig({...wizardConfig, [wizardStep === 1 ? 'flashcardDeckNew' : wizardStep === 2 ? 'pronDeckNew' : 'writeDeckNew']: e.target.value});
                           setShowWizardError(false);
                        }}
                        className={`w-full h-14 bg-slate-50 dark:bg-slate-800/50 rounded-2xl px-4 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none border transition-all ${showWizardError && wizardConfig[wizardStep === 1 ? 'flashcardMode' : wizardStep === 2 ? 'pronMode' : 'writeMode'] === 'new' && !wizardConfig[wizardStep === 1 ? 'flashcardDeckNew' : wizardStep === 2 ? 'pronDeckNew' : 'writeDeckNew'].trim() ? 'border-bad' : 'border-transparent focus:border-primary/80'}`}
                      />
                    </div>
                    
                    <div>
                      <label className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 mb-2 block">
                        Idioma do Baralho
                      </label>
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setIsLangDropdownOpenWizard(!isLangDropdownOpenWizard)}
                          className="w-full h-14 bg-slate-50 dark:bg-slate-800/50 rounded-2xl px-4 text-sm font-bold text-slate-700 dark:text-slate-200 outline-none border border-transparent focus:border-primary/80 transition-all flex items-center justify-between"
                        >
                          <span className="truncate">
                            {STRICT_LANGUAGES.find(l => l.code === wizardConfig[wizardStep === 1 ? 'flashcardLang' : wizardStep === 2 ? 'pronLang' : 'writeLang'])?.name || 'Inglês'}
                          </span>
                          <ChevronDown size={16} className={`text-slate-400 transition-transform ${isLangDropdownOpenWizard ? 'rotate-180' : ''}`} />
                        </button>
                        
                        {isLangDropdownOpenWizard && (
                          <div className="absolute top-full left-0 right-0 mt-2 bg-surface dark:bg-slate-800 rounded-2xl border border-black/[0.06] dark:border-slate-700 max-h-60 overflow-y-auto z-50 py-2">
                            {STRICT_LANGUAGES.map(lang => (
                              <div
                                key={lang.code}
                                onClick={() => {
                                  setWizardConfig({...wizardConfig, [wizardStep === 1 ? 'flashcardLang' : wizardStep === 2 ? 'pronLang' : 'writeLang']: lang.code});
                                  setIsLangDropdownOpenWizard(false);
                                }}
                                className={`px-4 py-3 cursor-pointer text-sm font-medium transition-colors ${wizardConfig[wizardStep === 1 ? 'flashcardLang' : wizardStep === 2 ? 'pronLang' : 'writeLang'] === lang.code ? 'bg-primary/10 text-primary dark:text-primary' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50'}`}
                              >
                                {lang.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mt-auto pt-2 shrink-0 gap-3">
                {wizardStep > 1 ? (
                  <button
                    onClick={() => { setWizardStep((wizardStep - 1) as 1 | 2); setShowWizardError(false); }}
                    className="w-14 h-14 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-full flex items-center justify-center active:scale-95 transition-all shrink-0"
                  >
                    <ChevronLeft size={24} />
                  </button>
                ) : <div className="w-14 shrink-0" />}

                {wizardStep === 3 ? (
                  <button
                    onClick={() => {
                       const val = wizardConfig.writeMode === 'new' ? wizardConfig.writeDeckNew : wizardConfig.writeDeckExisting; // For existing, writeDeck holds selection
                       if (wizardConfig.writeMode === 'new' && !val.trim()) {
                          setShowWizardError(true);
                       } else {
                          confirmExerciseWizard();
                       }
                    }}
                    disabled={wizardConfig.writeMode === 'existing' && !wizardConfig.writeDeckExisting}
                    className="flex-1 h-14 bg-primary text-slate-900 rounded-2xl font-medium text-sm hover:brightness-110 active:scale-95 transition-all disabled:opacity-50"
                  >
                    Criar
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      const mode = wizardStep === 1 ? wizardConfig.flashcardMode : wizardConfig.pronMode;
                      const val = wizardStep === 1 ? (wizardConfig.flashcardMode === 'new' ? wizardConfig.flashcardDeckNew : wizardConfig.flashcardDeckExisting) : (wizardConfig.pronMode === 'new' ? wizardConfig.pronDeckNew : wizardConfig.pronDeckExisting);
                      if (mode === 'new' && !val.trim()) {
                         setShowWizardError(true);
                         return;
                      }
                      setShowWizardError(false);
                      if (wizardStep === 1) {
                        setWizardConfig(prev => ({
                          ...prev,
                          pronMode: prev.flashcardMode === 'new' ? 'new' : prev.pronMode,
                          pronDeckNew: prev.flashcardMode === 'new' ? prev.flashcardDeckNew : prev.pronDeckNew,
                          pronLang: prev.flashcardMode === 'new' ? prev.flashcardLang : prev.pronLang
                        }));
                        setWizardStep(2);
                      } else if (wizardStep === 2) {
                        setWizardConfig(prev => ({
                          ...prev,
                          writeMode: prev.pronMode === 'new' ? 'new' : prev.writeMode,
                          writeDeckNew: prev.pronMode === 'new' ? prev.pronDeckNew : prev.writeDeckNew,
                          writeLang: prev.pronMode === 'new' ? prev.pronLang : prev.writeLang
                        }));
                        setWizardStep(3);
                      }
                    }}
                    disabled={(wizardStep === 1 && wizardConfig.flashcardMode === 'existing' && !wizardConfig.flashcardDeckExisting) || (wizardStep === 2 && wizardConfig.pronMode === 'existing' && !wizardConfig.pronDeckExisting)}
                    className="w-14 h-14 bg-primary text-slate-900 rounded-full flex items-center justify-center hover:brightness-110 active:scale-95 transition-all shrink-0 ml-auto disabled:opacity-50"
                  >
                    <ChevronRight size={24} />
                  </button>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Edição de Nota */}
      {
        editingNote && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-md animate-in fade-in" onClick={() => setEditingNote(null)}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-full max-w-xl animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-2xl font-medium text-ink mb-6 tracking-[-0.03em]">{t('reader.editTranslationNote')}</h3>

              <div className="mb-4">
                <span className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 mb-2 block">{t('reader.originalText')}</span>
                <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-2xl text-sm font-medium text-slate-600 dark:text-slate-300 border border-black/[0.06] dark:border-white/[0.06] line-clamp-2">
                  {parseNote(typeof editingNote.content === 'string' ? editingNote.content : String(editingNote.content || '')).original || 'Texto não encontrado'}
                </div>
              </div>

              <span className="text-[10px] font-normal text-slate-400 uppercase font-mono tracking-[0.14em] px-2 mb-2 block">{t('reader.yourTranslationNote')}</span>
              <textarea
                value={typeof editContent === 'string' ? editContent : String(editContent || '')}
                onChange={(e) => setEditContent(e.target.value)}
                className="w-full h-32 bg-slate-100 dark:bg-slate-800 rounded-2xl p-4 text-sm font-medium text-slate-700 dark:text-slate-200 outline-none border border-transparent focus:border-primary/80 transition-all resize-none"
                placeholder={t("reader.typeTranslationNoteHere")}
              />

              <div className="grid grid-cols-2 gap-3 mt-6">
                <button
                  onClick={() => setEditingNote(null)}
                  className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full transition-colors"
                >
                  {t('common.cancel', 'Cancelar')}
                </button>
                <button
                  onClick={async () => {
                    // Safe parsing back to string
                    const rawContent = typeof editingNote.content === 'string' ? editingNote.content : String(editingNote.content || '');
                    const originalText = parseNote(rawContent).original;
                    // Format correctly as "Original -> Translation"
                    const newContentString = `${originalText} -> ${editContent}`;

                    storageService.updateAnnotation(editingNote.id, { content: newContentString });
                    
                    // Sincronização D1 Cloud para Edição
                    if (user && !user.isGuest && user.id) {
                      try {
                        await translationService.saveUserNote(user.id, {
                          id: editingNote.id,
                          book_id: editingNote.bookId || id || '',
                          book_title: bookData?.title || 'Livro Desconhecido',
                          original: originalText,
                          translation: editContent,
                          page_number: editingNote.pageNumber || currentPage,
                        });
                      } catch (e) {
                        console.error("Erro ao sincronizar edição no Cloud/D1:", e);
                      }
                    }

                    setNoteUpdateToggle(v => v + 1);
                    setEditingNote(null);
                  }}
                  className="text-[13px] font-medium h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all"
                >
                  {t('common.save', 'Salvar')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {
        showDeleteConfirm && (
          <div className="fixed inset-0 z-[6000] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-sm animate-in fade-in" onClick={() => setShowDeleteConfirm(null)}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[75vw] max-w-md" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-xl font-medium text-ink tracking-[-0.02em] mb-2">{t('reader.deleteNoteConfirm')}</h3>
              <p className="text-sm text-slate-500 mb-8">{t('reader.deleteNoteWarning')}</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setShowDeleteConfirm(null)} className="text-[13px] font-medium h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full">{t('common.cancel', 'Cancelar')}</button>
                <button
                  onClick={() => {
                    if (showDeleteConfirm) {
                      const idToDelete = showDeleteConfirm;
                      // 1. Local Delete
                      storageService.deleteAnnotation(idToDelete);

                      // 2. Cloud Delete (D1)
                      if (user && !user.isGuest && user.id) {
                        translationService.deleteUserNote(user.id, idToDelete).catch((err) => {
                          console.error("Failed to delete note from server (D1):", err);
                        });
                      }

                      // 3. Update UI
                      setAnnotations(prev => prev.filter(a => a.id !== idToDelete));
                      setNoteUpdateToggle(p => p + 1);
                      setShowDeleteConfirm(null);
                      setShowDeleteSuccess(true);
                    }
                  }} className="text-[13px] font-medium h-12 bg-bad text-white rounded-full"
                >
                  {t('common.delete', 'Excluir')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* MODAL SELETOR DE IDIOMA TTS */}
      {
        isReaderLangModalOpen && (
          <div className={`fixed inset-0 z-[4000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-2xl animate-in fade-in ${isUIVisible ? '' : 'ui-invisible'}`} onClick={() => setIsReaderLangModalOpen(false)}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-lg" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <Globe className="text-primary" size={24} />
                  <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('reader.choosePlaybackLang')}</h3>
                </div>
                <button onClick={() => setIsReaderLangModalOpen(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="max-h-[60vh] overflow-y-auto custom-scrollbar space-y-2 pr-2">
                {TTS_OPTIONS.map((option) => (
                  <button
                    key={option.locale}
                    onClick={() => {
                      setReaderTtsLocale(option.locale);
                      localStorage.setItem(`bookTtsLang_${id}`, option.locale);
                      setIsReaderLangModalOpen(false);
                      setBookLangDetected(true); // Hide the globe button after selection

                      // Save the chosen language back to D1 so it auto-detects next time
                      // Find the ISO code from the TTS locale (e.g., 'fr-FR' → 'fr')
                      const isoCode = Object.entries(languageMap).find(([, v]) => v === option.locale)?.[0]
                        || option.locale.split('-')[0];
                      if (user && !user.isGuest && bookData?.id) {
                        dbManager.from('books', user.id).update(
                          { language: isoCode },
                          { custom_id: bookData.id }
                        ).then(() => {
                          console.log(`[Lang] Saved language '${isoCode}' to D1 for book ${bookData.id}`);
                        }).catch(err => {
                          console.error('[Lang] Failed to save language to D1:', err);
                        });
                      }
                    }}
                    className={`w-full text-left px-6 py-4 rounded-2xl font-bold transition-all ${readerTtsLocale === option.locale
                      ? 'bg-ink text-surface'
                      : 'bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      }`}
                  >
                    {option.name}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <button
                  onClick={() => setIsReaderLangModalOpen(false)}
                  className="text-[13px] font-medium w-full h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full hover:bg-tint-stone transition-colors"
                >
                  {t('common.close', 'Fechar')}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* FONTE (Design 2.0): folha minimalista na base */}
      {
        isReadingSettingsModalOpen && (
          <div className="fixed inset-0 z-[6000] flex items-end justify-center p-3 animate-in fade-in duration-300" onClick={() => setIsReadingSettingsModalOpen(false)}>
            <div className="w-full max-w-md bg-[color:var(--hud-solid)] text-[color:var(--hud-ink)] border border-[color:var(--hud-line)] max-h-[78vh] overflow-y-auto custom-scrollbar rounded-[28px] p-5 shadow-[0_32px_80px_-32px_rgba(0,0,0,0.55)] animate-in fade-in slide-in-from-bottom-8 duration-500" style={{ marginBottom: 'env(safe-area-inset-bottom)' }} onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-display text-xl font-light tracking-[-0.04em] text-[color:var(--hud-ink)]">
                  {t('reader.fontLabel', 'Fonte')}<span className="text-primary">.</span>
                </h3>
                <HudButton label={t('common.close', 'Fechar')} onClick={() => setIsReadingSettingsModalOpen(false)} size="sm" tooltip="none">
                  <X size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:rotate-90" />
                </HudButton>
              </div>

              {(() => {
                const pill = (active: boolean) => `h-10 rounded-full text-[12px] font-medium flex items-center justify-center transition-all duration-300 active:scale-95 ${active ? 'bg-[color:var(--hud-strong)] text-[color:var(--hud-on-strong)]' : 'bg-[color:var(--hud-soft)] text-[color:var(--hud-muted)] hover:text-[color:var(--hud-ink)]'}`;
                const label = 'font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--hud-muted)] mb-2';
                return (
                  <div className="space-y-5">
                    {/* Tamanho + negrito/itálico */}
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => setReaderFontSize(prev => Math.max(12, prev - 2))} className={`${pill(false)} w-14 group`} aria-label={t('reader.decreaseFont', 'Diminuir fonte')}>
                        <Minus size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:scale-90" />
                      </button>
                      <div className="flex-1 h-10 rounded-full bg-[color:var(--hud-soft)] flex items-center justify-center font-mono text-[12px] tabular-nums text-[color:var(--hud-ink)]">
                        <span key={readerFontSize} className="animate-in fade-in zoom-in-90 duration-300">{readerFontSize}px</span>
                      </div>
                      <button type="button" onClick={() => setReaderFontSize(prev => Math.min(32, prev + 2))} className={`${pill(false)} w-14 group`} aria-label={t('reader.increaseFont', 'Aumentar fonte')}>
                        <Plus size={16} strokeWidth={1.75} className="transition-transform duration-300 group-hover:scale-110" />
                      </button>
                      <button type="button" onClick={() => setReaderIsBold(!readerIsBold)} className={`${pill(readerIsBold)} w-10`} aria-label={t('reader.boldLabel', 'Negrito')}>
                        <Bold size={15} strokeWidth={2} />
                      </button>
                      <button type="button" onClick={() => setReaderIsItalic(!readerIsItalic)} className={`${pill(readerIsItalic)} w-10`} aria-label={t('reader.italic', 'Itálico')}>
                        <Italic size={15} strokeWidth={2} />
                      </button>
                    </div>

                    <div>
                      <p className={label}>{t('reader.alignmentLabel', 'Alinhamento')}</p>
                      <div className="grid grid-cols-4 gap-2">
                        {[
                          { id: 'left', icon: AlignLeft },
                          { id: 'center', icon: AlignCenter },
                          { id: 'right', icon: AlignRight },
                          { id: 'justify', icon: AlignJustify },
                        ].map((align) => (
                          <button key={align.id} type="button" onClick={() => setReaderTextAlign(align.id)} className={pill(readerTextAlign === align.id)}>
                            <align.icon size={16} strokeWidth={1.75} />
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className={label}>{t('reader.marginsHoriz', 'Margens')}</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {[
                            { id: 'narrow', w: 'w-3' },
                            { id: 'medium', w: 'w-2' },
                            { id: 'wide', w: 'w-1' },
                          ].map((m) => (
                            <button key={m.id} type="button" onClick={() => setReaderMarginSize(m.id)} className={pill(readerMarginSize === m.id)} title={m.id}>
                              <span className="flex items-center gap-0.5"><span className={`${m.w} h-3 rounded-sm bg-current opacity-40`} /><span className="w-1.5 h-3 rounded-sm bg-current" /><span className={`${m.w} h-3 rounded-sm bg-current opacity-40`} /></span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className={label}>{t('reader.verticalSpacing', 'Espaçamento')}</p>
                        <div className="grid grid-cols-3 gap-1.5">
                          {[
                            { id: 'short', gap: 'gap-[2px]' },
                            { id: 'medium', gap: 'gap-[4px]' },
                            { id: 'long', gap: 'gap-[6px]' },
                          ].map((sp) => (
                            <button key={sp.id} type="button" onClick={() => setReaderVerticalSpacing(sp.id)} className={pill(readerVerticalSpacing === sp.id)} title={sp.id}>
                              <span className={`flex flex-col ${sp.gap}`}><span className="w-4 h-[2px] rounded bg-current" /><span className="w-4 h-[2px] rounded bg-current" /><span className="w-4 h-[2px] rounded bg-current" /></span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className={label}>{t('reader.preferredFont', 'Família')}</p>
                      <div
                        className="reader-font-row flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 select-none"
                        // Computador: rolar com a roda do mouse e clicar e arrastar (no toque o arrastar nativo segue igual)
                        onWheel={(e) => {
                          const el = e.currentTarget;
                          if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY;
                        }}
                        onPointerDown={(e) => {
                          if (e.pointerType !== 'mouse') return;
                          const el = e.currentTarget as HTMLDivElement & { _drag?: { x: number; left: number; moved: boolean } };
                          el._drag = { x: e.clientX, left: el.scrollLeft, moved: false };
                        }}
                        onPointerMove={(e) => {
                          const el = e.currentTarget as HTMLDivElement & { _drag?: { x: number; left: number; moved: boolean } };
                          if (!el._drag) return;
                          const dx = e.clientX - el._drag.x;
                          if (Math.abs(dx) > 4) { el._drag.moved = true; el.style.cursor = 'grabbing'; }
                          el.scrollLeft = el._drag.left - dx;
                        }}
                        onPointerUp={(e) => { const el = e.currentTarget as any; el.style.cursor = ''; setTimeout(() => { el._drag = undefined; }, 0); }}
                        onPointerLeave={(e) => { const el = e.currentTarget as any; el.style.cursor = ''; el._drag = undefined; }}
                        onClickCapture={(e) => { const el = e.currentTarget as any; if (el._drag?.moved) { e.stopPropagation(); e.preventDefault(); } }}
                      >
                        {READER_FONTS.map((font) => (
                          <button
                            key={font.name}
                            type="button"
                            onClick={() => setReaderFontFamily(font.name)}
                            className={`${pill(readerFontFamily === font.name)} shrink-0 px-4 text-[13px]`}
                            style={{ fontFamily: font.family }}
                          >
                            {font.name === 'System Default' ? 'Sistema' : font.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )
      }
      {/* IR PARA PÁGINA (Design 2.0) */}
      {
        isJumpModalOpen && (
          <div className="fixed inset-0 z-[6000] flex items-center justify-center p-6 bg-black/20 backdrop-blur-[20px] animate-in fade-in duration-300" onClick={(e) => { if (e.target === e.currentTarget) { setIsJumpModalOpen(false); setJumpError(null); setJumpPageInput(''); } }}>
            <form onSubmit={handleJumpSubmit} className="hud-element w-full max-w-xs rounded-[28px] p-6 text-center animate-in fade-in zoom-in-95 slide-in-from-bottom-4 duration-500" onClick={(e) => e.stopPropagation()}>
              <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--hud-muted)]">{t('reader.goToPage', 'Ir para página')}</p>
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min={1}
                max={totalPages}
                placeholder={String(currentPage)}
                value={jumpPageInput}
                onChange={(e) => { setJumpPageInput(e.target.value); setJumpError(null); }}
                className={`mt-3 w-full bg-transparent text-center font-display text-5xl font-light tracking-[-0.04em] tabular-nums outline-none text-[color:var(--hud-ink)] placeholder:text-[color:var(--hud-muted)] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none ${jumpError ? 'text-bad' : ''}`}
                autoFocus
              />
              <p className={`mt-1 font-mono text-[11px] tabular-nums ${jumpError ? 'text-bad' : 'text-[color:var(--hud-muted)]'}`}>
                {jumpError || `1 – ${totalPages}`}
              </p>
              <div className="mt-6 grid grid-cols-2 gap-2">
                <button type="button" onClick={() => { setIsJumpModalOpen(false); setJumpError(null); setJumpPageInput(''); }} className="h-11 rounded-full text-[13px] font-medium text-[color:var(--hud-muted)] hover:text-[color:var(--hud-ink)] hover:bg-[color:var(--hud-soft)] transition-colors duration-300">
                  {t('reader.cancel', 'Cancelar')}
                </button>
                <button type="submit" className="group h-11 rounded-full text-[13px] font-medium bg-[color:var(--hud-strong)] text-[color:var(--hud-on-strong)] flex items-center justify-center gap-1.5 hover:opacity-90 active:scale-95 transition-all duration-300">
                  {t('reader.go', 'Ir')}
                  <ChevronRight size={15} strokeWidth={1.75} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
              </div>
            </form>
          </div>
        )
      }

      {
        appNotification.show && (
          <div className="fixed inset-0 z-[5000] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200" onClick={() => setAppNotification(prev => ({ ...prev, show: false }))}>
            <div className="bg-surface p-8 rounded-[28px] w-[85vw] max-w-sm animate-in zoom-in-95 duration-200 flex flex-col items-center text-center gap-6" onClick={(e) => e.stopPropagation()}>
              {!appNotification.hideIcon && (
                <div className={`w-14 h-14 rounded-full flex items-center justify-center ${appNotification.type === 'error' ? 'bg-tint-rose text-ink' : 'bg-tint-sage text-ink'}`}>
                  {appNotification.type === 'error' ? <AlertTriangle size={22} strokeWidth={1.75} /> : <Check size={22} strokeWidth={1.75} />}
                </div>
              )}
              <div className="space-y-2">
                <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{appNotification.title}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed">{appNotification.message}</p>
              </div>
              <button
                onClick={() => setAppNotification(prev => ({ ...prev, show: false }))}
                className={`text-[13px] font-medium w-full h-12 text-white rounded-full transition-all hover:opacity-90 active:scale-95 ${appNotification.type === 'error' ? 'bg-bad' : 'bg-ink'}`}
              >
                OK
              </button>
            </div>
          </div>
        )
      }

      {/* Batch Delete Confirmation Modal */}
      {
        isBatchDeleteConfirmOpen && (
          <div className="fixed inset-0 z-[6000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={() => setIsBatchDeleteConfirmOpen(false)} />
            <div className="relative w-full max-w-sm bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-8 animate-in fade-in zoom-in-95 duration-200">
              <div className="flex justify-between items-start mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-tint-lavender rounded-full flex items-center justify-center text-ink [&_svg]:text-ink [&_svg]:w-4 [&_svg]:h-4">
                    <AlertTriangle className="text-primary" size={20} />
                  </div>
                  <span className="text-[10px] font-normal uppercase font-mono tracking-[0.14em] text-primary/60">IA LIBRITUI</span>
                </div>
                <button onClick={() => setIsBatchDeleteConfirmOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-full text-muted hover:text-ink hover:bg-tint-stone transition-colors">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-6 mb-8">
                <h3 className="text-xl font-medium text-ink leading-snug tracking-[-0.02em]">
                  {t('reader.deleteSelectedNotes').replace('{{count}}', String(selectedNoteIds.size))}
                </h3>
                <p className="text-slate-400 text-sm leading-relaxed">
                  {t('reader.deleteNoteWarning')}
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <button
                  onClick={confirmBatchDelete}
                  className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full active:scale-[0.98] transition-all"
                >
                  {t('common.delete', 'Confirmar Exclusão')}
                </button>
                <button
                  onClick={() => setIsBatchDeleteConfirmOpen(false)}
                  className="text-[13px] font-medium w-full h-12 bg-transparent text-muted hover:text-ink rounded-full transition-all"
                >
                  {t('common.cancel', 'Cancelar')}
                </button>
              </div>
            </div>
          </div>
        )
      }
      {/* Success Notification Modal (Nota Excluída) */}
      {
        showDeleteSuccess && (
          <div className="fixed inset-0 z-[7000] flex items-center justify-center p-6 bg-slate-950/60 backdrop-blur-md animate-in fade-in duration-200">
            <div className="bg-surface p-10 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[80vw] max-w-sm text-center animate-in zoom-in-95 duration-200">
              <div className="w-14 h-14 bg-tint-sage rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="text-ink" size={22} strokeWidth={1.75} />
              </div>
              <h3 className="text-2xl font-medium text-ink mb-2">{t('reader.success')}</h3>
              <p className="text-slate-500 dark:text-slate-400 text-sm mb-10 leading-relaxed">
                {t('reader.deleteSuccess', 'Nota excluída com sucesso!')}
              </p>
              <button
                onClick={() => setShowDeleteSuccess(false)}
                className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full hover:opacity-90 active:scale-95 transition-all"
              >
                {t('common.confirm', 'Confirmar')}
              </button>
            </div>
          </div>
        )
      }
      {/* Sync New Confirmation Modal */}
      <GenerateExerciseModal
        isOpen={!!showSyncNewConfirm}
        onClose={() => setShowSyncNewConfirm(null)}
        onConfirm={confirmSyncNew}
        itemCount={showSyncNewConfirm?.count || 0}
        bookLangCode={showSyncNewConfirm?.langCode || ''}
        existingFlashcardDecks={showSyncNewConfirm?.flashcardDecks || []}
        existingPronunciationDecks={showSyncNewConfirm?.pronunciationDecks || []}
        existingWritingDecks={showSyncNewConfirm?.writingDecks || []}
      />

      {/* Sync Language Picker Modal */}
      {
        isPickingLangForSync && (
          <div className="fixed inset-0 z-[7000] flex items-center justify-center p-6 bg-slate-950/80 backdrop-blur-2xl animate-in fade-in duration-300" onClick={() => setIsPickingLangForSync(false)}>
            <div className="bg-surface p-8 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] w-[85vw] max-w-lg" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-tint-lavender rounded-full flex items-center justify-center text-ink [&_svg]:text-ink [&_svg]:w-4 [&_svg]:h-4">
                    <Globe className="text-primary" size={24} />
                  </div>
                  <h3 className="text-xl font-medium text-ink tracking-[-0.02em]">{t('reader.exerciseLang')}</h3>
                </div>
                <button onClick={() => setIsPickingLangForSync(false)} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-slate-400">
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                {FLASHCARD_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleStartSyncNew(lang.code)}
                    className="flex items-center gap-4 p-4 rounded-2xl border border-black/[0.06] dark:border-white/[0.06] hover:border-primary hover:bg-primary/5 transition-all group text-left"
                  >
                    <span className="text-2xl group-hover:scale-110 transition-transform">{lang.flag}</span>
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold dark:text-ink">{lang.name}</span>
                      <span className="text-[10px] text-slate-400 uppercase font-mono tracking-[0.14em]">{lang.code}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )
      }


      {/* QuoteCardModal is now handled in /sharing route */}

      <style>{`
        span.palavra-interativa {
          transition: background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease;
          display: inline-block;
          border-radius: 3px;
          padding: 0 1px;
          cursor: pointer;
        }
        @media (hover: hover) {
          span.palavra-interativa:hover {
            background-color: rgba(var(--primary-rgb), 0.15);
          }
        }
        span.palavra-interativa.is-selected {
          background-color: #F6DECA !important;
          color: #8A4318 !important;
          border-radius: 2px;
          line-height: inherit !important;
          font-weight: inherit !important;
          font-size: inherit !important;
        }
        .claro-texto-preto, .claro-texto-preto * {
          color: #1C2430 !important;
        }
        .dark span.palavra-interativa.is-selected {
          background-color: #4A362C !important;
          color: #ffffff !important;
        }
        .palavra-interativa.destaque-nota {
          background-color: rgba(var(--primary-rgb), 0.35) !important;
          border-bottom: 2px solid rgb(var(--primary-rgb)) !important;
          box-shadow: 0 2px 8px rgba(var(--primary-rgb), 0.2);
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          border-radius: 4px;
          position: relative;
        }
        @media (hover: hover) {
          .palavra-interativa.destaque-nota:hover {
            background-color: rgba(var(--primary-rgb), 0.5) !important;
            box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.4);
          }
        }
        .palavra-interativa.highlight-speaking {
          background: linear-gradient(135deg, #F2A86F 0%, #E8823F 100%) !important;
          color: #000 !important;
          border-radius: 6px;
          transition: all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          box-shadow: 0 4px 15px rgba(242, 168, 111, 0.4), 0 0 30px rgba(232, 130, 63, 0.2);
          transform: scale(1.08) translateY(-1px);
          z-index: 100;
          position: relative;
          text-shadow: 0 1px 0 rgba(255,255,255,0.4);
          animation: pulse-reading 1.5s ease-in-out infinite;
        }
        @keyframes pulse-reading {
          0%, 100% { box-shadow: 0 4px 15px rgba(242, 168, 111, 0.4), 0 0 20px rgba(232, 130, 63, 0.2); }
          50% { box-shadow: 0 6px 20px rgba(242, 168, 111, 0.6), 0 0 40px rgba(232, 130, 63, 0.4); transform: scale(1.1) translateY(-2px); }
        }
        .dark .palavra-interativa.highlight-speaking {
          background: linear-gradient(135deg, #E8823F 0%, #B85E2C 100%) !important;
          color: #000 !important;
        }
        @keyframes pulse-destaque {
          0% { box-shadow: 0 0 0 0px rgba(var(--primary-rgb), 0.7); transform: scale(1); }
          50% { box-shadow: 0 0 0 10px rgba(var(--primary-rgb), 0); transform: scale(1.05); }
          100% { box-shadow: 0 0 0 0px rgba(var(--primary-rgb), 0); transform: scale(1); }
        }
        @media (hover: hover) {
          .dark .palavra-interativa:hover {
            background-color: rgba(var(--primary-rgb), 0.3);
          }
        }
        
        .ui-toggle-btn {
          cursor: pointer !important;
          min-width: 56px;
          min-height: 56px;
          display: flex;
          align-items: center;
          justify-content: center;
          -webkit-tap-highlight-color: transparent;
          touch-action: manipulation !important;
          z-index: 6 !important;
        }

        /* Prevent jumping during selection on mobile */
        .viewer-container {
          overflow-anchor: none !important;
          -webkit-overflow-scrolling: touch;
        }

        .hud-element.is-hidden {
          visibility: hidden !important;
          pointer-events: none !important;
          opacity: 0 !important;
        }

        .section-packet > *:first-child {
          margin-top: 0 !important;
        }
        
        .reader-header {
          padding-top: env(safe-area-inset-top, 24px) !important;
          padding-bottom: 12px !important;
        }

        /* Auto-hide controls */
        .reader-control-auto-hide {
          opacity: 0.15 !important;
          transition: opacity 0.3s ease-in-out !important;
          pointer-events: auto !important;
        }
        @media (hover: hover) {
          .reader-control-auto-hide:hover {
            opacity: 0.7 !important;
          }
        }
        .reader-control-auto-hide:focus-within,
        .reader-control-auto-hide.is-active {
          opacity: 0.7 !important;
        }
        @media (max-width: 768px) {
          .reader-control-auto-hide {
            opacity: 0.7 !important; /* Sempre visível no mobile quando UI estiver ativa */
          }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-3px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }

        .active-note-highlight {
          outline: 4px solid rgb(var(--primary-rgb)) !important;
          outline-offset: 4px;
          background-color: rgba(var(--primary-rgb), 0.5) !important;
          z-index: 1000 !important;
          position: relative;
          border-radius: 6px;
          box-shadow: 0 0 0 100vmax rgba(0,0,0,0.1), 0 0 40px rgb(var(--primary-rgb));
          animation: pulse-destaque 1.2s infinite ease-out;
        }
      `}</style>
      {/* Safe Haven Error Overlay */}
      {
        loadError && (
          <div className="fixed inset-0 z-[250] flex items-center justify-center p-6 bg-slate-950/90 backdrop-blur-xl">
            <div className="bg-surface p-10 rounded-[28px] border border-black/[0.06] dark:border-white/[0.06] max-w-md w-full text-center space-y-6 animate-in zoom-in-95 duration-300">
              <div className="w-20 h-20 bg-warn/10 text-warn rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle size={40} />
              </div>
              <h2 className="text-2xl font-medium dark:text-ink">{safeT('reader.optimizingApp', t('reader.optimizingApp', 'Erro na Leitura'))}</h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed font-medium">
                {safeT('reader.reimportDesc', 'Por favor, re-importe este livro da Biblioteca Online. Suas notas e progresso estão seguros!')}
              </p>
              <div className="space-y-3">
                <button
                  onClick={handleReimport}
                  className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full active:scale-95 transition-all flex items-center justify-center gap-3"
                >
                  <RotateCcw size={18} /> {safeT('reader.reimportAndRestore', 'Re-importar e Restaurar')}
                </button>
                <button
                  onClick={async () => {
                    if (refreshBooks) await refreshBooks(true);
                    const targetReturn = location.state?.from || (location.state?.shadowing ? '/shadowing' : '/library');
                    navigate(targetReturn);
                  }}
                  className="text-[13px] font-medium w-full h-12 bg-tint-stone/60 text-muted hover:text-ink rounded-full hover:bg-slate-200"
                >
                  {safeT('reader.backToShelf', 'Voltar à Estante')}
                </button>
              </div>
            </div>
          </div>
        )
      }


      {
        isSidebarOpen && (
          <>
            <div className="fixed inset-0 z-[400] bg-black/70 backdrop-blur-[60px] animate-in fade-in duration-1000 pointer-events-auto" />
            <aside className="fixed right-0 top-0 bottom-0 w-[85vw] md:w-[400px] max-w-full h-full border-l border-black/[0.06] dark:border-white/[0.06] bg-white/90 dark:bg-slate-950/90 backdrop-blur-2xl p-4 sm:p-6 flex flex-col gap-4 sm:gap-6 z-[410] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] transition-all duration-1000 animate-in fade-in slide-in-from-right-full overflow-x-hidden">
              {/* Botão "X" branco 50% conforme solicitado */}
              <button
                onClick={() => { setIsSidebarOpen(false); textToSpeechService.stop(); }}
                className="absolute top-4 right-4 z-10 p-2 text-white/50 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
              <div className="flex flex-col gap-2 shrink-0 pr-8">
                <div className="flex justify-between items-center">
                  <h2 className="font-normal text-[10px] uppercase font-mono tracking-[0.14em] text-primary">{t('reader.notebook')}</h2>
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    onClick={() => setIsPickingLangForSync(true)}
                    className="p-2.5 sm:p-3 bg-warn/10 text-warn rounded-xl hover:bg-warn hover:text-white transition-all flex items-center justify-center group shrink-0"
                    title={t('reader.syncNew')}
                  >
                    <Zap size={18} fill={isSyncingNew ? "currentColor" : "none"} className={isSyncingNew ? "animate-pulse" : ""} />
                  </button>
                  <button
                    onClick={() => {
                      setIsSelectionMode(!isSelectionMode);
                      setSelectedNoteIds(new Set());
                      setActiveNoteActionId(null);
                    }}
                    className={`text-[10px] font-normal uppercase font-mono tracking-[0.14em] py-2 px-3 sm:px-4 rounded-xl transition-all shrink-0 ${isSelectionMode ? 'bg-ink text-surface' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-primary'}`}
                  >
                    {isSelectionMode ? t('reader.selectingBtn') : t('reader.selectBtn')}
                  </button>

                  {isSelectionMode && (
                    <div className="flex items-center gap-1.5 ml-auto shrink-0 animate-in fade-in slide-in-from-right-2">
                      <input 
                        type="checkbox"
                        checked={selectedNoteIds.size === annotations.length && annotations.length > 0}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedNoteIds(new Set(annotations.map(a => a.id)));
                          } else {
                            setSelectedNoteIds(new Set());
                          }
                        }}
                        className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                      />
                      <span className="text-[9px] font-normal text-slate-500 uppercase font-mono tracking-[0.14em] whitespace-nowrap">
                        {selectedNoteIds.size} selecionadas
                      </span>
                    </div>
                  )}

                  {isSelectionMode && selectedNoteIds.size > 0 && (
                    <div className="flex items-center gap-1 shrink-0 animate-in fade-in slide-in-from-left-2 bg-slate-100 dark:bg-slate-800/80 p-1 rounded-xl">
                      <button
                        onClick={openBatchFlashcardModal}
                        className="p-1.5 text-tintd-lavender hover:bg-tint-lavender dark:hover:bg-tintd-lavender/20 rounded-lg transition-colors"
                        title={t('reader.createFlashcardsSelected')}
                      >
                        <Brain size={18} />
                      </button>
                      <button
                        onClick={handleBatchDelete}
                        className="p-1.5 text-bad hover:bg-tint-rose dark:hover:bg-bad/20 rounded-lg transition-colors"
                        title="Excluir Selecionados"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Sort / Spacing */}
                <div className="pt-4 border-t border-black/[0.06] dark:border-slate-800/50 mt-2">
                  <div className="relative">
                    <button
                      onClick={() => setIsSortOpen(!isSortOpen)}
                      className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-black/[0.06] dark:border-white/[0.06] rounded-2xl text-[10px] font-medium text-slate-500 hover:text-primary transition-all group"
                    >
                      <div className="flex items-center gap-2">
                        <Filter size={14} className={isSortOpen ? 'text-primary' : ''} />
                        {sortMode === 'recent' ? t('reader.showRecent') : 
                         sortMode === 'old' ? t('reader.showOldest') : 
                         t('reader.showFavorites')}
                      </div>
                      <ChevronDown size={14} className={`transition-transform duration-300 ${isSortOpen ? 'rotate-180 text-primary' : ''}`} />
                    </button>

                    {isSortOpen && (
                      <div className="absolute top-full left-0 right-0 mt-2 z-50 bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[1.5rem] shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        {[
                          { id: 'recent', label: t('reader.showRecent'), icon: <Clock size={14} /> },
                          { id: 'old', label: t('reader.showOldest'), icon: <Clock size={14} className="rotate-180" /> },
                          { id: 'favorite', label: t('reader.showFavorites'), icon: <Heart size={14} /> },
                        ].map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => {
                              setSortMode(mode.id as any);
                              setIsSortOpen(false);
                            }}
                            className={`text-[13px] font-medium w-full flex items-center gap-3 px-6 h-12 transition-all ${sortMode === mode.id ? 'bg-ink text-surface' : 'text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                          >
                            {mode.icon}
                            {mode.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div 
                className="flex-1 overflow-y-auto space-y-4 pr-1 custom-scrollbar pb-safe pt-4"
                onScroll={handleNotesScroll}
              >
                {[...annotations]
                  .filter(a => sortMode === 'favorite' ? a.favorited === 'yes' : true)
                  .sort((a, b) => {
                    if (sortMode === 'old') return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
                    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
                  })
                  .slice(0, visibleNotesCount)
                  .map(a => {
                  const { original, translation: parsedTranslation, commentary } = parseNote(a.content);
                  const isActive = activeNoteActionId === a.id;
                  const isSelected = selectedNoteIds.has(a.id);
                  const bookLocale = bookData?.language || targetLanguage;

                  return (
                    <div key={a.id} className="relative">
                      {isActive && (
                        <div className="mb-2 bg-surface border border-black/[0.06] dark:border-white/[0.06] p-2 rounded-2xl animate-in fade-in zoom-in-95 duration-200">
                          {/* Row 1 */}
                          <div className="flex items-center justify-around">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                jumpToPage(a.pageNumber);
                                setJumpedNoteId(a.id);
                                setIsSidebarOpen(false);
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title={t('reader.goToPageStr')}
                            >
                              <div className="p-2 bg-primary/10 text-primary rounded-xl group-hover:bg-primary group-hover:text-white transition-all">
                                <Target size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em]">{t('common.go', 'Ir')}</span>
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayNoteAudio(original, bookLocale);
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title=" {t('reader.listenPronunciation')}"
                            >
                              <div className="p-2 bg-tintd-lavender/10 text-tintd-lavender rounded-xl group-hover:bg-tintd-lavender group-hover:text-white transition-all">
                                <Volume2 size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em]">{safeT('common.listen', 'Ouvir')}</span>
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openFlashcardCreationModal(a);
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title={t('reader.createFlashcard')}
                            >
                              <div className="p-2 bg-tintd-lavender/10 text-tintd-lavender rounded-xl group-hover:bg-tintd-lavender group-hover:text-white transition-all">
                                <Brain size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-tintd-lavender">{safeT('reader.card', 'Card')}</span>
                            </button>
                          </div>

                          {/* Divider */}
                          <div className="mx-2 my-1 h-px bg-slate-100 dark:bg-slate-800 opacity-50" />

                          {/* Row 2 */}
                          <div className="flex items-center justify-around">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingNote(a);
                                setEditContent(parseNote(typeof a.content === 'string' ? a.content : String(a.content || '')).translation);
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title={t('reader.editNote')}
                            >
                              <div className="p-2 bg-tintd-sky/10 text-tintd-sky rounded-xl group-hover:bg-tintd-sky group-hover:text-white transition-all">
                                <Edit size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-tintd-sky"
                                onClick={() => {
                                    navigate('/sharing', {
                                        state: {
                                            shareQuote: {
                                                text: original,
                                                bookTitle: bookData?.title || 'Desconhecido',
                                                authorName: bookData?.authors?.[0]?.name || bookData?.author || '',
                                                coverUrl: bookData?.coverUrl || bookData?.cover_url || null,
                                                bookId: bookData?.id
                                            }
                                        }
                                    });
                                }}
                              >{safeT('common.edit', 'Editar')}</span>
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate('/sharing', {
                                    state: {
                                        shareQuote: {
                                            text: original,
                                            bookTitle: bookData?.title || '',
                                            authorName: bookData?.authors?.[0]?.name || bookData?.author || '',
                                            coverUrl: bookData?.coverUrl || bookData?.cover_url || null,
                                            bookId: bookData?.id
                                        }
                                    }
                                });
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title="Compartilhar"
                            >
                              <div className="p-2 bg-tintd-rose/10 text-tintd-rose rounded-xl group-hover:bg-tintd-rose group-hover:text-white transition-all">
                                <Share2 size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-tintd-rose">{safeT('common.share', 'Share')}</span>
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteConfirm(a.id);
                              }}
                              className="p-1.5 flex flex-col items-center gap-1 group"
                              title={t('reader.deleteNote')}
                            >
                              <div className="p-2 bg-bad/10 text-bad rounded-xl group-hover:bg-bad group-hover:text-white transition-all">
                                <Trash2 size={16} />
                              </div>
                              <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em] text-bad">{safeT('common.delete', 'Excluir')}</span>
                            </button>
                          </div>
                        </div>
                      )}
                      <div
                        onClick={() => {
                          if (isSelectionMode) {
                            const newSelection = new Set(selectedNoteIds);
                            if (isSelected) newSelection.delete(a.id);
                            else newSelection.add(a.id);
                            setSelectedNoteIds(newSelection);
                          } else {
                            setActiveNoteActionId(isActive ? null : a.id);
                          }
                        }}
                        className={`p-6 rounded-3xl border transition-all cursor-pointer ${isSelectionMode ? 'animate-float' : ''} ${isSelected ? 'border-primary ring-2 ring-primary/20' : isActive ? 'bg-primary/5 border-primary' : 'bg-slate-50 dark:bg-slate-800/50 border-black/[0.06] dark:border-white/[0.06] hover:border-primary/80'
                          }`}
                        style={isSelected ? { borderColor: 'rgb(var(--primary-rgb))' } : {}}
                      >
                        <div className="space-y-3">
                          <p className="text-[14px] font-semibold leading-tight text-primary">{original}</p>
                          <div className="border-t border-black/[0.06] dark:border-slate-700 w-full opacity-50" />
                          <p className="text-[13px] font-bold leading-relaxed text-ink dark:text-slate-200">{parsedTranslation || "Sem conteúdo."}</p>
                          {commentary && (
                            <p className="text-[11px] font-medium italic text-slate-500 dark:text-slate-400 leading-snug">{commentary}</p>
                          )}
                        </div>
                        <div className="flex justify-between items-center pt-4 mt-2 opacity-60">
                          <div className="flex items-center gap-2">
                            <span className="text-[8px] font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">Pág {a.pageNumber}</span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleFavorite(a);
                              }}
                              className={`p-1.5 rounded-lg transition-all ${a.favorited === 'yes' ? 'bg-bad/10 text-bad scale-110' : 'hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400'}`}
                            >
                              <Heart size={14} fill={a.favorited === 'yes' ? 'currentColor' : 'none'} />
                            </button>
                          </div>
                          <span className="text-[8px] font-normal uppercase font-mono tracking-[0.14em]">{new Date(a.createdAt).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {annotations.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-20 text-center px-6">
                    <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-3xl flex items-center justify-center mb-4 text-slate-300">
                      <MessageSquare size={32} />
                    </div>
                    <p className="text-sm font-bold text-ink">{t('reader.noNotesYet')}</p>
                    <p className="text-xs text-slate-500 mt-2">{t('reader.selectWordsToStart')}</p>
                  </div>
                )}
              </div>
            </aside>
          </>
        )
      }
    </div >
  );
};

export default LibriTuiReaderPage;
